<template>
  <AuthenticatedView
    :loading="qLoading && !result"
    :current-user="currentUser"
    :has-result="!!result"
  >
    <AppHeader
      v-if="currentUser"
      :user="currentUser"
      :chat-id="chatId"
      :from-id="fromId"
      dashboard-url="/dashboard"
      profile-url="/profile"
    />
    <div :class="['card', { 'full-width': isFullWidth }]">
      <div class="task-show-breadcrumbs-row">
        <div class="task-show-breadcrumbs">
          <breadcrumbs :items="breadcrumbsItems" :link-last-item="true" />
        </div>
      </div>
      <div class="task-show-top-bar">
        <div class="task-show-top-bar-left">
          <div v-if="boardUrl" class="back-button-container">
            <a :href="boardUrl" class="back-button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="back-arrow-icon"
                aria-hidden="true"
              >
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              {{ s__('TaskDetail|Back to Board') }}
            </a>
          </div>
          <div v-else-if="tasksListUrl" class="back-button-container">
            <a :href="tasksListUrl" class="back-button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="back-arrow-icon"
                aria-hidden="true"
              >
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              {{ tasksListLabel }}
            </a>
          </div>
        </div>

        <div v-if="hasTaskMenuActions" ref="taskMenuRef" class="task-menu">
          <button
            type="button"
            class="btn-link-secondary btn-sm task-menu-trigger"
            :title="s__('TaskDetail|Task Menu')"
            @click.stop="toggleTaskMenu"
          >
            <span class="btn-text">{{ s__('TaskDetail|Task Menu') }}</span>
            <span class="task-menu-icon task-menu-icon-chevron" aria-hidden="true">▾</span>
            <span class="task-menu-icon task-menu-icon-burger" aria-hidden="true">☰</span>
          </button>
          <div v-if="showTaskMenu" class="task-menu-dropdown">
            <button
              v-if="canToggleFullWidth"
              type="button"
              class="task-menu-item"
              @click="handleTaskMenuToggleFullWidth"
            >
              {{
                isFullWidth
                  ? s__('TaskDetail|Exit full-width mode')
                  : s__('TaskDetail|Enter full-width mode')
              }}
            </button>
            <button
              v-if="canClone"
              type="button"
              class="task-menu-item"
              @click="handleTaskMenuClone"
            >
              {{ s__('TaskDetail|Clone task') }}
            </button>
            <button
              v-if="!isPersonalProject"
              type="button"
              class="task-menu-item"
              @click="handleTaskMenuShare"
            >
              {{ s__('TaskDetail|Share link') }}
            </button>
          </div>
        </div>
      </div>
      <archived-project-warning
        v-if="!loading && !taskNotFound && projectArchived"
        :archived="projectArchived"
      />
      <div v-if="loading">{{ __('Loading…') }}</div>
      <div v-else-if="taskNotFound" class="task-info-container">
        <div class="confidential-message">
          <div class="confidential-icon">🔒</div>
          <h3>{{ s__('TaskDetail|Task Not Accessible') }}</h3>
          <p>
            {{
              s__(
                'TaskDetail|This task does not exist or you do not have permission to view it. The task may be confidential, or you may not be a member of this project.'
              )
            }}
          </p>
        </div>
      </div>
      <div v-else class="task-info-container">
        <div v-if="task?.archived" style="margin-bottom: 12px">
          <span class="archived-badge">{{ s__('TaskDetail|Archived') }}</span>
        </div>

        <!-- Title Line (editable) -->
        <div class="title-container" :class="{ archived: task?.archived }">
          <div v-if="!editingTitle" class="title-content-wrapper">
            <div class="title-main">
              <span class="title-chip task-id-chip" :class="{ archived: task?.archived }">
                #{{ task?.iid }}
              </span>
              <button
                v-if="showTaskCategoryChip && canUpdate"
                type="button"
                class="title-chip task-category-chip is-clickable"
                :class="{ archived: task?.archived }"
                :title="s__('TaskDetail|Change task category')"
                @click="startEditTaskCategory"
              >
                {{ task.taskCategory.name }}
              </button>
              <span
                v-else-if="showTaskCategoryChip"
                class="title-chip task-category-chip"
                :class="{ archived: task?.archived }"
              >
                {{ task.taskCategory.name }}
              </span>
              <div class="title-text-with-edit">
                <span
                  class="task-title"
                  :class="{ archived: task?.archived }"
                  style="font-size: 1.1rem; font-weight: 500; color: #212529"
                  >{{ task?.title }}</span
                >
                <button
                  v-if="canUpdate"
                  class="btn-icon task-title-edit-btn"
                  :title="s__('TaskDetail|Edit title')"
                  @click="startEditTitle"
                >
                  <EditIcon />
                </button>
              </div>
            </div>
            <div class="title-secondary">
              <div class="title-secondary-item">
                <span class="title-secondary-label">{{ __('Priority') }}:</span>
                <div v-if="!editingPriority" class="title-secondary-value-group">
                  <button
                    v-if="canUpdate"
                    type="button"
                    class="title-chip task-priority-chip is-clickable"
                    :class="{ 'action-required-chip': !task?.priority }"
                    :title="s__('TaskDetail|Edit priority')"
                    @click.stop="startEditPriority"
                  >
                    <PriorityIcon v-if="task?.priority" :priority="task.priority" :size="14" />
                    <span>{{
                      task?.priority ? translatePriority(task.priority) : s__('TaskDetail|Set up')
                    }}</span>
                  </button>
                  <span
                    v-else-if="task?.priority"
                    class="title-chip task-priority-chip"
                    style="display: inline-flex; align-items: center; gap: 4px"
                  >
                    <PriorityIcon :priority="task.priority" :size="14" />
                    <span>{{ translatePriority(task.priority) }}</span>
                  </span>
                  <span v-else class="muted">{{ s__('TaskDetail|Not set') }}</span>
                </div>
                <div v-else ref="priorityEditRef" class="title-secondary-editor">
                  <priority-select
                    v-model="editForm.priority"
                    :options="priorityOptions"
                    :placeholder="
                      isPriorityRequired
                        ? s__('TaskDetail|Select priority')
                        : s__('TaskDetail|Not set')
                    "
                  />
                </div>
              </div>

              <div class="title-secondary-item title-secondary-item-due">
                <span class="title-secondary-label">{{ s__('TaskDetail|Due') }}:</span>
                <div v-if="!editingDueDate" class="title-secondary-value-group">
                  <button
                    v-if="canUpdate"
                    type="button"
                    class="title-chip task-due-chip is-clickable"
                    :class="{ 'action-required-chip': isDueAtRequired && !task?.dueAt }"
                    :title="s__('TaskDetail|Edit due date')"
                    @click="startEditDueDate"
                  >
                    <formatted-date-time
                      v-if="task?.dueAt"
                      :value="task.dueAt"
                      :locale="userLocale"
                      :timezone="userTimezone"
                    />
                    <template v-else>{{ s__('TaskDetail|Set up') }}</template>
                  </button>
                  <span v-else class="muted">
                    <formatted-date-time
                      v-if="task?.dueAt"
                      :value="task.dueAt"
                      :locale="userLocale"
                      :timezone="userTimezone"
                    /><template v-else>{{ s__('TaskDetail|Not set') }}</template>
                  </span>
                </div>
                <div v-else class="title-secondary-editor">
                  <DateTimeInput
                    :datetime="editForm.dueAt"
                    :title="s__('TaskDetail|Due')"
                    :auto-open="true"
                    :clearable="!isDueAtRequired"
                    @update:datetime="handleDueDateUpdate"
                    @cancel="cancelEditDueDate"
                  />
                </div>
              </div>

              <button
                v-if="task?.workflowStatus"
                type="button"
                class="btn-primary btn-sm title-status-button"
                :title="s__('TaskDetail|Click to change status')"
                @click="openTransitionModal"
                style="display: flex; align-items: center; gap: 6px; padding: 4px 8px"
              >
                <span class="title-status-content">
                  <span
                    v-if="task?.workflowStatus?.color"
                    class="status-dot"
                    :style="{ backgroundColor: task.workflowStatus.color }"
                  ></span>
                  <span class="status-text">{{ task?.workflowStatus?.name || '—' }}</span>
                </span>
              </button>
            </div>
          </div>
          <div v-else>
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px">
              <span style="color: #757575; font-size: 0.9rem; font-weight: 500"
                >#{{ task?.id }}</span
              >
              <SingleLineTextarea
                v-model="editForm.title"
                style="
                  flex: 1;
                  font-size: 1.1rem;
                  padding: 8px;
                  border: 1px solid #ddd;
                  border-radius: 4px;
                  line-height: 1.4;
                  resize: vertical;
                "
              />
            </div>
            <div style="display: flex; gap: 8px">
              <button class="btn-sm" @click="saveTitle">{{ s__('TaskDetail|Save') }}</button>
              <button class="btn-sm btn-secondary" @click="cancelEditTitle">
                {{ s__('TaskDetail|Cancel') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Second Line: Participants -->
        <div v-if="!isPersonalProject" class="row participants-row" style="margin-bottom: 16px">
          <div class="participants-block">
            <div class="participants-top-row">
              <div class="participants-line participants-line-assignee">
                <label class="participants-line-label">{{ s__('TaskDetail|Assignee') }}</label>
                <div v-if="!editingAssignee" class="participants-line-main">
                  <button
                    v-if="canUpdateAssignee"
                    class="subscribers-summary-trigger assignee-summary-trigger assignee-edit-trigger"
                    :class="{ 'assignee-summary-unassigned': !task?.assignee }"
                    :title="
                      assigneeProjectRole ||
                      (task?.assignee
                        ? s__('TaskDetail|Edit assignee')
                        : s__('TaskDetail|Assign user'))
                    "
                    @click="startEditAssignee"
                  >
                    <span class="participants-user-summary">
                      <template v-if="task?.assignee">
                        <span class="participants-user-username">{{
                          task.assignee.username ? '@' + task.assignee.username : ''
                        }}</span>
                        <span v-if="task.assignee.name" class="participants-user-name">
                          {{ task.assignee.name }}
                        </span>
                        <span
                          v-if="!task.assignee.username && !task.assignee.name"
                          class="participants-user-fallback"
                        >
                          User {{ task.assignee.id }}
                        </span>
                      </template>
                      <span v-else class="participants-user-placeholder">{{ __('Assign') }}</span>
                    </span>
                  </button>
                  <div
                    v-else
                    class="participants-user-summary"
                    :title="assigneeProjectRole || null"
                  >
                    <template v-if="task?.assignee">
                      <span class="participants-user-username">{{
                        task.assignee.username ? '@' + task.assignee.username : ''
                      }}</span>
                      <span v-if="task.assignee.name" class="participants-user-name">
                        {{ task.assignee.name }}
                      </span>
                      <span
                        v-if="!task.assignee.username && !task.assignee.name"
                        class="participants-user-fallback"
                      >
                        User {{ task.assignee.id }}
                      </span>
                    </template>
                    <span v-else class="muted">{{ __('Unassigned') }}</span>
                  </div>
                </div>
                <div v-else ref="assigneeEditRef" class="participants-line-main">
                  <user-select
                    v-model="editForm.assigneeId"
                    :users="projectMembers"
                    :placeholder="
                      isAssigneeRequired
                        ? s__('TaskDetail|Select assignee')
                        : s__('TaskDetail|Not assigned')
                    "
                    :searchable="true"
                    :project-id="project?.id"
                    :show-empty-option="!isAssigneeRequired"
                  />
                </div>
              </div>

              <div class="participants-line participants-line-author">
                <label class="participants-line-label">{{ s__('TaskDetail|Author') }}</label>
                <div class="participants-line-main">
                  <div class="participants-user-summary" :title="authorProjectRole || null">
                    <template v-if="task?.author">
                      <span class="participants-user-username">{{
                        task.author.username ? '@' + task.author.username : ''
                      }}</span>
                      <span v-if="task.author.name" class="participants-user-name">
                        {{ task.author.name }}
                      </span>
                      <span
                        v-if="!task.author.username && !task.author.name"
                        class="participants-user-fallback"
                      >
                        User {{ task.author.id }}
                      </span>
                    </template>
                    <span v-else class="muted">—</span>
                  </div>
                </div>
              </div>

              <div class="participants-line participants-line-subscribers">
                <div class="participants-line-main participants-line-main-subscribers">
                  <button
                    class="subscribers-summary-trigger"
                    :title="
                      sprintf(s__('TaskDetail|%{count} subscribers'), { count: subscribersCount })
                    "
                    @click="showSubscribersModal = true"
                  >
                    <span class="participants-summary-text">{{
                      s__('TaskDetail|Participants')
                    }}</span>
                    <span class="subscribers-badge subscribers-badge-inline">{{
                      subscribersCount
                    }}</span>
                  </button>
                  <button
                    v-if="!isAuthorOrAssignee"
                    class="subscribers-summary-trigger subscribers-toggle-trigger"
                    :title="
                      subscribed ? s__('TaskDetail|Unsubscribe') : s__('TaskDetail|Subscribe')
                    "
                    :disabled="subscribing"
                    @click="toggleSubscription"
                  >
                    <BellOffIcon v-if="subscribed" />
                    <BellPlusIcon v-else />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Finished Date (only shown when task is finished) -->
        <div
          v-if="task?.workflowStatus?.isFinal && task?.finishedAt"
          class="row"
          style="margin-bottom: 16px"
        >
          <div style="flex: 1">
            <label>{{ s__('TaskDetail|Finished') }}</label>
            <div class="muted">
              <formatted-date-time
                :value="task.finishedAt"
                :locale="userLocale"
                :timezone="userTimezone"
              />
            </div>
          </div>
        </div>

        <div v-if="showTaskQuickActions" class="row" style="margin-bottom: 16px">
          <div style="flex: 1">
            <TaskQuickActions
              :show-add-description="showAddDescriptionAction"
              :show-create-checklist="showCreateChecklistAction"
              @add-description="startEditDescription"
              @create-checklist="openChecklistCreateForm"
            />
          </div>
        </div>

        <!-- Fifth Line: Description -->
        <div
          v-if="editingDescription || hasTaskDescription"
          class="row"
          style="margin-bottom: 16px"
        >
          <div style="flex: 1">
            <div class="description-label-row">
              <label>{{ s__('TaskDetail|Description') }}</label>
              <button
                v-if="canUpdate && !editingDescription"
                class="btn-icon"
                :title="s__('TaskDetail|Edit description')"
                @click="startEditDescription"
              >
                <EditIcon />
              </button>
            </div>
            <div v-if="!editingDescription" class="task-description-readonly">
              <div
                class="muted task-description rich-content-read"
                @click="handleTaskMentionClick"
                v-html="processedTaskDescription"
              ></div>
            </div>
            <div v-else>
              <TaskDescriptionEditor
                v-model="editForm.description"
                :members="projectMembers"
                :project-id="projectId"
                :enable-wysiwyg="wysiwygEnabled"
                :placeholder="s__('TaskDetail|Description')"
                :preview-enabled="!!editForm.description.trim()"
                :clipboard-upload-enabled="s3AttachmentsEnabled"
                :attachment-context="taskDescriptionAttachmentContext"
                @preview="showDescriptionPreview"
                @cancel="cancelEditDescription"
                @save="saveDescription"
                @upload-finished="trackDescriptionInlineAttachmentUpload"
              />
            </div>
          </div>
        </div>

        <!-- Checklist (only if feature is enabled) -->
        <div v-if="showChecklistContainer" class="row" style="margin-bottom: 16px">
          <div style="flex: 1">
            <Checklist
              ref="checklistRef"
              :key="`checklist-${taskId}`"
              :task-id="taskGlobalId || taskId"
              :checklist="task?.checklist"
              :checklist-completed="task?.checklistCompleted ?? true"
              :can-update="canManageChecklistItems"
              :show-create-button="false"
              :blocking-transition="isBlockingTransition"
              @checklist-created="handleChecklistCreated"
              @checklist-updated="handleChecklistUpdated"
              @checklist-deleted="handleChecklistDeleted"
            />
          </div>
        </div>

        <!-- Separator before additional fields -->
        <div style="margin-top: 24px; padding-top: 16px; border-top: 1px solid #eee">
          <!-- Project Labels -->
          <div class="row">
            <div style="flex: 1">
              <OnTaskUpdateLabelsForm
                scope="project"
                :task-id="taskGlobalId || taskId"
                :project-id="projectId"
                :task-labels="task?.labels || []"
                :available-labels="projectLabels"
                :can-create-label="canManageLabels"
                :chat-id="chatId"
                :from-id="fromId"
                :label-text="
                  personalLabelsEnabled
                    ? s__('TaskDetail|Project Labels')
                    : s__('TaskDetail|Labels')
                "
                :empty-message="s__('TaskDetail|No labels')"
                :edit-button-title="s__('TaskDetail|Edit labels')"
                :create-button-title="__('Create')"
                :create-modal-title="s__('TaskDetail|Create Label')"
                @update:task-labels="handleProjectLabelsUpdate"
                @refetch="refetchProjectLabelsOnly"
              />
            </div>
          </div>

          <!-- Personal Labels -->
          <div v-if="personalLabelsEnabled" class="row">
            <div style="flex: 1">
              <OnTaskUpdateLabelsForm
                scope="personal"
                :task-id="taskGlobalId || taskId"
                :task-labels="task?.personalLabels || []"
                :available-labels="personalLabels"
                :can-create-label="true"
                :label-text="s__('TaskDetail|Personal Labels')"
                :empty-message="s__('TaskDetail|No personal labels')"
                :edit-button-title="s__('TaskDetail|Edit personal labels')"
                :create-button-title="__('Create')"
                :create-modal-title="s__('TaskDetail|Create Personal Label')"
                @update:task-labels="handlePersonalLabelsUpdate"
                @refetch="refetchPersonalLabelsAndUpdate"
              />
            </div>
          </div>

          <!-- Task Hierarchy (Vertical Relations) -->
          <div v-if="verticalRelationsEnabled" class="row">
            <div style="flex: 1">
              <div class="hierarchy-block">
                <div class="collapsible-header" @click="hierarchyExpanded = !hierarchyExpanded">
                  <label
                    style="
                      cursor: pointer;
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      margin: 0;
                      flex: 1;
                    "
                  >
                    <span class="collapse-icon">{{ hierarchyExpanded ? '▼' : '▶' }}</span>
                    {{ s__('TaskDetail|Hierarchy') }}
                  </label>
                </div>
                <div v-if="hierarchyExpanded" class="collapsible-content">
                  <TaskHierarchy
                    :task-id="taskId"
                    :task-global-id="taskGlobalId || task?.id"
                    :project-id="projectId"
                    :can-manage-relations="canManageRelations"
                    :chat-id="chatId"
                    :from-id="fromId"
                    :task-category-id="task?.taskCategory?.id"
                    :project-members="projectMembers"
                    :project-task-categories="projectTaskCategories"
                    :priorities="priorities"
                    :required-fields="requiredFields"
                    :is-personal-project="isPersonalProject"
                    :expanded="hierarchyExpanded"
                    @parent-changed="handleParentChanged()"
                    @child-created="handleChildCreated()"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Horizontal Relations -->
          <div v-if="horizontalRelationTypes.length > 0" class="row">
            <div style="flex: 1">
              <div class="horizontal-relations-block">
                <div
                  class="collapsible-header"
                  @click="horizontalRelationsExpanded = !horizontalRelationsExpanded"
                >
                  <label
                    style="
                      cursor: pointer;
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      margin: 0;
                      flex: 1;
                    "
                  >
                    <span class="collapse-icon">{{ horizontalRelationsExpanded ? '▼' : '▶' }}</span>
                    {{ s__('TaskDetail|Horizontal Relations') }}
                    <span
                      :class="[
                        'badge',
                        'badge-orange',
                        { 'badge-faded': horizontalRelationsCount === 0 },
                      ]"
                      >{{ horizontalRelationsCount }}</span
                    >
                  </label>
                </div>
                <div v-if="horizontalRelationsExpanded" class="collapsible-content">
                  <TaskRelations
                    :task-id="taskGlobalId || taskId"
                    :project-id="projectId"
                    :outgoing-relations="outgoingHorizontalRelations"
                    :incoming-relations="incomingHorizontalRelations"
                    :relation-types="horizontalRelationTypes"
                    :can-manage-relations="canManageRelations"
                    :chat-id="chatId"
                    :from-id="fromId"
                    @relation-added="handleRelationChanged"
                    @relation-updated="handleRelationChanged"
                    @relation-removed="handleRelationChanged"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Time Tracking -->
          <div v-if="timeTrackingEnabled" class="row">
            <div style="flex: 1">
              <div class="time-tracking-block">
                <div
                  class="collapsible-header"
                  @click="timeTrackingExpanded = !timeTrackingExpanded"
                >
                  <label
                    style="
                      cursor: pointer;
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      margin: 0;
                      flex: 1;
                    "
                  >
                    <span class="collapse-icon">{{ timeTrackingExpanded ? '▼' : '▶' }}</span>
                    {{ s__('TaskDetail|Time Tracking') }}
                    <span v-if="!timeTrackingExpanded" class="time-summary-header">
                      <span v-if="myTimeSpent > 0" class="time-summary-badge">
                        <span class="time-badge-label">{{ s__('TaskDetail|This task') }}:</span>
                        <span class="time-badge-value">{{ formatDuration(myTimeSpent) }}</span>
                      </span>
                      <span v-if="totalTimeSpentInDescendants > 0" class="time-summary-badge">
                        <span class="time-badge-label">{{ s__('TaskDetail|In children') }}:</span>
                        <span class="time-badge-value">{{
                          formatDuration(totalTimeSpentInDescendants)
                        }}</span>
                      </span>
                    </span>
                  </label>
                </div>
                <div v-if="timeTrackingExpanded" class="collapsible-content">
                  <div v-if="timeEntriesLoading" class="loading-state">
                    {{ s__('TaskDetail|Loading time entries...') }}
                  </div>
                  <TimeEntries
                    v-else
                    :task-id="taskGlobalId || taskId"
                    :project-id="projectId"
                    :chat-id="chatId"
                    :from-id="fromId"
                    :entries="timeEntries"
                    :total-time-spent="totalTimeSpent"
                    :my-time-spent="myTimeSpent"
                    :total-time-spent-in-descendants="totalTimeSpentInDescendants"
                    :child-tasks="childTasks"
                    :is-admin-or-owner="isAdminOrOwner"
                    :locale="userLocale"
                    :timezone="userTimezone"
                    @time-logged="handleTimeLogged"
                    @time-updated="handleTimeUpdated"
                    @time-deleted="handleTimeDeleted"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Attachments -->
          <div v-if="s3AttachmentsEnabled" class="row">
            <div style="flex: 1">
              <div class="attachments-block">
                <div class="collapsible-header" @click="attachmentsExpanded = !attachmentsExpanded">
                  <label
                    style="
                      cursor: pointer;
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      margin: 0;
                      flex: 1;
                    "
                  >
                    <span class="collapse-icon">{{ attachmentsExpanded ? '▼' : '▶' }}</span>
                    {{ s__('TaskDetail|Attachments') }}
                    <span :class="['badge', { 'badge-faded': attachmentsCount === 0 }]">{{
                      attachmentsCount
                    }}</span>
                  </label>
                </div>
                <div v-if="attachmentsExpanded" class="collapsible-content">
                  <TaskAttachments
                    :task-id="taskGlobalId || taskId"
                    :expanded="attachmentsExpanded"
                    :can-upload="canUpdate"
                    :current-user-id="currentUser?.id"
                    :task-author-id="task?.author?.id"
                    @uploaded="handleAttachmentsChanged"
                    @deleted="handleAttachmentsChanged"
                    @count-changed="handleAttachmentsCountChanged"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Confidentiality -->
          <div v-if="confidentialTasksEnabled && !isPersonalProject" class="row">
            <div style="flex: 1">
              <div class="task-field-label-row">
                <label class="confidentiality-label">
                  <span>{{ s__('TaskDetail|Confidentiality') }}</span>
                  <svg
                    v-if="task?.confidential"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="confidentiality-state-icon confidentiality-state-icon-lock"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M8 0a4 4 0 0 1 4 4v2.05a2.5 2.5 0 0 1 2 2.45v5a2.5 2.5 0 0 1-2.5 2.5h-7A2.5 2.5 0 0 1 2 13.5v-5a2.5 2.5 0 0 1 2-2.45V4a4 4 0 0 1 4-4M4.5 7A1.5 1.5 0 0 0 3 8.5v5A1.5 1.5 0 0 0 4.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 11.5 7zM8 1a3 3 0 0 0-3 3v2h6V4a3 3 0 0 0-3-3"
                    />
                  </svg>
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="confidentiality-state-icon confidentiality-state-icon-unlock"
                    viewBox="0 0 16 16"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M12 0a4 4 0 0 1 4 4v2.5h-1V4a3 3 0 1 0-6 0v2h.5A2.5 2.5 0 0 1 12 8.5v5A2.5 2.5 0 0 1 9.5 16h-7A2.5 2.5 0 0 1 0 13.5v-5A2.5 2.5 0 0 1 2.5 6H8V4a4 4 0 0 1 4-4M2.5 7A1.5 1.5 0 0 0 1 8.5v5A1.5 1.5 0 0 0 2.5 15h7a1.5 1.5 0 0 0 1.5-1.5v-5A1.5 1.5 0 0 0 9.5 7z"
                    />
                  </svg>
                </label>
                <button
                  v-if="canUpdate"
                  class="btn-icon confidentiality-edit-btn"
                  :title="s__('TaskDetail|Edit confidentiality')"
                  @click="startEditConfidentiality"
                >
                  <EditIcon />
                </button>
              </div>
              <div class="muted" style="margin-bottom: 8px">
                <template v-if="task?.confidential">
                  {{
                    s__(
                      'TaskDetail|Task is visible to author, assignee, project admins, and whitelisted viewers.'
                    )
                  }}
                </template>
                <template v-else>
                  {{ s__('TaskDetail|Task is visible to all project members.') }}
                </template>
              </div>
              <div v-if="task?.confidential" class="confidential-whitelist-summary">
                <div
                  v-if="task?.whitelistedViewers && task.whitelistedViewers.length > 0"
                  class="confidential-whitelist-badges"
                >
                  <span
                    v-for="viewer in task.whitelistedViewers"
                    :key="viewer.id"
                    class="label-badge"
                    style="background-color: #2196f3; color: #fff"
                  >
                    <template v-if="viewer.username">@{{ viewer.username }}</template>
                    <template v-if="viewer.username && viewer.name"> - </template>
                    <template v-if="viewer.name">{{ viewer.name }}</template>
                    <template v-if="!viewer.username && !viewer.name"
                      >User {{ viewer.id }}</template
                    >
                  </span>
                </div>
                <span v-else class="muted">{{ s__('TaskDetail|No whitelisted viewers') }}</span>
              </div>
            </div>
          </div>

          <Teleport to="body">
            <div v-if="showConfidentialityModal" class="modal-overlay">
              <div class="modal-content">
                <div class="modal-header">
                  <h3>{{ s__('TaskDetail|Edit confidentiality') }}</h3>
                  <button class="modal-close" aria-label="Close" @click="cancelEditConfidentiality">
                    ×
                  </button>
                </div>
                <div class="modal-body">
                  <label style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px">
                    <input v-model="editForm.confidential" type="checkbox" />
                    {{ s__('TaskDetail|Confidential?') }}
                  </label>
                  <div class="muted" style="margin-bottom: 12px">
                    {{
                      s__(
                        'TaskDetail|Only visible to author, assignee, project admins, and whitelisted viewers.'
                      )
                    }}
                  </div>

                  <div>
                    <label>{{ s__('TaskDetail|Whitelisted Viewers') }}</label>
                    <select
                      v-model="selectedWhitelistedViewerIds"
                      multiple
                      size="4"
                      :disabled="!editForm.confidential"
                      style="
                        width: 100%;
                        padding: 8px;
                        border: 1px solid #ddd;
                        border-radius: 4px;
                        margin-bottom: 8px;
                      "
                    >
                      <option v-for="member in projectMembers" :key="member.id" :value="member.id">
                        {{ formatUserForSelect(member) }}
                      </option>
                    </select>
                    <div class="muted" style="margin-bottom: 8px">
                      {{ s__('TaskDetail|Select users who can view this confidential task') }}
                    </div>
                  </div>

                  <div style="display: flex; gap: 8px; justify-content: flex-end; margin-top: 12px">
                    <button class="btn-sm btn-primary" @click="saveConfidentiality">
                      {{ s__('TaskDetail|Save') }}
                    </button>
                    <button class="btn-sm btn-secondary" @click="cancelEditConfidentiality">
                      {{ s__('TaskDetail|Cancel') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Teleport>

          <!-- Grade -->
          <div
            v-if="
              !isPersonalProject &&
              gradesEnabled &&
              task?.taskCategory?.gradingScale &&
              isTaskFinished
            "
            class="row"
          >
            <div style="flex: 1">
              <label>{{ s__('TaskDetail|Grades') }}</label>
              <div v-if="!editingGrade">
                <!-- Show all grades -->
                <div
                  v-if="task?.grades && task.grades.length > 0"
                  style="display: flex; flex-direction: column; gap: 12px"
                >
                  <div
                    v-for="gradeItem in task.grades"
                    :key="gradeItem.id"
                    class="grade-item"
                    style="
                      display: flex;
                      align-items: center;
                      gap: 8px;
                      flex-wrap: wrap;
                      padding: 8px;
                      border-radius: 4px;
                    "
                  >
                    <span
                      class="grade-badge"
                      :class="{ 'grade-confidential': gradeItem.confidential }"
                    >
                      <strong>{{ gradeItem.grade.value }}</strong>
                      <span
                        v-if="gradeItem.grade.description"
                        class="muted"
                        style="margin-left: 8px"
                      >
                        - {{ gradeItem.grade.description }}
                      </span>
                      <span
                        v-if="gradeItem.confidential"
                        class="confidential-indicator"
                        style="margin-left: 8px"
                        :title="s__('TaskDetail|Confidential grade')"
                      >
                        🔒
                      </span>
                    </span>
                    <span class="muted" style="font-size: 12px">
                      {{ s__('TaskDetail|by') }}
                      <span class="user-inline">
                        <span v-if="gradeItem.givenBy?.username"
                          >@{{ gradeItem.givenBy.username }}</span
                        >
                        <span v-if="gradeItem.givenBy?.username && gradeItem.givenBy?.name"> </span>
                        <span v-if="gradeItem.givenBy?.name">{{ gradeItem.givenBy.name }}</span>
                        <span v-if="!gradeItem.givenBy?.username && !gradeItem.givenBy?.name"
                          >User {{ gradeItem.givenBy?.id }}</span
                        >
                      </span>
                      <span v-if="gradeItem.givenTo">
                        {{ s__('TaskDetail|for') }}
                        <span class="user-inline">
                          <span v-if="gradeItem.givenTo.username"
                            >@{{ gradeItem.givenTo.username }}</span
                          >
                          <span v-if="gradeItem.givenTo.username && gradeItem.givenTo.name"> </span>
                          <span v-if="gradeItem.givenTo.name">{{ gradeItem.givenTo.name }}</span>
                          <span v-if="!gradeItem.givenTo.username && !gradeItem.givenTo.name"
                            >User {{ gradeItem.givenTo.id }}</span
                          >
                        </span>
                      </span>
                    </span>
                    <button
                      v-if="gradeItem.canManageGrade"
                      class="btn-icon"
                      :title="s__('TaskDetail|Edit grade')"
                      @click="startEditGrade(gradeItem)"
                    >
                      <EditIcon />
                    </button>
                    <button
                      v-if="gradeItem.canManageGrade"
                      class="btn-icon"
                      :title="s__('TaskDetail|Delete grade')"
                      @click="deleteGrade(gradeItem)"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>
                <div v-else style="display: flex; align-items: center; gap: 8px">
                  <span class="muted">{{ s__('TaskDetail|No grades yet') }}</span>
                </div>
                <!-- Add Grade button (only if user can grade and doesn't have a grade yet) -->
                <div v-if="task.canGradeTask && !task?.grade" style="margin-top: 8px">
                  <button class="btn-sm" @click="startEditGrade()">
                    {{ s__('TaskDetail|Add Grade') }}
                  </button>
                </div>
              </div>
              <div v-else>
                Confidential
                <div style="margin-bottom: 8px">
                  <label>{{ s__('ProjectSettings|Value') }} *</label>
                  <select
                    v-model="gradeForm.gradeId"
                    style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px"
                  >
                    <option :value="null">{{ s__('TaskDetail|Select grade') }}</option>
                    <option v-for="grade in sortedGrades" :key="grade.id" :value="grade.id">
                      {{ grade.value }}{{ grade.description ? ' - ' + grade.description : '' }}
                    </option>
                  </select>
                </div>
                <div style="margin-bottom: 8px">
                  <label style="display: flex; align-items: center; gap: 8px">
                    <input v-model="gradeForm.confidential" type="checkbox" />
                    <span>{{ s__('TaskDetail|Confidential?') }}</span>
                  </label>
                  <div class="muted" style="font-size: 12px; margin-top: 4px">
                    {{
                      s__(
                        'TaskDetail|Confidential grades are only visible to task author, assignee, and project owners'
                      )
                    }}
                  </div>
                </div>
                <div style="display: flex; gap: 8px">
                  <button
                    class="btn-sm"
                    :disabled="!gradeForm.gradeId || submittingGrade"
                    @click="saveGrade"
                  >
                    {{ submittingGrade ? s__('TaskDetail|Saving...') : s__('TaskDetail|Save') }}
                  </button>
                  <button class="btn-sm btn-secondary" @click="cancelEditGrade">
                    {{ s__('TaskDetail|Cancel') }}
                  </button>
                </div>
                <div v-if="gradeError" class="error" style="margin-top: 8px">{{ gradeError }}</div>
              </div>
            </div>
          </div>

          <!-- Archived Status -->
          <div
            class="row"
            style="margin-bottom: 16px; border-top: 1px solid #eeeeee; padding-top: 16px"
          >
            <div style="flex: 1">
              <div
                style="
                  display: flex;
                  align-items: center;
                  justify-content: flex-end;
                  gap: 8px;
                  position: relative;
                "
              >
                <span ref="infoIconRef" class="info-icon" @click.stop="toggleArchivedInfo"
                  ><InfoIcon
                /></span>
                <div v-if="showArchivedInfo" ref="infoPopupRef" class="info-popup" @click.stop>
                  {{
                    s__('TaskDetail|Archived tasks are hidden by default in task lists and reports')
                  }}
                </div>
                <button
                  v-if="canArchive"
                  class="btn-xs"
                  :class="task?.archived ? 'btn-primary' : 'btn-secondary'"
                  @click="toggleArchived"
                >
                  {{ task?.archived ? s__('TaskDetail|Unarchive') : s__('TaskDetail|Archive') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Comments & History Tabs -->
        <div style="margin-top: 24px">
          <div class="tabs">
            <button
              :class="['tab', { active: activeTab === 'comments' }]"
              @click="activeTab = 'comments'"
            >
              <MessageSquareIcon :size="16" />
              <span>{{ s__('TaskDetail|Comments') }}</span>
              <span v-if="comments.length > 0" class="badge badge-blue">{{ comments.length }}</span>
            </button>
            <button
              :class="['tab', { active: activeTab === 'history' }]"
              @click="activeTab = 'history'"
            >
              <ActivityIcon :size="16" />
              <span>{{ s__('TaskDetail|History') }}</span>
              <span v-if="eventsCount > 0" class="badge badge-blue">{{ eventsCount }}</span>
            </button>
          </div>

          <!-- Comments Tab -->
          <div v-if="activeTab === 'comments'" class="tab-content">
            <!-- File input for comments (shared between main form and replies) -->
            <input
              ref="commentFileInput"
              type="file"
              multiple
              style="display: none"
              accept="image/*,video/*,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,application/zip,application/x-rar-compressed,image/svg+xml,application/vnd.apple.keynote,application/vnd.apple.pages,application/vnd.apple.numbers"
              @change="handleCommentFileSelect"
            />
            <!-- Top-level Comment Form (only when not replying/editing) -->
            <div v-if="canComment && !replyingTo && !editingComment" class="comment-form">
              <TaskCommentEditor
                v-model="commentForm.body"
                :members="projectMembers"
                :project-id="projectId"
                :placeholder="s__('TaskDetail|Write a comment...')"
                :preview-enabled="!!commentForm.body.trim()"
                :submit-disabled="!commentForm.body.trim()"
                :submitting="submittingComment"
                :clipboard-upload-enabled="s3AttachmentsEnabled"
                :attachment-context="commentAttachmentContext"
                :attachment-draft-id="commentAttachmentDraftId"
                @draft-created="commentAttachmentDraftId = $event"
                @preview="showCommentPreview"
                @submit="submitComment"
              >
                <template #toolbar-extra>
                  <button
                    v-if="s3AttachmentsEnabled"
                    type="button"
                    class="toolbar-btn"
                    @click="commentFileInput?.click()"
                  >
                    {{ s__('Attachments|Add files') }}
                  </button>
                </template>
              </TaskCommentEditor>
              <div
                v-if="commentForm.files && commentForm.files.length > 0"
                class="comment-files-preview"
                style="margin-bottom: 8px"
              >
                <div
                  v-for="(file, index) in commentForm.files"
                  :key="index"
                  class="comment-file-item"
                  style="
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 4px 8px;
                    background: #f5f5f5;
                    border-radius: 4px;
                    margin-bottom: 4px;
                  "
                >
                  <span style="font-size: 12px; color: #666">{{ file.name }}</span>
                  <span style="font-size: 11px; color: #999">{{ formatFileSize(file.size) }}</span>
                  <button
                    type="button"
                    style="
                      margin-left: auto;
                      padding: 2px 6px;
                      font-size: 11px;
                      background: #dc3545;
                      color: white;
                      border: none;
                      border-radius: 3px;
                      cursor: pointer;
                    "
                    @click="removeCommentFile(index)"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div v-if="commentError" class="error" style="margin-top: 8px">
                {{ commentError }}
              </div>
            </div>

            <!-- Show previous comments Button -->
            <div
              v-if="comments.length > 0 && commentsHasPreviousPage"
              style="margin-bottom: 16px; text-align: center"
            >
              <button
                type="button"
                class="btn-sm btn-secondary"
                :disabled="loadingMoreComments"
                @click.prevent="loadEarlierComments"
              >
                {{
                  loadingMoreComments ? s__('Loading…') : s__('TaskDetail|Show previous comments')
                }}
              </button>
            </div>

            <!-- Comments List -->
            <div v-if="comments.length === 0 && !canComment" class="empty-state">
              {{ s__('TaskDetail|No comments yet.') }}
            </div>
            <div v-else-if="comments.length === 0" class="empty-state">
              {{ s__('TaskDetail|No comments yet. Be the first to comment!') }}
            </div>
            <div v-else class="comments-list">
              <div v-for="comment in topLevelComments" :key="comment.id" class="comment-thread">
                <!-- Top-level comment -->
                <comment-item
                  :comment="comment"
                  :project-roles-map="projectRolesMap"
                  :can-comment="canComment"
                  :current-user-id="currentUserId"
                  :is-editing="editingComment?.id === comment.id"
                  :project-id="projectId"
                  :task-mentions-map="taskMentionsMap"
                  :locale="userLocale"
                  :timezone="userTimezone"
                  @reply="startReply"
                  @edit="startEditComment"
                  @delete="deleteComment"
                  @delete-attachment="handleDeleteCommentAttachment"
                />

                <!-- Edit form for this comment -->
                <div v-if="editingComment?.id === comment.id" class="comment-form reply-form">
                  <TaskCommentEditor
                    v-model="commentForm.body"
                    :members="projectMembers"
                    :project-id="projectId"
                    :placeholder="s__('TaskDetail|Comment')"
                    :show-preview="false"
                    :preview-enabled="!!commentForm.body.trim()"
                    :submit-disabled="!commentForm.body.trim()"
                    :submitting="submittingComment"
                    :clipboard-upload-enabled="s3AttachmentsEnabled"
                    :attachment-context="editingCommentAttachmentContext"
                    @upload-finished="trackCommentInlineAttachmentUpload"
                    @preview="showCommentPreview"
                    @submit="submitComment"
                  />
                  <div style="display: flex; gap: 8px; margin-top: 8px">
                    <button class="btn-sm btn-secondary" @click="cancelEdit">
                      {{ s__('TaskDetail|Cancel') }}
                    </button>
                  </div>
                  <div v-if="commentError" class="error" style="margin-top: 8px">
                    {{ commentError }}
                  </div>
                </div>

                <!-- Reply form for this comment -->
                <div v-if="replyingTo?.id === comment.id" class="comment-form reply-form">
                  <div class="replying-to">
                    {{
                      sprintf(s__('TaskDetail|Replying to %{user}'), {
                        user: replyingTo.authorName,
                      })
                    }}
                  </div>
                  <div
                    class="comment-textarea-wrapper"
                    style="position: relative; margin-bottom: 8px"
                  >
                    <textarea
                      ref="commentTextarea"
                      v-model="commentForm.body"
                      :placeholder="s__('TaskDetail|Write a reply...')"
                      rows="3"
                      style="width: 100%; padding-right: 32px; box-sizing: border-box"
                      @keydown="handleCommentKeyDown"
                      @input="handleCommentInput"
                      @contextmenu.prevent.stop="commentContextMenu.handleContextMenu"
                    ></textarea>
                    <button
                      type="button"
                      class="comment-attach-btn"
                      @click="commentFileInput?.click()"
                      :title="s__('Attachments|Add files')"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-paperclip w-4 h-4 text-gray-600"
                      >
                        <path d="M13.234 20.252 21 12.3"></path>
                        <path
                          d="m16 6-8.414 8.586a2 2 0 0 0 0 2.828 2 2 0 0 0 2.828 0l8.414-8.586a4 4 0 0 0 0-5.656 4 4 0 0 0-5.656 0l-8.415 8.585a6 6 0 1 0 8.486 8.486"
                        ></path>
                      </svg>
                    </button>
                  </div>
                  <markdown-context-menu
                    :show="commentContextMenu.showMenu"
                    :position="commentContextMenu.menuPosition"
                    :has-selection="commentContextMenu.hasSelection"
                    @format="handleCommentFormat"
                  />
                  <mention-autocomplete
                    :show="commentMentions.showAutocomplete"
                    :position="commentMentions.autocompletePosition"
                    :filtered-members="commentMentions.filteredMembers"
                    :selected-index="commentMentions.selectedIndex"
                    @select="handleCommentMentionSelect"
                    @hover="handleCommentMentionHover"
                  />
                  <div style="display: flex; gap: 8px">
                    <button
                      class="btn-sm"
                      :disabled="!commentForm.body.trim() || submittingComment"
                      @click="submitComment"
                    >
                      {{
                        submittingComment
                          ? s__('TaskDetail|Submitting...')
                          : s__('TaskDetail|Reply')
                      }}
                    </button>
                    <button class="btn-sm btn-secondary" @click="cancelReply">
                      {{ s__('TaskDetail|Cancel') }}
                    </button>
                  </div>
                  <div v-if="commentError" class="error" style="margin-top: 8px">
                    {{ commentError }}
                  </div>
                </div>

                <!-- Nested replies -->
                <div v-if="comment.replies && comment.replies.length > 0" class="replies">
                  <!-- Show previous replies button -->
                  <div
                    v-if="hasMoreReplies(comment)"
                    style="margin-bottom: 12px; text-align: center"
                  >
                    <button
                      type="button"
                      class="btn-sm btn-secondary"
                      :disabled="loadingMoreReplies[comment.id]"
                      @click.prevent="loadMoreReplies(comment)"
                    >
                      {{
                        loadingMoreReplies[comment.id]
                          ? s__('Loading…')
                          : s__('TaskDetail|Show previous replies')
                      }}
                    </button>
                  </div>
                  <div v-for="reply in comment.replies" :key="reply.id">
                    <comment-item
                      :comment="reply"
                      :project-roles-map="projectRolesMap"
                      :can-comment="canComment"
                      :current-user-id="currentUserId"
                      :is-reply="true"
                      :is-editing="editingComment?.id === reply.id"
                      :project-id="projectId"
                      :task-mentions-map="taskMentionsMap"
                      :locale="userLocale"
                      :timezone="userTimezone"
                      @edit="startEditComment"
                      @delete="deleteComment"
                      @delete-attachment="handleDeleteCommentAttachment"
                    />

                    <!-- Edit form for reply -->
                    <div v-if="editingComment?.id === reply.id" class="comment-form reply-form">
                      <TaskCommentEditor
                        v-model="commentForm.body"
                        :members="projectMembers"
                        :project-id="projectId"
                        :placeholder="s__('TaskDetail|Comment')"
                        :show-preview="false"
                        :preview-enabled="!!commentForm.body.trim()"
                        :submit-disabled="!commentForm.body.trim()"
                        :submitting="submittingComment"
                        :clipboard-upload-enabled="s3AttachmentsEnabled"
                        :attachment-context="editingCommentAttachmentContext"
                        @upload-finished="trackCommentInlineAttachmentUpload"
                        @preview="showCommentPreview"
                        @submit="submitComment"
                      />
                      <div style="display: flex; gap: 8px; margin-top: 8px">
                        <button class="btn-sm btn-secondary" @click="cancelEdit">
                          {{ s__('TaskDetail|Cancel') }}
                        </button>
                      </div>
                      <div v-if="commentError" class="error" style="margin-top: 8px">
                        {{ commentError }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- History Tab -->
          <div v-if="activeTab === 'history'" class="tab-content">
            <div v-if="events.length === 0" class="empty-state">
              {{ s__('TaskDetail|No history yet.') }}
            </div>
            <div v-else class="timeline">
              <div v-for="event in events" :key="event.id" class="timeline-item">
                <div class="timeline-dot"></div>
                <div class="timeline-content">
                  <div class="event-header">
                    <strong>{{ event.eventName }}</strong>
                    <span class="event-time"
                      ><formatted-date-time
                        :value="event.createdAt"
                        :locale="userLocale"
                        :timezone="userTimezone"
                    /></span>
                  </div>
                  <div class="event-description">{{ event.description }}</div>
                  <div class="event-actor">by {{ event.actorName }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Comment Preview Modal -->
      <Teleport to="body">
        <div v-if="showPreviewModal" class="modal-overlay">
          <div class="modal-content" style="max-width: 600px">
            <div class="modal-header">
              <h3>{{ s__('TaskDetail|Comment Preview') }}</h3>
              <button class="modal-close" aria-label="Close" @click="showPreviewModal = false">
                ×
              </button>
            </div>
            <div class="modal-body">
              <div class="preview-content rich-content-read rich-content-preview">
                <div v-html="previewContent"></div>
                <div
                  v-if="!previewContent"
                  class="muted"
                  style="color: #9e9e9e; font-style: italic"
                >
                  {{ s__('TaskDetail|No content to preview') }}
                </div>
              </div>
              <div style="margin-top: 16px; text-align: right">
                <button class="btn-sm btn-secondary" @click="showPreviewModal = false">
                  {{ s__('TaskDetail|Close') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- Description Preview Modal -->
      <Teleport to="body">
        <div v-if="showDescriptionPreviewModal" class="modal-overlay">
          <div class="modal-content" style="max-width: 600px">
            <div class="modal-header">
              <h3>{{ s__('TaskDetail|Description Preview') }}</h3>
              <button
                class="modal-close"
                aria-label="Close"
                @click="showDescriptionPreviewModal = false"
              >
                ×
              </button>
            </div>
            <div class="modal-body">
              <div class="preview-content rich-content-read rich-content-preview">
                <div v-html="descriptionPreviewContent"></div>
                <div
                  v-if="!descriptionPreviewContent"
                  class="muted"
                  style="color: #9e9e9e; font-style: italic"
                >
                  {{ s__('TaskDetail|No content to preview') }}
                </div>
              </div>
              <div style="margin-top: 16px; text-align: right">
                <button class="btn-sm btn-secondary" @click="showDescriptionPreviewModal = false">
                  {{ s__('TaskDetail|Close') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- Transition Modal -->
      <Teleport to="body">
        <div v-if="showTransitionModal" class="modal-overlay">
          <div class="modal-content">
            <div class="modal-header">
              <h3>{{ s__('TaskDetail|Change Status') }}</h3>
              <button class="modal-close" aria-label="Close" @click="closeTransitionModal">
                ×
              </button>
            </div>
            <div class="modal-body">
              <div class="current-status" style="margin-bottom: 16px">
                <span class="muted">{{ s__('TaskDetail|Current status:') }}</span>
                <strong
                  style="margin-left: 8px; display: inline-flex; align-items: center; gap: 6px"
                >
                  <span
                    v-if="task?.workflowStatus?.color"
                    class="status-dot"
                    :style="{ backgroundColor: task.workflowStatus.color }"
                  ></span>
                  {{ task?.workflowStatus?.name || '—' }}
                </strong>
              </div>
              <div v-if="loadingTransitions" style="text-align: center; padding: 16px">
                {{ s__('Loading…') }}
              </div>
              <div
                v-else-if="availableTransitions.length === 0"
                style="text-align: center; padding: 16px; color: #757575"
              >
                {{ s__('TaskDetail|No available transitions') }}
              </div>
              <div
                v-else
                style="display: flex; flex-direction: column; align-items: flex-start; gap: 8px"
              >
                <button
                  v-for="transition in availableTransitions"
                  :key="transition.id"
                  type="button"
                  :disabled="executing || isTransitionDisabled(transition)"
                  :title="getTransitionTooltip(transition)"
                  class="btn-secondary transition-button-modal"
                  :class="{ 'transition-disabled-checklist': isTransitionDisabled(transition) }"
                  @click="executeTransition(transition)"
                >
                  <span class="transition-name">{{
                    transition.name || s__('TaskDetail|Move')
                  }}</span>
                  <span class="transition-status">
                    <span class="transition-arrow">
                      <PlayIcon />
                    </span>
                    <span
                      v-if="transition.targetStatus?.color"
                      class="status-dot"
                      :style="{ backgroundColor: transition.targetStatus.color }"
                    ></span>
                    <span class="transition-status-text">{{ transition.targetStatus.name }}</span>
                    <span
                      v-if="isTransitionDisabled(transition)"
                      class="checklist-lock-icon"
                      :title="getTransitionTooltip(transition)"
                    >
                      🔒
                    </span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- Subscribers Modal -->
      <Teleport to="body">
        <div v-if="showSubscribersModal" class="modal-overlay">
          <div class="modal-content">
            <div class="modal-header">
              <h3>
                {{ sprintf(s__('TaskDetail|Subscribers (%{count})'), { count: subscribersCount }) }}
              </h3>
              <button class="modal-close" aria-label="Close" @click="showSubscribersModal = false">
                ×
              </button>
            </div>
            <div class="modal-body">
              <!-- Subscribe other users form -->
              <div
                v-if="canSubscribeOthers && availableUsersToSubscribe.length > 0"
                class="subscribe-user-form"
              >
                <label>{{ s__('TaskDetail|Subscribe a user') }}</label>
                <div class="subscribe-user-controls">
                  <div class="subscribe-user-select">
                    <user-select
                      v-model="selectedUserToSubscribe"
                      :users="availableUsersToSubscribe"
                      :placeholder="s__('TaskDetail|Select user...')"
                      :show-empty-option="false"
                      :disabled="subscribingUser"
                      :searchable="true"
                      :project-id="project?.id"
                    />
                  </div>
                  <button
                    class="btn-sm subscribe-user-submit-btn"
                    :disabled="!selectedUserToSubscribe || subscribingUser"
                    :title="
                      subscribingUser
                        ? s__('TaskDetail|Subscribing...')
                        : s__('TaskDetail|Subscribe a user')
                    "
                    @click="subscribeUserToTask"
                  >
                    <span class="subscribe-user-submit-btn-text">{{
                      subscribingUser
                        ? s__('TaskDetail|Subscribing...')
                        : s__('TaskDetail|Subscribe a user')
                    }}</span>
                    <span class="subscribe-user-submit-btn-icon" aria-hidden="true">
                      <PlusIcon :size="16" color="#fff" />
                    </span>
                  </button>
                </div>
              </div>

              <div v-if="loadingSubscribers" class="empty-state">
                {{ s__('Loading…') }}
              </div>
              <div v-else-if="subscribers && subscribers.length > 0" class="subscribers-list">
                <div v-for="subscriber in subscribers" :key="subscriber.id" class="subscriber-item">
                  <div class="user-display-content" style="flex: 1">
                    <div class="user-username">
                      {{ subscriber.username ? '@' + subscriber.username : '' }}
                    </div>
                    <div v-if="subscriber.name" class="user-name">{{ subscriber.name }}</div>
                    <div v-if="!subscriber.username && !subscriber.name" class="user-fallback">
                      User {{ subscriber.id }}
                    </div>
                  </div>
                  <button
                    v-if="canSubscribeOthers"
                    class="btn-icon"
                    :title="s__('TaskDetail|Unsubscribe')"
                    :disabled="subscribingUser"
                    @click="unsubscribeUserFromTask(subscriber.id)"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <div v-else class="empty-state">
                {{ s__('TaskDetail|No subscribers') }}
              </div>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- Task Category Selection Modal -->
      <Teleport to="body">
        <div v-if="showTaskCategoryModal" class="modal-overlay">
          <div class="modal-content">
            <div class="modal-header">
              <h3>{{ s__('TaskDetail|Change task category') }}</h3>
              <button class="modal-close" aria-label="Close" @click="cancelEditTaskCategory">
                ×
              </button>
            </div>
            <div class="modal-body">
              <div class="row">
                <div style="flex: 1">
                  <label>{{ s__('TaskDetail|Change task category') }}</label>
                  <select
                    v-model="selectedTaskCategoryId"
                    class="task-category-select-modal"
                    :disabled="changingCategory || loadingWorkflowStatuses"
                  >
                    <option
                      v-for="category in projectTaskCategories"
                      :key="category.id"
                      :value="category.id"
                    >
                      {{ category.name }}
                    </option>
                  </select>
                </div>
              </div>
              <div v-if="categoryChangeError" class="error">{{ categoryChangeError }}</div>
            </div>
            <div class="modal-footer">
              <button
                class="btn-secondary"
                :disabled="changingCategory"
                @click="cancelEditTaskCategory"
              >
                {{ s__('TaskDetail|Cancel') }}
              </button>
              <button
                class="btn-primary"
                :disabled="
                  changingCategory ||
                  loadingWorkflowStatuses ||
                  !selectedTaskCategoryId ||
                  selectedTaskCategoryId === task?.taskCategory?.id
                "
                @click="handleTaskCategoryChange"
              >
                <span v-if="changingCategory || loadingWorkflowStatuses">{{
                  s__('Loading…')
                }}</span>
                <span v-else>{{ s__('TaskDetail|Continue') }}</span>
              </button>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- Workflow Status Selection Modal -->
      <Teleport to="body">
        <div v-if="showWorkflowStatusModal" class="modal-overlay">
          <div class="modal-content">
            <div class="modal-header">
              <h3>{{ s__('TaskDetail|Select Workflow Status') }}</h3>
              <button class="modal-close" aria-label="Close" @click="closeWorkflowStatusModal">
                ×
              </button>
            </div>
            <div class="modal-body">
              <p style="margin-bottom: 16px; color: #757575">
                {{
                  s__(
                    'TaskDetail|The new task category uses a different workflow. Please select the workflow status for this task.'
                  )
                }}
              </p>
              <div v-if="loadingWorkflowStatuses" style="text-align: center; padding: 16px">
                {{ s__('Loading…') }}
              </div>
              <div
                v-else-if="newWorkflowStatuses.length === 0"
                style="text-align: center; padding: 16px; color: #757575"
              >
                {{ s__('TaskDetail|No workflow statuses available') }}
              </div>
              <div v-else style="display: flex; flex-direction: column; gap: 8px">
                <button
                  v-for="status in newWorkflowStatuses"
                  :key="status.id"
                  type="button"
                  class="btn-secondary transition-button-modal btn-sm"
                  :disabled="changingCategory"
                  @click="selectWorkflowStatus(status)"
                >
                  <span class="transition-status">
                    <span class="transition-arrow">
                      <PlayIcon />
                    </span>
                    <span
                      v-if="status.color"
                      class="status-dot"
                      :style="{ backgroundColor: status.color }"
                    ></span>
                    {{ status.name }}
                  </span>
                </button>
              </div>
              <div v-if="categoryChangeError" class="error" style="margin-top: 12px">
                {{ categoryChangeError }}
              </div>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- Grade Loss Warning Modal -->
      <Teleport to="body">
        <div v-if="showGradeWarningModal" class="modal-overlay">
          <div class="modal-content">
            <div class="modal-header">
              <h3>{{ s__('TaskDetail|Warning: Grades Will Be Lost') }}</h3>
              <button class="modal-close" aria-label="Close" @click="closeGradeWarningModal">
                ×
              </button>
            </div>
            <div class="modal-body">
              <p style="margin-bottom: 16px; color: #c62828">
                {{
                  s__(
                    'TaskDetail|Changing the task category will remove all existing grades because the new task category uses a different grading scale.'
                  )
                }}
              </p>
              <p style="margin-bottom: 16px; color: #757575">
                {{ s__('TaskDetail|This action cannot be undone.') }}
              </p>
              <div style="display: flex; gap: 8px; justify-content: flex-end; margin-top: 24px">
                <button
                  class="btn-sm btn-secondary"
                  :disabled="changingCategory"
                  @click="closeGradeWarningModal"
                >
                  {{ s__('TaskDetail|Cancel') }}
                </button>
                <button
                  class="btn-sm"
                  :disabled="changingCategory"
                  @click="confirmCategoryChangeWithGradeLoss"
                >
                  {{
                    changingCategory ? s__('TaskDetail|Changing...') : s__('TaskDetail|Continue')
                  }}
                </button>
              </div>
              <div v-if="categoryChangeError" class="error" style="margin-top: 12px">
                {{ categoryChangeError }}
              </div>
            </div>
          </div>
        </div>
      </Teleport>

      <!-- Clone Task Modal -->
      <CloneTaskModal
        v-if="canClone"
        :visible="showCloneModal"
        :task="task"
        :project-id="projectId"
        :locale="userLocale"
        :timezone="userTimezone"
        @close="showCloneModal = false"
        @cloned="handleTasksCloned"
      />

      <!-- Alert Banner for Task Operations -->
      <AlertBanner
        v-if="taskAlert"
        :type="taskAlert.type"
        :title="taskAlert.title"
        :message="taskAlert.message"
        @dismiss="taskAlert = null"
      />
    </div>
  </AuthenticatedView>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useQuery, useMutation, useApolloClient, useLazyQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import CommentItem from './CommentItem.vue'
import { telegramMarkdownToHtml } from '../../../utils/telegramEditorCodec.js'
import { extractTaskMentions } from '../../../utils/taskMentions.js'
import { fetchMentionedTasks } from '../../../utils/fetchMentionedTasks.js'
import { showConfirm } from '@/utils/telegramWebApp'
import { useMarkdownHotkeys } from '../../../composables/useMarkdownHotkeys.js'
import { useMarkdownContextMenu } from '../../../composables/useMarkdownContextMenu.js'
import { useMentionAutocomplete } from '../../../composables/useMentionAutocomplete.js'
import { useTaskMentionAutocomplete } from '../../../composables/useTaskMentionAutocomplete.js'
import { useBodyScrollLock } from '../../../composables/useBodyScrollLock.js'
import MarkdownContextMenu from '../../common/MarkdownContextMenu.vue'
import MarkdownFormatToolbar from '../../common/MarkdownFormatToolbar.vue'
import MentionAutocomplete from '../../common/MentionAutocomplete.vue'
import TaskAutocomplete from '../../common/TaskAutocomplete.vue'
import UserSelect from '../../common/UserSelect.vue'
import PrioritySelect from '../../common/PrioritySelect.vue'
import DateTimeInput from '../../common/DateTimeInput.vue'
import SingleLineTextarea from '../../common/SingleLineTextarea.vue'
import { s__, __, sprintf, translatePriority, loadTranslations } from '../../../locale/index.js'
import { buildProjectRolesMap, getProjectRole } from '@/utils/projectRoles'
import FormattedDateTime from '../../common/FormattedDateTime.vue'
import EditIcon from '../../common/icons/EditIcon.vue'
import TrashIcon from '../../common/icons/TrashIcon.vue'
import PriorityIcon from '../../common/icons/PriorityIcon.vue'
import TransitionIcon from '../../common/icons/TransitionIcon.vue'
import InfoIcon from '../../common/icons/InfoIcon.vue'
import PlayIcon from '../../common/icons/PlayIcon.vue'
import BellPlusIcon from '../../common/icons/BellPlusIcon.vue'
import BellOffIcon from '../../common/icons/BellOffIcon.vue'
import CloseIcon from '../../common/icons/CloseIcon.vue'
import MessageSquareIcon from '../../common/icons/MessageSquareIcon.vue'
import ActivityIcon from '../../common/icons/ActivityIcon.vue'
import PlusIcon from '../../common/icons/PlusIcon.vue'
import Breadcrumbs from '../../common/Breadcrumbs.vue'
import TaskRelations from './TaskRelations.vue'
import TaskHierarchy from './TaskHierarchy.vue'
import TaskAttachments from './TaskAttachments.vue'
import TimeEntries from './TimeEntries.vue'
import TaskDescriptionEditor from './TaskDescriptionEditor.vue'
import TaskCommentEditor from './TaskCommentEditor.vue'
import CloneTaskModal from './CloneTaskModal.vue'
import ArchivedProjectWarning from '../../common/ArchivedProjectWarning.vue'
import OnTaskUpdateLabelsForm from '../../common/OnTaskUpdateLabelsForm.vue'
import Checklist from './Checklist.vue'
import TaskQuickActions from './TaskQuickActions.vue'
import AppHeader from '../../common/AppHeader.vue'
import AuthenticatedView from '../../common/AuthenticatedView.vue'
import AlertBanner from '../../common/AlertBanner.vue'
import { removeInitialLoading } from '@/utils/loadingScreen'
import { readFullWidthPreference, writeFullWidthPreference } from '@/utils/fullWidthPreference'

const props = defineProps({
  projectId: String,
  taskId: String,
  taskGlobalId: String,
  chatId: String,
  fromId: String,
})
const apolloClient = useApolloClient().client

// Feature flag for S3 attachments
const s3AttachmentsEnabled = computed(() => {
  return window.gon?.s3_attachments_enabled === true
})
const wysiwygEnabled = computed(() => window.gon?.wysiwyg_editor_enabled !== false)
const taskDescriptionAttachmentContext = computed(() => ({
  attachableType: 'task',
  attachableId: taskGlobalId.value || task.value?.id || props.taskId,
}))

const loading = ref(true)
const error = ref('')
const task = ref(null)
const project = ref(null)
const taskGlobalId = computed(() => task.value?.id || props.taskGlobalId || null)
// Preserve checklistsEnabled to prevent component unmount during refetch
// This ref preserves the value even when project becomes undefined during refetch
const checklistsEnabled = ref(false)
const isFullWidth = ref(false)
const canToggleFullWidth = ref(false)
const projectLabels = ref([])
const personalLabels = ref([])
const personalLabelsEnabled = ref(false)
const projectMembers = ref([])
const memberships = ref([])
const projectRolesMap = computed(() => buildProjectRolesMap(memberships.value))
const projectTaskCategories = ref([])
const requiredFields = ref([])
const executing = ref(false)
const transitionError = ref('')
const showTransitionModal = ref(false)
const showTaskCategoryModal = ref(false)

// Lock body scroll when transition modal is open
useBodyScrollLock(showTransitionModal)
useBodyScrollLock(showTaskCategoryModal)

const confidentialTasksEnabled = ref(false)
const canManageLabels = ref(false)
const showSubscribersModal = ref(false)
const subscribers = ref([])
const loadingSubscribers = ref(false)
const editingTaskCategory = ref(false)
const selectedTaskCategoryId = ref(null)
const showWorkflowStatusModal = ref(false)
const showGradeWarningModal = ref(false)
const newWorkflowStatuses = ref([])
const loadingWorkflowStatuses = ref(false)
const pendingCategoryChange = ref(null)
const changingCategory = ref(false)
const categoryChangeError = ref('')
const showArchivedInfo = ref(false)
const infoIconRef = ref(null)
const infoPopupRef = ref(null)
const newLabelForm = ref({
  title: '',
  color: '#2196F3',
})
const showConfidentialityModal = ref(false)
const selectedWhitelistedViewerIds = ref([])
const gradesEnabled = ref(false)

// Board back-link state
const boardId = ref(null)
const board = ref(null)
const loadingBoard = ref(false)

// Grade state
const editingGrade = ref(false)
const gradeError = ref('')
const submittingGrade = ref(false)
const gradeForm = ref({
  gradeId: null,
  confidential: false,
})

// Editing state
const editingTitle = ref(false)
const editingDescription = ref(false)
const editingPriority = ref(false)
const editingDueDate = ref(false)
const taskAlert = ref(null)
const editingAssignee = ref(false)
const originalAssigneeId = ref(null)
const assigneeEditRef = ref(null)
const isOpeningAssigneeDropdown = ref(false)
const priorityEditRef = ref(null)
const isOpeningPrioritySelect = ref(false)
const editForm = ref({
  title: '',
  description: '',
  priority: null,
  dueDate: '',
  dueHour: '00',
  dueMinute: '00',
  assigneeId: null,
  confidential: false,
})
const checklistRef = ref(null)
const checklistCreateRequested = ref(false)
const canUpdate = ref(false)
const canUpdateAssigneeBackend = ref(false)
const canArchive = ref(false)
const canClone = ref(false)
const showCloneModal = ref(false)
const shareCopyFeedback = ref(false)
const shareFeedbackText = ref('')
let shareCopyFeedbackTimeout = null
const showTaskMenu = ref(false)
const taskMenuRef = ref(null)
const isPersonalProject = ref(false)
const projectArchived = ref(false)
const taskNotFound = ref(false)

// Subscription state
const subscribed = ref(false)
const subscribersCount = ref(0)
const subscribing = ref(false)
const canSubscribeOthers = ref(false)
const selectedUserToSubscribe = ref(null)
const subscribingUser = ref(false)

// Horizontal relations state
const horizontalRelationTypes = ref([])
const outgoingHorizontalRelations = ref([])
const incomingHorizontalRelations = ref([])
const canManageRelations = ref(false)

// Vertical relations (hierarchy)
const verticalRelationsEnabled = ref(false)
const childTasks = ref([]) // Only populated when time tracking is expanded

// Time tracking
const timeTrackingEnabled = ref(false)
const timeTrackingExpanded = ref(false)
const hierarchyExpanded = ref(false)
const attachmentsExpanded = ref(false)
const attachmentsCount = ref(0)
const horizontalRelationsExpanded = ref(false)
const timeEntries = ref([])
const timeEntriesLoading = ref(false)
const timeEntriesLoaded = ref(false)
const totalTimeSpent = ref(0)
const myTimeSpent = ref(0)
const totalTimeSpentInDescendants = ref(0)

// Helper function to format duration in minutes to "Xh Ym" format
function formatDuration(minutes) {
  if (!minutes || minutes === 0) return '0m'
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  const parts = []
  if (hours > 0) parts.push(`${hours}h`)
  if (mins > 0 || parts.length === 0) parts.push(`${mins}m`)
  return parts.join(' ')
}

function formatSubscriberLabel(subscriber) {
  if (!subscriber) return ''
  if (subscriber.username && subscriber.name) {
    return `@${subscriber.username} ${subscriber.name}`
  }
  if (subscriber.username) return `@${subscriber.username}`
  if (subscriber.name) return subscriber.name
  return `User ${subscriber.id}`
}

const isAdminOrOwner = ref(false)

// Events count (loaded on page load, full events loaded lazily)
const eventsCount = ref(0)

// Comments & History tabs
const activeTab = ref('comments')
const comments = ref([])
const canComment = ref(false)
const currentUserId = ref(null)
const userTimezone = ref(null)
const userLocale = ref(null)
const commentTextarea = ref(null)
const descriptionTextarea = ref(null)

// Computed: horizontal relations count
const horizontalRelationsCount = computed(() => {
  return (
    (outgoingHorizontalRelations.value?.length || 0) +
    (incomingHorizontalRelations.value?.length || 0)
  )
})

// Computed: check if current user is author or assignee
const isAuthorOrAssignee = computed(() => {
  if (!currentUserId.value || !task.value) return false
  return (
    task.value.author?.id === currentUserId.value || task.value.assignee?.id === currentUserId.value
  )
})

// Computed: project roles for task assignee and author
const assigneeProjectRole = computed(() => {
  if (!task.value?.assignee?.id) return null
  return getProjectRole(projectRolesMap.value, task.value.assignee.id)
})

const authorProjectRole = computed(() => {
  if (!task.value?.author?.id) return null
  return getProjectRole(projectRolesMap.value, task.value.author.id)
})

const showTaskCategoryChip = computed(
  () => !!task.value?.taskCategory?.name && (projectTaskCategories.value?.length || 0) > 1
)

const hasTaskDescription = computed(() => (task.value?.description || '').trim().length > 0)
const showAddDescriptionAction = computed(
  () => !editingDescription.value && !hasTaskDescription.value && canUpdate.value
)
const showCreateChecklistAction = computed(
  () =>
    checklistsEnabled.value &&
    !task.value?.checklist?.id &&
    (canManageChecklistItems.value || canUpdate.value)
)
const showTaskQuickActions = computed(
  () => showAddDescriptionAction.value || showCreateChecklistAction.value
)
const showChecklistContainer = computed(
  () => checklistsEnabled.value && (!!task.value?.checklist?.id || checklistCreateRequested.value)
)

function mergeChecklistForTask(incomingChecklist) {
  if (!incomingChecklist) return null

  const existingChecklist = task.value?.checklist
  const incomingItems = Array.isArray(incomingChecklist.items) ? incomingChecklist.items : null
  const existingItems = Array.isArray(existingChecklist?.items) ? existingChecklist.items : []
  const existingItemsById = new Map(existingItems.map(item => [item.id, item]))
  const mergedItems = incomingItems
    ? incomingItems.map(item => ({ ...(existingItemsById.get(item.id) || {}), ...item }))
    : existingItems

  return {
    ...existingChecklist,
    ...incomingChecklist,
    items: mergedItems,
  }
}

const hasTaskMenuActions = computed(
  () => canToggleFullWidth.value || canClone.value || !isPersonalProject.value
)

// Computed: check if user can update assignee (reactive to task changes)
// User can update assignee if:
// 1. They can update_task (from backend), OR
// 2. They are the current assignee (can delegate)
const canUpdateAssignee = computed(() => {
  if (!currentUserId.value || !task.value) return false
  // Check backend permission first
  if (canUpdateAssigneeBackend.value) return true
  // Also check if current user is the assignee (can delegate)
  return task.value.assignee?.id === currentUserId.value
})

// Computed: check if user can manage checklist items (reactive to task changes)
// User can manage checklist items if:
// 1. They can update_task (from backend), OR
// 2. They are the current assignee (assignees can manage checklist items per policy)
const canManageChecklistItems = computed(() => {
  if (!currentUserId.value || !task.value) return false
  // Check backend permission first
  if (canUpdate.value) return true
  // Also check if current user is the assignee (assignees can manage checklist items)
  return task.value.assignee?.id === currentUserId.value
})

// Comment form state
const commentForm = ref({ body: '', files: [] })
const commentAttachmentDraftId = ref(null)
const commentAttachmentContext = computed(() => ({
  projectId: props.projectId,
  purpose: 'task_comment_body',
}))
const editingCommentAttachmentContext = computed(() => ({
  attachableType: 'comment',
  attachableId: editingComment.value?.id || null,
}))
const descriptionOriginalInlineAttachmentIds = ref([])
const descriptionUploadedInlineAttachmentIds = ref([])
const commentOriginalInlineAttachmentIds = ref([])
const commentUploadedInlineAttachmentIds = ref([])
const commentFileInput = ref(null)
const replyingTo = ref(null)
const editingComment = ref(null)
const submittingComment = ref(false)
const commentError = ref('')
const showPreviewModal = ref(false)
const previewContent = ref('')
const showDescriptionPreviewModal = ref(false)

useBodyScrollLock(showConfidentialityModal)
const descriptionPreviewContent = ref('')

// Set up markdown hotkeys
const commentHotkeys = useMarkdownHotkeys(commentTextarea)
const descriptionHotkeys = useMarkdownHotkeys(descriptionTextarea)

// Set up context menus
const commentContextMenu = useMarkdownContextMenu(commentTextarea)
const descriptionContextMenu = useMarkdownContextMenu(descriptionTextarea)

// Set up mention autocomplete (projectMembers is already a ref)
const commentMentions = useMentionAutocomplete(commentTextarea, projectMembers)
const descriptionMentions = useMentionAutocomplete(descriptionTextarea, projectMembers)

// Set up task mention autocomplete
const projectIdRef = computed(() => props.projectId)
const commentTaskMentions = useTaskMentionAutocomplete(commentTextarea, projectIdRef)
const descriptionTaskMentions = useTaskMentionAutocomplete(descriptionTextarea, projectIdRef)

// Combined key handler for comments (handles hotkeys, user mentions, and task mentions)
const handleCommentKeyDown = event => {
  // Store active textarea in composable for mention autocomplete
  if (event.target && event.target.tagName === 'TEXTAREA') {
    // The composable will handle this, but we can also update refs here if needed
    commentTextarea.value = event.target
  }
  // First check task mention autocomplete (has highest priority)
  if (commentTaskMentions.handleKeyDown(event)) {
    return
  }
  // Then check user mention autocomplete
  if (commentMentions.handleKeyDown(event)) {
    return
  }
  // Finally check markdown hotkeys
  commentHotkeys.handleKeyDown(event)
}

// Combined key handler for description (handles hotkeys, user mentions, and task mentions)
const handleDescriptionKeyDown = event => {
  // First check task mention autocomplete (has highest priority)
  if (descriptionTaskMentions.handleKeyDown(event)) {
    return
  }
  // Then check user mention autocomplete
  if (descriptionMentions.handleKeyDown(event)) {
    return
  }
  // Finally check markdown hotkeys
  descriptionHotkeys.handleKeyDown(event)
}

// Handle mention selection
const handleCommentMentionSelect = member => {
  commentMentions.insertMention(member)
}

const handleDescriptionMentionSelect = member => {
  descriptionMentions.insertMention(member)
}

// Handle mention hover
const handleCommentMentionHover = index => {
  commentMentions.selectedIndex = index
}

const handleDescriptionMentionHover = index => {
  descriptionMentions.selectedIndex = index
}

// Handle input for both user and task mentions
const handleCommentInput = event => {
  commentMentions.handleInput(event)
  commentTaskMentions.handleInput(event)
}

const handleDescriptionInput = event => {
  descriptionMentions.handleInput(event)
  descriptionTaskMentions.handleInput(event)
}

// Handle task mention selection
const handleCommentTaskMentionSelect = task => {
  commentTaskMentions.insertTaskMention(task)
}

const handleDescriptionTaskMentionSelect = task => {
  descriptionTaskMentions.insertTaskMention(task)
}

// Handle task mention hover
const handleCommentTaskMentionHover = index => {
  commentTaskMentions.selectedIndex = index
}

const handleDescriptionTaskMentionHover = index => {
  descriptionTaskMentions.selectedIndex = index
}

// Handle context menu format actions
const handleCommentFormat = formatType => {
  switch (formatType) {
    case 'bold':
      commentContextMenu.formatBold()
      break
    case 'italic':
      commentContextMenu.formatItalic()
      break
    case 'code':
      commentContextMenu.formatCode()
      break
    case 'strikethrough':
      commentContextMenu.formatStrikethrough()
      break
    case 'underline':
      commentContextMenu.formatUnderline()
      break
    case 'monospace':
      commentContextMenu.formatMonospace()
      break
    case 'spoiler':
      commentContextMenu.formatSpoiler()
      break
    case 'link':
      commentContextMenu.formatLink()
      break
    case 'quotation':
      commentContextMenu.formatQuotation()
      break
  }
}

const handleCommentAction = actionType => {
  switch (actionType) {
    case 'cut':
      commentContextMenu.cut()
      break
    case 'copy':
      commentContextMenu.copy()
      break
    case 'delete':
      commentContextMenu.deleteText()
      break
  }
}

const handleDescriptionFormat = formatType => {
  switch (formatType) {
    case 'bold':
      descriptionContextMenu.formatBold()
      break
    case 'italic':
      descriptionContextMenu.formatItalic()
      break
    case 'code':
      descriptionContextMenu.formatCode()
      break
    case 'strikethrough':
      descriptionContextMenu.formatStrikethrough()
      break
    case 'underline':
      descriptionContextMenu.formatUnderline()
      break
    case 'monospace':
      descriptionContextMenu.formatMonospace()
      break
    case 'spoiler':
      descriptionContextMenu.formatSpoiler()
      break
    case 'link':
      descriptionContextMenu.formatLink()
      break
    case 'quotation':
      descriptionContextMenu.formatQuotation()
      break
  }
}

const handleDescriptionAction = actionType => {
  switch (actionType) {
    case 'cut':
      descriptionContextMenu.cut()
      break
    case 'copy':
      descriptionContextMenu.copy()
      break
    case 'delete':
      descriptionContextMenu.deleteText()
      break
  }
}

// Build task URL helper
const buildTaskUrl = (taskIid, taskProjectId) => {
  const chatId = props.chatId || new URLSearchParams(window.location.search).get('chat_id')
  const fromId = props.fromId || new URLSearchParams(window.location.search).get('from_id')

  let url = `/projects/${taskProjectId || props.projectId}/tasks/${taskIid}`
  const params = []
  if (chatId) params.push(`chat_id=${encodeURIComponent(chatId)}`)
  if (fromId) params.push(`from_id=${encodeURIComponent(fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')

  return url
}

// Process task description with task mentions
const processedTaskDescription = computed(() => {
  if (!task.value?.description) return '—'

  return telegramMarkdownToHtml(task.value.description, {
    processTaskMentions: true,
    taskMap: taskMentionsMap.value, // Use fetched task information
    buildTaskUrl: (taskId, projectId) => buildTaskUrl(taskId, projectId || props.projectId),
  })
})

// Handle clicks on task mention links
const handleTaskMentionClick = event => {
  if (event.target.classList.contains('task-mention-link')) {
    event.preventDefault()
    const taskId = event.target.dataset.taskId
    const projectId = event.target.dataset.projectId || props.projectId
    const url = buildTaskUrl(taskId, projectId)
    window.location.href = url
  }
}

function person(u) {
  if (!u) return '—'
  return u.username ? '@' + u.username : u.name || 'User ' + u.id
}

// Format user for display in selectboxes: shows username and name
function formatUserForSelect(user) {
  if (!user) return '—'
  const parts = []
  if (user.username) parts.push('@' + user.username)
  if (user.name) parts.push(user.name)
  if (parts.length > 0) return parts.join(' ')
  return 'User ' + user.id
}

// Convert ISO datetime to separate date, hour, minute
function toLocalDateHourMinute(isoString) {
  if (!isoString) return { date: '', hour: '00', minute: '00' }
  const d = new Date(isoString)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  return {
    date: `${year}-${month}-${day}`,
    hour: hours,
    minute: minutes,
  }
}

// Combine date, hour, minute into ISO datetime
function fromLocalDateHourMinute(dateStr, hour, minute) {
  if (!dateStr) return null
  const h = hour || '00'
  const m = minute || '00'
  const datetime = `${dateStr}T${h}:${m}`
  try {
    return new Date(datetime).toISOString()
  } catch {
    return null
  }
}

const availableTransitions = ref([])
const loadingTransitions = ref(false)
const events = computed(() => {
  if (activeTab.value === 'history' && eventsResult.value?.project?.task?.events) {
    return eventsResult.value.project.task.events
  }
  return []
})

const dashboardUrl = computed(() => {
  let url = '/dashboard'
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

// Share links: Telegram deep link and browser URL
function base64UrlEncode(str) {
  const bytes = new TextEncoder().encode(str)
  const binary = String.fromCharCode(...bytes)
  const base64 = typeof btoa !== 'undefined' ? btoa(binary) : ''
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

const shareLinks = computed(() => {
  const pid = project.value?.id
  const taskIid = task.value?.iid
  const taskId = task.value?.id
  const browserLink = pid && taskIid ? `${window.location.origin}${buildTaskUrl(taskIid, pid)}` : ''
  if (!pid || !taskId) {
    return { telegramLink: null, browserLink }
  }
  const chatId = project.value?.telegramChatId ?? props.chatId ?? null
  const fromId = props.fromId ?? null
  const payload = {
    page: 'task_show',
    project_id: pid,
    task_id: taskId,
    ...(chatId != null && chatId !== '' && { chat_id: String(chatId) }),
    ...(fromId != null && fromId !== '' && { from_id: String(fromId) }),
  }
  const encoded = base64UrlEncode(JSON.stringify(payload))
  const telegramLink =
    botUsername.value && encoded ? `https://t.me/${botUsername.value}?startapp=${encoded}` : null
  return { telegramLink, browserLink }
})

function buildShareMessage() {
  const { telegramLink, browserLink } = shareLinks.value
  const titleLine = sprintf(s__('TaskDetail|Task from Tracktor app: %{title}'), {
    title: task.value?.title || `#${task.value?.iid || ''}`,
  })
  const openInTelegram = s__('TaskDetail|Open in Telegram')
  const openInBrowser = s__('TaskDetail|Open in browser')
  const lines = [titleLine]
  if (telegramLink) {
    lines.push(`${openInTelegram}: ${telegramLink}`)
  }
  lines.push(`${openInBrowser}: ${browserLink}`)
  return { title: titleLine, text: lines.slice(1).join('\n'), fullMessage: lines.join('\n') }
}

function showShareFeedback(messageKey) {
  shareFeedbackText.value = s__(messageKey)
  shareCopyFeedback.value = true
  if (shareCopyFeedbackTimeout) clearTimeout(shareCopyFeedbackTimeout)
  shareCopyFeedbackTimeout = setTimeout(() => {
    shareCopyFeedback.value = false
  }, 2000)
}

async function copyShareMessage() {
  const { title, fullMessage } = buildShareMessage()

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({
        title,
        text: fullMessage,
      })
      showShareFeedback('TaskDetail|Shared!')
      return 'shared'
    } catch (_e) {
      return copyToClipboardAndFeedback(fullMessage)
    }
  } else {
    return copyToClipboardAndFeedback(fullMessage)
  }
}

async function copyToClipboardAndFeedback(message) {
  try {
    await navigator.clipboard.writeText(message)
    showShareFeedback('TaskDetail|Copied!')
    return 'copied'
  } catch (_e) {
    return null
  }
}

const tasksIndexUrl = computed(() => {
  if (!props.projectId) return '#'

  // Try to get stored URL with filters from sessionStorage
  try {
    const projectSegmentFromPath = window.location.pathname.match(/^\/projects\/([^/]+)/)?.[1]
    const candidateKeys = [projectSegmentFromPath, props.projectId].filter(Boolean)

    for (const key of candidateKeys) {
      const storedUrl = sessionStorage.getItem(`taskIndexUrl_${key}`)
      if (!storedUrl) continue

      // Parse the stored URL and preserve chat_id/from_id from props if needed
      const storedUrlObj = new URL(storedUrl, window.location.origin)
      const currentParams = new URLSearchParams(storedUrlObj.search)

      // Update chat_id and from_id if provided in props
      if (props.chatId) {
        currentParams.set('chat_id', props.chatId)
      }
      if (props.fromId) {
        currentParams.set('from_id', props.fromId)
      }

      return (
        storedUrlObj.pathname + (currentParams.toString() ? '?' + currentParams.toString() : '')
      )
    }
  } catch (e) {
    console.warn('Failed to get stored task index URL:', e)
  }

  // Fallback to default URL
  const projectSegmentFromPath = window.location.pathname.match(/^\/projects\/([^/]+)/)?.[1]
  const projectSegment = projectSegmentFromPath || props.projectId
  let url = `/projects/${encodeURIComponent(projectSegment)}/dashboard`
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
})

const projectTitle = computed(() => {
  if (!project.value) return null
  return project.value.title
})

const breadcrumbsItems = computed(() => {
  const items = [{ label: s__('Dashboard|Main'), url: dashboardUrl.value }]
  if (projectTitle.value) {
    items.push({ label: projectTitle.value, url: tasksIndexUrl.value })
  }
  return items
})

const isPriorityRequired = computed(() => {
  return !isPersonalProject.value && requiredFields.value.includes('priority')
})

const priorityOptions = computed(() => {
  const options = []
  if (!isPriorityRequired.value) {
    options.push({ value: null, label: s__('TaskDetail|Not set') })
  }
  options.push(
    { value: 'HIGHEST', label: s__('TaskDetail|Highest') },
    { value: 'HIGH', label: s__('TaskDetail|High') },
    { value: 'MEDIUM', label: s__('TaskDetail|Medium') },
    { value: 'LOW', label: s__('TaskDetail|Low') },
    { value: 'LOWEST', label: s__('TaskDetail|Lowest') }
  )
  return options
})

// Simple priorities array for child components (TaskHierarchy)
const priorities = computed(() => [
  { value: 'HIGHEST' },
  { value: 'HIGH' },
  { value: 'MEDIUM' },
  { value: 'LOW' },
  { value: 'LOWEST' },
])

const isAssigneeRequired = computed(() => {
  return !isPersonalProject.value && requiredFields.value.includes('assignee_id')
})

const isDueAtRequired = computed(() => {
  return !isPersonalProject.value && requiredFields.value.includes('due_at')
})

// Board URL for back-link
const boardUrl = computed(() => {
  if (!board.value || !boardId.value) return null

  // First, try to get stored board URL with session parameters
  try {
    const storedBoardUrl = sessionStorage.getItem(`boardUrl_${boardId.value}`)
    if (storedBoardUrl) {
      const storedUrlObj = new URL(storedBoardUrl, window.location.origin)
      const currentParams = new URLSearchParams(storedUrlObj.search)

      // Update chat_id and from_id if provided in props
      if (props.chatId) {
        currentParams.set('chat_id', props.chatId)
      }
      if (props.fromId) {
        currentParams.set('from_id', props.fromId)
      }

      return (
        storedUrlObj.pathname + (currentParams.toString() ? '?' + currentParams.toString() : '')
      )
    }
  } catch (e) {
    console.warn('Failed to get stored board URL:', e)
  }

  // Fallback: build URL without session parameters
  const chat = props.chatId ? `?chat_id=${encodeURIComponent(props.chatId)}` : ''
  const from = props.fromId ? `${chat ? '&' : '?'}from_id=${encodeURIComponent(props.fromId)}` : ''

  if (board.value.isProjectBoard && props.projectId) {
    return `/projects/${props.projectId}/boards/${boardId.value}${chat}${from}`
  } else if (board.value.isPersonalBoard) {
    return `/boards/${boardId.value}${chat}${from}`
  }

  return null
})

// Tasks list URL for back-link (project tasks or dashboard)
const tasksListUrl = computed(() => {
  // Don't show if board URL is available (board takes priority)
  if (boardUrl.value) return null

  // Most reliable: Check document.referrer to determine where user actually came from
  if (typeof document !== 'undefined' && document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer)
      const referrerPath = referrerUrl.pathname

      // Check if referrer is a project dashboard page
      const projectTasksMatch = referrerPath.match(/^\/projects\/([^/]+)\/dashboard(\?|$)/)
      if (projectTasksMatch) {
        // User came from project dashboard - use stored URL or build from referrer
        if (tasksIndexUrl.value && tasksIndexUrl.value !== '#') {
          return tasksIndexUrl.value
        }
        // Fallback: build URL from referrer
        const projectId = projectTasksMatch[1]
        let url = `/projects/${projectId}/dashboard`
        const params = []
        // Preserve query params from referrer
        referrerUrl.searchParams.forEach((value, key) => {
          params.push(`${key}=${encodeURIComponent(value)}`)
        })
        // Add chat_id/from_id from props if provided
        if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
        if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
        if (params.length > 0) url += '?' + params.join('&')
        return url
      }

      // Check if referrer is calendar
      if (referrerPath === '/calendar' || referrerPath.startsWith('/calendar')) {
        // User came from calendar - use stored URL or build from referrer
        try {
          const storedCalendarUrl = sessionStorage.getItem('calendarUrl')
          if (storedCalendarUrl) {
            const storedUrlObj = new URL(storedCalendarUrl, window.location.origin)
            const currentParams = new URLSearchParams(storedUrlObj.search)

            // Update chat_id and from_id if provided in props
            if (props.chatId) {
              currentParams.set('chat_id', props.chatId)
            }
            if (props.fromId) {
              currentParams.set('from_id', props.fromId)
            }

            return (
              storedUrlObj.pathname +
              (currentParams.toString() ? '?' + currentParams.toString() : '')
            )
          }
        } catch (e) {
          console.warn('Failed to get stored calendar URL:', e)
        }
        // Fallback: build URL from referrer
        let url = '/calendar'
        const params = []
        // Preserve query params from referrer (view, date, etc.)
        referrerUrl.searchParams.forEach((value, key) => {
          params.push(`${key}=${encodeURIComponent(value)}`)
        })
        // Add chat_id/from_id from props if provided
        if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
        if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
        if (params.length > 0) url += '?' + params.join('&')
        return url
      }

      // Check if referrer is dashboard
      if (referrerPath === '/dashboard' || referrerPath.startsWith('/dashboard')) {
        // User came from dashboard - use stored URL or build from referrer
        try {
          const storedDashboardUrl = sessionStorage.getItem('dashboardUrl')
          if (storedDashboardUrl) {
            const storedUrlObj = new URL(storedDashboardUrl, window.location.origin)
            const currentParams = new URLSearchParams(storedUrlObj.search)

            // Update chat_id and from_id if provided in props
            if (props.chatId) {
              currentParams.set('chat_id', props.chatId)
            }
            if (props.fromId) {
              currentParams.set('from_id', props.fromId)
            }

            return (
              storedUrlObj.pathname +
              (currentParams.toString() ? '?' + currentParams.toString() : '')
            )
          }
        } catch (e) {
          console.warn('Failed to get stored dashboard URL:', e)
        }
        // Fallback: build URL from referrer
        let url = '/dashboard'
        const params = []
        // Preserve query params from referrer
        referrerUrl.searchParams.forEach((value, key) => {
          params.push(`${key}=${encodeURIComponent(value)}`)
        })
        // Add chat_id/from_id from props if provided
        if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
        if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
        if (params.length > 0) url += '?' + params.join('&')
        return url
      }
    } catch (e) {
      // Invalid referrer URL, fall through to sessionStorage
    }
  }

  // Fallback: Use sessionStorage if referrer is not available or doesn't match
  // Check project tasks URL first (more specific)
  if (tasksIndexUrl.value && tasksIndexUrl.value !== '#') {
    return tasksIndexUrl.value
  }

  // Then check dashboard URL
  try {
    const storedDashboardUrl = sessionStorage.getItem('dashboardUrl')
    if (storedDashboardUrl) {
      const storedUrlObj = new URL(storedDashboardUrl, window.location.origin)
      const currentParams = new URLSearchParams(storedUrlObj.search)

      // Update chat_id and from_id if provided in props
      if (props.chatId) {
        currentParams.set('chat_id', props.chatId)
      }
      if (props.fromId) {
        currentParams.set('from_id', props.fromId)
      }

      return (
        storedUrlObj.pathname + (currentParams.toString() ? '?' + currentParams.toString() : '')
      )
    }
  } catch (e) {
    console.warn('Failed to get stored dashboard URL:', e)
  }

  return null
})

// Locale key to force re-render when translations are loaded
const localeKey = ref(0)

// Label for tasks list back button
const tasksListLabel = computed(() => {
  // Depend on localeKey to force re-render when translations load
  localeKey.value // eslint-disable-line no-unused-expressions
  if (tasksListUrl.value) {
    // Check if it's a project dashboard URL, calendar URL, or user dashboard URL
    if (tasksListUrl.value.startsWith('/projects/') && tasksListUrl.value.includes('/dashboard')) {
      return s__("TaskDetail|To Project's Tasks")
    } else if (tasksListUrl.value === '/calendar' || tasksListUrl.value.startsWith('/calendar?')) {
      return s__('TaskDetail|Back to Calendar')
    } else if (
      tasksListUrl.value === '/dashboard' ||
      tasksListUrl.value.startsWith('/dashboard?')
    ) {
      return s__('TaskDetail|Back to Dashboard')
    }
  }
  return s__('TaskDetail|Back to Tasks')
})

// Computed: check if task is finished (workflow status is final)
const isTaskFinished = computed(() => {
  return task.value?.workflowStatus?.isFinal === true
})

// Computed: sorted grades from task type's grading scale
const sortedGrades = computed(() => {
  const scale = task.value?.taskCategory?.gradingScale
  if (!scale || !scale.grades) return []
  return [...scale.grades].sort((a, b) => a.position - b.position)
})

// Comments are already top-level with replies nested from GraphQL
// Comments are stored in oldest-first order, so we can display them directly
const topLevelComments = computed(() => {
  return comments.value.filter(c => !c.deleted)
})

// Check if there are more replies to load for a comment
function hasMoreReplies(comment) {
  return comment.hasMoreReplies || false
}

// Track how many replies we've loaded for each comment
const loadedRepliesCount = ref({})

// Get the offset for loading more replies
function getRepliesOffset(comment) {
  // We start with 5 replies, so offset is 5 for the next batch
  return loadedRepliesCount.value[comment.id] || 5
}

const loadingMoreReplies = ref({}) // Track loading state per comment

// Load more replies for a comment using TaskCommentsResolver with replied_comment_id
async function loadMoreReplies(comment) {
  if (!hasMoreReplies(comment) || loadingMoreReplies.value[comment.id]) return

  loadingMoreReplies.value[comment.id] = true

  try {
    // Use TaskCommentsResolver with replied_comment_id to fetch more replies
    // We need to get replies older than the oldest one we currently have
    // Since we have 5 newest replies, we need to skip those and get the next batch
    // For offset-based pagination, we'll use the offset directly
    const offset = getRepliesOffset(comment)

    // For GraphQL-Ruby's ActiveRecordRelationConnection, the cursor is base64 encoded offset
    // We need to encode the offset as base64 without padding
    // Format: base64(offset.to_s) without padding (remove trailing =)
    const cursor = btoa(offset.toString()).replace(/=+$/, '')

    const repliesQuery = gql`
      query GetCommentReplies(
        $projectId: ID!
        $taskId: ID!
        $repliedCommentId: ID!
        $first: Int
        $after: String
      ) {
        project(id: $projectId) {
          task(id: $taskId) {
            comments(repliedCommentId: $repliedCommentId, first: $first, after: $after) {
              edges {
                node {
                  id
                  body
                  createdAt
                  updatedAt
                  deleted
                  edited
                  repliedCommentId
                  authorName
                  canUpdate
                  canDelete
                  author {
                    id
                    username
                    name
                  }
                  attachments {
                    id
                    filename
                    byteSize
                    contentType
                    createdAt
                    downloadUrl
                    humanSize
                    canCreatePublicLink
                    canManagePublicLink
                    publicLink {
                      id
                      expiresAt
                      passwordProtected
                      publicUrl
                      revoked
                      expired
                    }
                  }
                }
                cursor
              }
              pageInfo {
                hasNextPage
                hasPreviousPage
                startCursor
                endCursor
              }
            }
          }
        }
      }
    `

    const { data } = await apolloClient.query({
      query: repliesQuery,
      variables: {
        projectId: props.projectId,
        taskId: taskGlobalId.value || props.taskId,
        repliedCommentId: comment.id,
        first: 25,
        after: cursor,
      },
      fetchPolicy: 'network-only',
    })

    if (data?.project?.task?.comments) {
      const newReplyEdges = data.project.task.comments.edges || []
      // New replies come in newest-first order (from backend)
      // We want to prepend them (older replies go above current ones)
      // Since replies are in newest-first order, we need to reverse to get oldest-first
      // The existing replies are already in oldest-first order (oldest at top, newest at bottom)
      // So we prepend the new older replies (which are now in oldest-first order) to the beginning
      const newReplies = [...newReplyEdges].reverse().map(edge => edge.node)

      // Find the comment and update its replies
      // Prepend older replies (they should appear above current replies)
      const commentIndex = comments.value.findIndex(c => c.id === comment.id)

      if (commentIndex !== -1) {
        const updatedComments = [...comments.value]
        // Update hasMoreReplies based on pageInfo
        const hasMore = data.project.task.comments.pageInfo?.hasNextPage || false
        const newRepliesCount = newReplies.length
        const oldRepliesCount = updatedComments[commentIndex].replies?.length || 0
        // Prepend older replies (they go above/before the current replies)
        // Both arrays are in oldest-first order, so prepending is correct
        updatedComments[commentIndex] = {
          ...updatedComments[commentIndex],
          replies: [...newReplies, ...updatedComments[commentIndex].replies],
          hasMoreReplies: hasMore,
        }
        // Update the count of loaded replies
        loadedRepliesCount.value[comment.id] =
          (loadedRepliesCount.value[comment.id] || 5) + newRepliesCount

        comments.value = updatedComments
      }
    }
  } catch (e) {
    console.error('Failed to load more replies:', e)
    error.value = e.message || 'Failed to load more replies'
  } finally {
    loadingMoreReplies.value[comment.id] = false
  }
}

const TASK_DETAIL_QUERY = gql`
  query GetTaskDetail(
    $projectId: ID!
    $taskId: ID!
    $first: Int
    $after: String
    $last: Int
    $before: String
  ) {
    currentUser {
      id
      name
      username
      preferredTimezone
      preferredLanguage
      personalLabelsEnabled
    }
    project(id: $projectId) {
      id
      title
      isPersonal
      telegramChatId
      archived
      confidentialTasksEnabled
      gradesEnabled
      verticalRelationsEnabled
      timeTrackingEnabled
      checklistsEnabled
      canManageLabels
      requiredFields
      labels {
        id
        title
        color
      }
      horizontalRelationTypes {
        id
        directTitle
        reverseTitle
        color
        icon
      }
      taskCategories {
        id
        name
        workflow {
          id
          name
          statuses {
            id
            name
            position
            isInitial
          }
        }
        gradingScale {
          id
          name
        }
      }
      members {
        id
        username
        name
      }
      memberships {
        id
        role
        projectRole
        user {
          id
          username
          name
        }
      }
      task(id: $taskId) {
        id
        iid
        title
        description
        archived
        dueAt
        finishedAt
        priority
        confidential
        workflowStatus {
          id
          name
          color
          isFinal
        }
        canUpdateTask
        canUpdateAssignee
        canCreateComment
        canGradeTask
        canArchiveTask
        canCloneTask
        canSubscribeOthers
        canManageRelations
        subscribed
        subscribersCount
        subscribers {
          id
          username
          name
        }
        eventsCount
        attachmentsCount
        outgoingHorizontalRelations {
          id
          horizontalRelationType {
            id
            directTitle
            reverseTitle
            color
            icon
          }
          targetTask {
            id
            iid
            title
          }
        }
        incomingHorizontalRelations {
          id
          horizontalRelationType {
            id
            directTitle
            reverseTitle
            color
            icon
          }
          sourceTask {
            id
            iid
            title
          }
        }
        totalTimeSpent
        myTimeSpent
        totalTimeSpentInDescendants
        grade {
          id
          grade {
            id
            value
            description
            position
          }
          givenBy {
            id
            username
            name
          }
          givenTo {
            id
            username
            name
          }
          confidential
          canManageGrade
          createdAt
          updatedAt
        }
        grades {
          id
          grade {
            id
            value
            description
            position
          }
          givenBy {
            id
            username
            name
          }
          givenTo {
            id
            username
            name
          }
          confidential
          canManageGrade
          createdAt
          updatedAt
        }
        taskCategory {
          id
          name
          workflow {
            id
            name
          }
          gradingScale {
            id
            name
            grades {
              id
              value
              description
              position
            }
          }
        }
        whitelistedViewers {
          id
          username
          name
        }
        labels {
          id
          title
          color
        }
        personalLabels {
          id
          title
          color
        }
        checklist {
          id
          taskId
          title
          completed
          completionPercentage
          items {
            id
            checklistId
            title
            completed
            position
          }
        }
        checklistCompleted
        author {
          id
          username
          name
        }
        assignee {
          id
          username
          name
        }
        comments(first: $first, after: $after, last: $last, before: $before) {
          edges {
            node {
              id
              body
              createdAt
              updatedAt
              deleted
              edited
              repliedCommentId
              authorName
              canUpdate
              canDelete
              author {
                id
                username
                name
              }
              replies {
                id
                body
                createdAt
                updatedAt
                deleted
                edited
                repliedCommentId
                authorName
                canUpdate
                canDelete
                author {
                  id
                  username
                  name
                }
                attachments {
                  id
                  filename
                  byteSize
                  contentType
                  createdAt
                  downloadUrl
                  humanSize
                  canCreatePublicLink
                  canManagePublicLink
                  publicLink {
                    id
                    expiresAt
                    passwordProtected
                    publicUrl
                    revoked
                    expired
                  }
                }
              }
              attachments {
                id
                filename
                byteSize
                contentType
                createdAt
                downloadUrl
                humanSize
                canCreatePublicLink
                canManagePublicLink
                publicLink {
                  id
                  expiresAt
                  passwordProtected
                  publicUrl
                  revoked
                  expired
                }
              }
              hasMoreReplies
            }
            cursor
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`

const EXECUTE_TRANSITION = gql`
  mutation ExecuteTransition($taskId: ID!, $transitionId: ID!) {
    executeTransition(taskId: $taskId, transitionId: $transitionId) {
      task {
        id
        workflowStatus {
          id
          name
          color
          isFinal
        }
        availableTransitions {
          id
          name
          targetStatus {
            id
            name
            color
            requiresCompletedChecklists
          }
        }
        events {
          id
          eventType
          eventName
          description
          actorName
          createdAt
          metadata
          changedData
          actor {
            id
            username
            name
          }
        }
      }
      errors
    }
  }
`

const BOARD_QUERY = gql`
  query Board($id: ID!) {
    board(id: $id) {
      id
      title
      isProjectBoard
      isPersonalBoard
      projectId
    }
  }
`

const ATTACH_LABEL = gql`
  mutation AttachLabel($taskId: ID!, $labelId: ID!) {
    attachLabel(taskId: $taskId, labelId: $labelId) {
      task {
        id
        labels {
          id
          title
          color
        }
      }
      errors
    }
  }
`

const DETACH_LABEL = gql`
  mutation DetachLabel($taskId: ID!, $labelId: ID!) {
    detachLabel(taskId: $taskId, labelId: $labelId) {
      task {
        id
        labels {
          id
          title
          color
        }
      }
      errors
    }
  }
`

const PERSONAL_LABELS_QUERY = gql`
  query PersonalLabels {
    personalLabels {
      id
      title
      color
    }
  }
`

const UPDATE_TASK_TITLE = gql`
  mutation UpdateTaskTitle($taskId: ID!, $title: String!) {
    updateTaskTitle(taskId: $taskId, title: $title) {
      task {
        id
        title
        events {
          id
          eventType
          eventName
          description
          actorName
          createdAt
          metadata
          changedData
          actor {
            id
            username
            name
          }
        }
      }
      errors
    }
  }
`

const UPDATE_TASK_DESCRIPTION = gql`
  mutation UpdateTaskDescription($taskId: ID!, $description: String, $removeAttachmentIds: [ID!]) {
    updateTaskDescription(
      taskId: $taskId
      description: $description
      removeAttachmentIds: $removeAttachmentIds
    ) {
      task {
        id
        description
        attachmentsCount
        events {
          id
          eventType
          eventName
          description
          actorName
          createdAt
          metadata
          changedData
          actor {
            id
            username
            name
          }
        }
      }
      errors
    }
  }
`

const UPDATE_TASK_PRIORITY = gql`
  mutation UpdateTaskPriority($taskId: ID!, $priority: PriorityEnum) {
    updateTaskPriority(taskId: $taskId, priority: $priority) {
      task {
        id
        priority
        events {
          id
          eventType
          eventName
          description
          actorName
          createdAt
          metadata
          changedData
          actor {
            id
            username
            name
          }
        }
      }
      errors
    }
  }
`

const UPDATE_TASK_DUE_DATE = gql`
  mutation UpdateTaskDueDate($taskId: ID!, $dueAt: ISO8601DateTime) {
    updateTaskDueDate(taskId: $taskId, dueAt: $dueAt) {
      task {
        id
        dueAt
        events {
          id
          eventType
          eventName
          description
          actorName
          createdAt
          metadata
          changedData
          actor {
            id
            username
            name
          }
        }
      }
      errors
    }
  }
`

const UPDATE_TASK_ASSIGNEE = gql`
  mutation UpdateTaskAssignee($taskId: ID!, $assigneeId: ID) {
    updateTaskAssignee(taskId: $taskId, assigneeId: $assigneeId) {
      task {
        id
        assignee {
          id
          username
          name
        }
        canUpdateAssignee
        events {
          id
          eventType
          eventName
          description
          actorName
          createdAt
          metadata
          changedData
          actor {
            id
            username
            name
          }
        }
      }
      errors
    }
  }
`

const CREATE_TASK_COMMENT = gql`
  mutation CreateTaskComment(
    $taskId: ID!
    $body: String!
    $repliedCommentId: ID
    $attachmentDraftId: ID
  ) {
    createTaskComment(
      taskId: $taskId
      body: $body
      repliedCommentId: $repliedCommentId
      attachmentDraftId: $attachmentDraftId
    ) {
      comment {
        id
        body
        createdAt
        updatedAt
        deleted
        edited
        repliedCommentId
        authorName
        canUpdate
        canDelete
        author {
          id
          username
          name
        }
        attachments {
          id
          filename
          byteSize
          contentType
          createdAt
          downloadUrl
          humanSize
          canCreatePublicLink
          canManagePublicLink
          publicLink {
            id
            expiresAt
            passwordProtected
            publicUrl
            revoked
            expired
          }
        }
      }
      errors
    }
  }
`

const UPDATE_TASK_COMMENT = gql`
  mutation UpdateTaskComment($id: ID!, $body: String!, $removeAttachmentIds: [ID!]) {
    updateTaskComment(id: $id, body: $body, removeAttachmentIds: $removeAttachmentIds) {
      comment {
        id
        body
        createdAt
        updatedAt
        deleted
        edited
        repliedCommentId
        authorName
        canUpdate
        canDelete
        author {
          id
          username
          name
        }
        task {
          id
          attachmentsCount
        }
        attachments {
          id
          filename
          byteSize
          contentType
          createdAt
          downloadUrl
          humanSize
          canCreatePublicLink
          canManagePublicLink
          publicLink {
            id
            expiresAt
            passwordProtected
            publicUrl
            revoked
            expired
          }
        }
      }
      errors
    }
  }
`

const DELETE_TASK_COMMENT = gql`
  mutation DeleteTaskComment($id: ID!) {
    deleteTaskComment(id: $id) {
      comment {
        id
        deleted
      }
      errors
    }
  }
`

const SUBSCRIBE_TO_TASK = gql`
  mutation SubscribeToTask($taskId: ID!) {
    subscribeToTask(taskId: $taskId) {
      subscription {
        id
      }
      errors
    }
  }
`

const UNSUBSCRIBE_FROM_TASK = gql`
  mutation UnsubscribeFromTask($taskId: ID!) {
    unsubscribeFromTask(taskId: $taskId) {
      success
      errors
    }
  }
`

const UPDATE_TASK_CONFIDENTIALITY = gql`
  mutation UpdateTaskConfidentiality($taskId: ID!, $confidential: Boolean!) {
    updateTaskConfidentiality(taskId: $taskId, confidential: $confidential) {
      task {
        id
        confidential
        whitelistedViewers {
          id
          username
          name
        }
      }
      errors
    }
  }
`

const UPDATE_TASK_ARCHIVED = gql`
  mutation UpdateTaskArchived($taskId: ID!, $archived: Boolean!) {
    updateTaskArchived(taskId: $taskId, archived: $archived) {
      task {
        id
        archived
        events {
          id
          eventType
          eventName
          description
          actorName
          createdAt
          metadata
          changedData
          actor {
            id
            username
            name
          }
        }
      }
      errors
    }
  }
`

const ADD_CONFIDENTIAL_TASK_VIEWER = gql`
  mutation AddConfidentialTaskViewer($taskId: ID!, $viewerId: ID!) {
    addConfidentialTaskViewer(taskId: $taskId, viewerId: $viewerId) {
      task {
        id
        whitelistedViewers {
          id
          username
          name
        }
      }
      errors
    }
  }
`

const REMOVE_CONFIDENTIAL_TASK_VIEWER = gql`
  mutation RemoveConfidentialTaskViewer($taskId: ID!, $viewerId: ID!) {
    removeConfidentialTaskViewer(taskId: $taskId, viewerId: $viewerId) {
      task {
        id
        whitelistedViewers {
          id
          username
          name
        }
      }
      errors
    }
  }
`

const CREATE_TASK_GRADE = gql`
  mutation CreateTaskGrade($taskId: ID!, $gradeId: ID!, $confidential: Boolean) {
    createTaskGrade(taskId: $taskId, gradeId: $gradeId, confidential: $confidential) {
      taskGrade {
        id
        grade {
          id
          value
          description
          position
        }
        givenBy {
          id
          username
          name
        }
        givenTo {
          id
          username
          name
        }
        confidential
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const UPDATE_TASK_GRADE = gql`
  mutation UpdateTaskGrade($taskId: ID!, $gradeId: ID, $confidential: Boolean) {
    updateTaskGrade(taskId: $taskId, gradeId: $gradeId, confidential: $confidential) {
      taskGrade {
        id
        grade {
          id
          value
          description
          position
        }
        givenBy {
          id
          username
          name
        }
        givenTo {
          id
          username
          name
        }
        confidential
        createdAt
        updatedAt
      }
      errors
    }
  }
`

const DESTROY_TASK_GRADE = gql`
  mutation DestroyTaskGrade($taskId: ID!) {
    destroyTaskGrade(taskId: $taskId) {
      success
      errors
    }
  }
`

const GET_TASK_SUBSCRIBERS = gql`
  query GetTaskSubscribers($projectId: ID!, $taskId: ID!) {
    project(id: $projectId) {
      task(id: $taskId) {
        id
        subscribersCount
        canSubscribeOthers
        subscribers {
          id
          username
          name
        }
      }
    }
  }
`

const SUBSCRIBE_USER_TO_TASK = gql`
  mutation SubscribeUserToTask($taskId: ID!, $userId: ID!) {
    subscribeUserToTask(taskId: $taskId, userId: $userId) {
      subscription {
        id
      }
      errors
    }
  }
`

const UNSUBSCRIBE_USER_FROM_TASK = gql`
  mutation UnsubscribeUserFromTask($taskId: ID!, $userId: ID!) {
    unsubscribeUserFromTask(taskId: $taskId, userId: $userId) {
      success
      errors
    }
  }
`

const BOT_USERNAME_Q = gql`
  query GetBotUsername {
    botUsername
  }
`

const CHANGE_TASK_CATEGORY = gql`
  mutation ChangeTaskCategory($taskId: ID!, $taskCategoryId: ID!, $workflowStatusId: ID) {
    changeTaskCategory(
      taskId: $taskId
      taskCategoryId: $taskCategoryId
      workflowStatusId: $workflowStatusId
    ) {
      task {
        id
        taskCategory {
          id
          name
          workflow {
            id
            name
          }
          gradingScale {
            id
            name
            grades {
              id
              value
              description
              position
            }
          }
        }
        workflowStatus {
          id
          name
          color
          isFinal
          requiresCompletedChecklists
        }
        grades {
          id
          grade {
            id
            value
            description
            position
          }
          givenBy {
            id
            username
            name
          }
          givenTo {
            id
            username
            name
          }
          confidential
          canManageGrade
          createdAt
          updatedAt
        }
        events {
          id
          eventType
          eventName
          description
          actorName
          createdAt
          metadata
          changedData
          actor {
            id
            username
            name
          }
        }
      }
      errors
      requiresWorkflowStatus
      willLoseGrades
    }
  }
`

const commentsHasPreviousPage = ref(false)
const commentsStartCursor = ref(null)
const loadingMoreComments = ref(false)
const skipNextWatch = ref(false) // Flag to skip watch handler after loading more comments

const EVENTS_Q = gql`
  query GetTaskEvents($projectId: ID!, $taskId: ID!) {
    project(id: $projectId) {
      task(id: $taskId) {
        id
        events {
          id
          eventType
          eventName
          description
          actorName
          createdAt
          metadata
          changedData
          actor {
            id
            username
            name
          }
        }
      }
    }
  }
`

// Create stable variables object to prevent unnecessary refetches
// Use a ref instead of computed to have more control
const mainQueryVariables = ref({
  projectId: props.projectId,
  taskId: taskGlobalId.value || props.taskId,
  first: 25,
  after: null,
  last: null,
  before: null,
})

// Watch for changes to props to update variables (but only if they actually change)
watch(
  [() => props.projectId, () => props.taskId],
  ([newProjectId, newTaskId], [oldProjectId, oldTaskId]) => {
    if (newProjectId !== oldProjectId || newTaskId !== oldTaskId) {
      mainQueryVariables.value = {
        projectId: newProjectId,
        taskId: newTaskId,
        first: 25,
        after: null,
        last: null,
        before: null,
      }
    }
  },
  { immediate: false }
)

const {
  result,
  loading: qLoading,
  error: qError,
  refetch,
  fetchMore: fetchMoreComments,
} = useQuery(TASK_DETAIL_QUERY, mainQueryVariables, {
  fetchPolicy: 'cache-first', // Use cache-first to prevent unnecessary refetches
  nextFetchPolicy: 'cache-first', // Keep using cache for subsequent fetches
  notifyOnNetworkStatusChange: false,
  refetchOnWindowFocus: false, // Don't refetch when window regains focus
  refetchOnReconnect: false, // Don't refetch on reconnect
})

// Current user for AppHeader
const currentUser = computed(() => result.value?.currentUser || null)

// Bot username for share deep link
const botUsername = ref(null)
const { result: botUsernameResult } = useQuery(BOT_USERNAME_Q)
watch(
  botUsernameResult,
  data => {
    if (data?.botUsername) {
      botUsername.value = data.botUsername
    }
  },
  { immediate: true }
)

// Load board info if board_id is present in URL
const urlParams = new URLSearchParams(window.location.search)
const urlBoardId = urlParams.get('board_id')
if (urlBoardId) {
  boardId.value = urlBoardId
}

// Always set up the query (composables must be called unconditionally)
const {
  load: loadBoard,
  result: boardResult,
  loading: boardLoading,
} = useLazyQuery(
  BOARD_QUERY,
  () => ({
    id: boardId.value,
  }),
  () => ({ enabled: !!boardId.value })
)

loadingBoard.value = boardLoading.value
watch(boardResult, data => {
  if (data?.board) {
    board.value = data.board
    loadingBoard.value = false
  }
})

// Load board if boardId is present
if (boardId.value) {
  loadBoard()
}

const {
  result: eventsResult,
  loading: eventsLoading,
  refetch: refetchEvents,
} = useQuery(
  EVENTS_Q,
  () => ({
    projectId: props.projectId,
    taskId: taskGlobalId.value || props.taskId,
  }),
  () => ({ enabled: activeTab.value === 'history' })
)

// Update eventsCount when events are loaded
watch(
  eventsResult,
  data => {
    if (data?.project?.task?.events) {
      eventsCount.value = data.project.task.events.length
    }
  },
  { immediate: true }
)
// Lazy query for time entries
const TIME_ENTRIES_QUERY = gql`
  query TimeEntries($projectId: ID!, $taskId: ID!) {
    project(id: $projectId) {
      task(id: $taskId) {
        id
        timeEntries {
          id
          durationMinutes
          formattedDuration
          workedOn
          description
          user {
            id
            username
          }
          canUpdate
          canDestroy
        }
        childTasks {
          id
          title
          timeEntries {
            id
            durationMinutes
            formattedDuration
            workedOn
            description
            user {
              id
              username
            }
            canUpdate
            canDestroy
          }
          myTimeSpent
          childTasks {
            id
            title
            timeEntries {
              id
              durationMinutes
              formattedDuration
              workedOn
              description
              user {
                id
                username
              }
              canUpdate
              canDestroy
            }
            myTimeSpent
          }
        }
      }
    }
  }
`

const {
  load: loadTimeEntriesQuery,
  result: timeEntriesResult,
  loading: timeEntriesQueryLoading,
} = useLazyQuery(
  TIME_ENTRIES_QUERY,
  () => ({
    projectId: props.projectId,
    taskId: taskGlobalId.value || props.taskId,
  }),
  {
    // Use 'no-cache' to prevent Apollo from writing to cache and invalidating main query
    fetchPolicy: 'no-cache',
  }
)

// Track if we're updating from time entries to prevent main query refetch
const updatingFromTimeEntries = ref(false)

// Watch for time entries result - use flush: 'post' to avoid triggering main query refetch
watch(
  timeEntriesResult,
  data => {
    const task = data?.project?.task
    if (task) {
      updatingFromTimeEntries.value = true
      timeEntries.value = task.timeEntries || []

      // Instead of replacing childTasks, merge time entries into existing structure
      // This prevents Apollo from detecting a cache change that would trigger main query refetch
      const timeEntriesByTaskId = new Map()
      const collectTimeEntries = tasks => {
        tasks.forEach(t => {
          if (t.timeEntries && t.timeEntries.length > 0) {
            timeEntriesByTaskId.set(t.id, t.timeEntries)
          }
          if (t.childTasks && t.childTasks.length > 0) {
            collectTimeEntries(t.childTasks)
          }
        })
      }
      collectTimeEntries(task.childTasks || [])

      // Merge time entries into existing childTasks structure without replacing it
      const mergeTimeEntries = tasks => {
        return tasks.map(existingChild => {
          const timeEntries = timeEntriesByTaskId.get(existingChild.id) || []
          const updatedChild = {
            ...existingChild,
            timeEntries: timeEntries,
          }
          if (existingChild.childTasks && existingChild.childTasks.length > 0) {
            updatedChild.childTasks = mergeTimeEntries(existingChild.childTasks)
          }
          return updatedChild
        })
      }

      // Use nextTick to defer the update and avoid triggering main query refetch
      nextTick(() => {
        // Only update if we have existing childTasks to merge into
        if (childTasks.value && childTasks.value.length > 0) {
          childTasks.value = mergeTimeEntries(childTasks.value)
        } else {
          // Fallback: if no existing childTasks, set from time entries query
          childTasks.value = (task.childTasks || []).map(child => ({
            ...child,
            timeEntries: child.timeEntries || [],
            childTasks: (child.childTasks || []).map(grandchild => ({
              ...grandchild,
              timeEntries: grandchild.timeEntries || [],
            })),
          }))
        }
        timeEntriesLoaded.value = true
        // Reset flag after a short delay to allow any reactive updates to complete
        setTimeout(() => {
          updatingFromTimeEntries.value = false
        }, 100)
      })
    }
  },
  { flush: 'post' }
)

async function loadTimeEntries() {
  if (timeEntriesQueryLoading.value) return
  if (timeEntriesLoaded.value) return

  try {
    await loadTimeEntriesQuery()
    // The result watcher will handle setting the data
  } catch (error) {
    console.error('Failed to load time entries:', error)
    timeEntriesLoaded.value = false // Reset on error so user can retry
  }
}

// Reload time entries after mutations
async function reloadTimeEntries() {
  timeEntriesLoaded.value = false // Reset flag to allow reload
  try {
    await loadTimeEntriesQuery()
    // Wait for the query to complete and timeEntriesResult to update
    // Poll until timeEntriesResult has new data, then manually update
    await new Promise(resolve => {
      const checkResult = () => {
        const data = timeEntriesResult.value
        const task = data?.project?.task
        if (task) {
          // Manually update time entries to ensure reactivity
          updatingFromTimeEntries.value = true
          timeEntries.value = [...(task.timeEntries || [])] // Create new array to force reactivity

          // Update child tasks time entries
          const timeEntriesByTaskId = new Map()
          const collectTimeEntries = tasks => {
            tasks.forEach(t => {
              if (t.timeEntries && t.timeEntries.length > 0) {
                timeEntriesByTaskId.set(t.id, t.timeEntries)
              }
              if (t.childTasks && t.childTasks.length > 0) {
                collectTimeEntries(t.childTasks)
              }
            })
          }
          collectTimeEntries(task.childTasks || [])

          const mergeTimeEntries = tasks => {
            return tasks.map(existingChild => {
              const timeEntries = timeEntriesByTaskId.get(existingChild.id) || []
              const updatedChild = {
                ...existingChild,
                timeEntries: [...timeEntries], // Create new array
              }
              if (existingChild.childTasks && existingChild.childTasks.length > 0) {
                updatedChild.childTasks = mergeTimeEntries(existingChild.childTasks)
              }
              return updatedChild
            })
          }

          nextTick(() => {
            if (childTasks.value && childTasks.value.length > 0) {
              childTasks.value = mergeTimeEntries(childTasks.value)
            } else {
              childTasks.value = (task.childTasks || []).map(child => ({
                ...child,
                timeEntries: [...(child.timeEntries || [])],
                childTasks: (child.childTasks || []).map(grandchild => ({
                  ...grandchild,
                  timeEntries: [...(grandchild.timeEntries || [])],
                })),
              }))
            }
            timeEntriesLoaded.value = true
            setTimeout(() => {
              updatingFromTimeEntries.value = false
            }, 100)
          })
          resolve()
        } else {
          setTimeout(checkResult, 50)
        }
      }
      checkResult()
    })
  } catch (error) {
    console.error('Failed to reload time entries:', error)
  }
}

// Helper function to calculate metrics from time entries
function calculateMetricsFromEntries(entries, currentUserId, isAdminOrOwner) {
  let total = 0
  let myTotal = 0

  entries.forEach(entry => {
    const minutes = entry.durationMinutes || 0
    // Total includes all entries if admin/owner, otherwise only own entries
    if (isAdminOrOwner || (entry.user && entry.user.id === currentUserId)) {
      total += minutes
    }
    // My total only includes current user's entries
    if (entry.user && entry.user.id === currentUserId) {
      myTotal += minutes
    }
  })

  return { total, myTotal }
}

// Helper function to calculate total time in descendants
function calculateDescendantsTime(childTasks, currentUserId, isAdminOrOwner) {
  let total = 0

  const processTask = task => {
    if (task.timeEntries && task.timeEntries.length > 0) {
      task.timeEntries.forEach(entry => {
        const minutes = entry.durationMinutes || 0
        // If admin/owner, count all entries; otherwise only own entries
        if (isAdminOrOwner || (entry.user && entry.user.id === currentUserId)) {
          total += minutes
        }
      })
    }
    if (task.childTasks && task.childTasks.length > 0) {
      task.childTasks.forEach(processTask)
    }
  }

  if (childTasks && childTasks.length > 0) {
    childTasks.forEach(processTask)
  }

  return total
}

// Get current user ID - use the ref if available, otherwise try to get from task
function getCurrentUserId() {
  if (currentUserId.value) {
    return currentUserId.value
  }
  // Fallback: try to get from task author
  if (task.value?.author?.id) {
    return task.value.author.id
  }
  return null
}

function handleTimeLogged(timeEntry) {
  if (!timeEntry) return

  // Insert the new time entry in the correct position based on date (workedOn)
  // Entries should be sorted by date, typically newest first (descending)
  const newEntries = [...timeEntries.value, timeEntry]
  // Sort by workedOn date (descending - newest first), then by id as tiebreaker
  newEntries.sort((a, b) => {
    const dateA = a.workedOn || ''
    const dateB = b.workedOn || ''
    if (dateA !== dateB) {
      return dateB.localeCompare(dateA) // Descending order (newest first)
    }
    // If dates are equal, sort by ID (newer entries typically have higher IDs)
    return (b.id || '').localeCompare(a.id || '')
  })
  timeEntries.value = newEntries

  // Recalculate metrics by adding the new entry's duration
  const userId = getCurrentUserId()
  const newMinutes = timeEntry.durationMinutes || 0

  // Update totals: add the new entry's duration
  if (isAdminOrOwner.value || (timeEntry.user && timeEntry.user.id === userId)) {
    totalTimeSpent.value += newMinutes
  }
  // Update my time: add if it's the current user's entry
  if (timeEntry.user && timeEntry.user.id === userId) {
    myTimeSpent.value += newMinutes
  }

  // Recalculate descendants time (unchanged for this task's entries)
  totalTimeSpentInDescendants.value = calculateDescendantsTime(
    childTasks.value,
    userId,
    isAdminOrOwner.value
  )
}

function handleTimeUpdated(timeEntry) {
  if (!timeEntry) return

  // Find the old entry to get its previous duration and user info
  const index = timeEntries.value.findIndex(e => e.id === timeEntry.id)
  const oldEntry = index !== -1 ? timeEntries.value[index] : null
  const oldMinutes = oldEntry ? oldEntry.durationMinutes || 0 : 0
  const newMinutes = timeEntry.durationMinutes || 0

  // If user info is missing from the updated entry (shouldn't happen, but be safe),
  // preserve it from the old entry
  if (!timeEntry.user && oldEntry && oldEntry.user) {
    timeEntry.user = oldEntry.user
  }

  // Update the time entry in the list
  if (index !== -1) {
    // Check if date changed - if so, we need to re-sort
    const dateChanged = oldEntry && oldEntry.workedOn !== timeEntry.workedOn
    if (dateChanged) {
      // Remove old entry and insert new one in correct position
      const entriesWithoutOld = timeEntries.value.filter(e => e.id !== timeEntry.id)
      const newEntries = [...entriesWithoutOld, timeEntry]
      // Sort by workedOn date (descending - newest first), then by id as tiebreaker
      newEntries.sort((a, b) => {
        const dateA = a.workedOn || ''
        const dateB = b.workedOn || ''
        if (dateA !== dateB) {
          return dateB.localeCompare(dateA) // Descending order (newest first)
        }
        return (b.id || '').localeCompare(a.id || '')
      })
      timeEntries.value = newEntries
    } else {
      // Date didn't change, just update in place
      timeEntries.value = [
        ...timeEntries.value.slice(0, index),
        timeEntry,
        ...timeEntries.value.slice(index + 1),
      ]
    }
  } else {
    // If not found, add it (shouldn't happen, but be safe)
    const newEntries = [...timeEntries.value, timeEntry]
    newEntries.sort((a, b) => {
      const dateA = a.workedOn || ''
      const dateB = b.workedOn || ''
      if (dateA !== dateB) {
        return dateB.localeCompare(dateA)
      }
      return (b.id || '').localeCompare(a.id || '')
    })
    timeEntries.value = newEntries
  }

  // Recalculate metrics by applying the difference
  const userId = getCurrentUserId()

  // Check if entries belong to current user
  const isCurrentUserEntry = timeEntry.user && String(timeEntry.user.id) === String(userId)
  const wasCurrentUserEntry =
    oldEntry && oldEntry.user && String(oldEntry.user.id) === String(userId)

  // Determine if old entry was counted in total (admin/owner sees all, regular user sees only own)
  const oldEntryWasCountedInTotal =
    oldEntry &&
    (isAdminOrOwner.value || (oldEntry.user && String(oldEntry.user.id) === String(userId)))

  // Determine if new entry should be counted in total
  // Admin/owner sees all entries, regular user sees only their own
  const newEntryShouldCountInTotal = isAdminOrOwner.value || isCurrentUserEntry

  // Update totalTimeSpent: subtract old, add new
  // Only subtract if the old entry was actually counted
  if (oldEntryWasCountedInTotal && oldMinutes > 0) {
    totalTimeSpent.value = Math.max(0, totalTimeSpent.value - oldMinutes)
  }
  // Only add if the new entry should be counted
  if (newEntryShouldCountInTotal && newMinutes > 0) {
    totalTimeSpent.value += newMinutes
  }

  // Update myTimeSpent: subtract old if it was user's entry, add new if it's user's entry
  if (wasCurrentUserEntry && oldMinutes > 0) {
    myTimeSpent.value = Math.max(0, myTimeSpent.value - oldMinutes)
  }
  if (isCurrentUserEntry && newMinutes > 0) {
    myTimeSpent.value += newMinutes
  }

  // Recalculate descendants time (unchanged for this task's entries)
  totalTimeSpentInDescendants.value = calculateDescendantsTime(
    childTasks.value,
    userId,
    isAdminOrOwner.value
  )
}

function handleTimeDeleted(timeEntryId) {
  if (!timeEntryId) return

  // Remove the time entry from the list
  timeEntries.value = timeEntries.value.filter(e => e.id !== timeEntryId)

  // Recalculate metrics
  const userId = getCurrentUserId()
  const metrics = calculateMetricsFromEntries(timeEntries.value, userId, isAdminOrOwner.value)
  totalTimeSpent.value = metrics.total
  myTimeSpent.value = metrics.myTotal

  // Recalculate descendants time
  totalTimeSpentInDescendants.value = calculateDescendantsTime(
    childTasks.value,
    userId,
    isAdminOrOwner.value
  )
}

// Sync loading state from query
watch(timeEntriesQueryLoading, loading => {
  timeEntriesLoading.value = loading
})

// Watch for tab expansion to load time entries lazily
watch(timeTrackingExpanded, expanded => {
  if (expanded && timeTrackingEnabled.value) {
    loadTimeEntries()
  }
})

// Task mentions state
const taskMentionsMap = ref({})
const loadingTaskMentions = ref(false)

// Extract and fetch mentioned tasks from description and comments
async function loadTaskMentions() {
  if (!props.projectId || !task.value) return

  const taskIds = new Set()

  // Extract from description
  if (task.value.description) {
    const descTaskIds = extractTaskMentions(task.value.description)
    descTaskIds.forEach(id => taskIds.add(id))
  }

  // Extract from all comments
  if (comments.value && comments.value.length > 0) {
    comments.value.forEach(comment => {
      if (comment.body) {
        const commentTaskIds = extractTaskMentions(comment.body)
        commentTaskIds.forEach(id => taskIds.add(id))
      }
      // Also check replies
      if (comment.replies) {
        comment.replies.forEach(reply => {
          if (reply.body) {
            const replyTaskIds = extractTaskMentions(reply.body)
            replyTaskIds.forEach(id => taskIds.add(id))
          }
        })
      }
    })
  }

  const uniqueTaskIds = Array.from(taskIds)
  if (uniqueTaskIds.length === 0) return

  // Filter out tasks we already have
  const tasksToFetch = uniqueTaskIds.filter(id => !taskMentionsMap.value[id])
  if (tasksToFetch.length === 0) return

  loadingTaskMentions.value = true
  try {
    const fetchedTasks = await fetchMentionedTasks(apolloClient, props.projectId, tasksToFetch)
    taskMentionsMap.value = { ...taskMentionsMap.value, ...fetchedTasks }
  } catch (error) {
    console.error('Failed to fetch mentioned tasks:', error)
  } finally {
    loadingTaskMentions.value = false
  }
}

// Watch for task and comments changes to reload mentions
watch(
  () => task.value?.description,
  () => {
    loadTaskMentions()
  },
  { immediate: true }
)

watch(
  comments,
  () => {
    loadTaskMentions()
  },
  { deep: true, immediate: true }
)
const { mutate: executeTransitionMutation } = useMutation(EXECUTE_TRANSITION)
const { mutate: updateTitleMutation } = useMutation(UPDATE_TASK_TITLE)

// Lazy query for personal labels
const {
  load: loadPersonalLabels,
  result: personalLabelsResult,
  refetch: refetchPersonalLabels,
} = useLazyQuery(
  PERSONAL_LABELS_QUERY,
  () => ({}),
  () => ({
    enabled: personalLabelsEnabled.value,
  })
)
const { mutate: updateDescriptionMutation } = useMutation(UPDATE_TASK_DESCRIPTION)
const { mutate: updatePriorityMutation } = useMutation(UPDATE_TASK_PRIORITY)
const { mutate: updateDueDateMutation } = useMutation(UPDATE_TASK_DUE_DATE)
const { mutate: updateAssigneeMutation } = useMutation(UPDATE_TASK_ASSIGNEE)
const { mutate: createCommentMutation } = useMutation(CREATE_TASK_COMMENT)
const { mutate: updateCommentMutation } = useMutation(UPDATE_TASK_COMMENT)
const { mutate: deleteCommentMutation } = useMutation(DELETE_TASK_COMMENT)
const { mutate: subscribeToTaskMutation } = useMutation(SUBSCRIBE_TO_TASK)
const { mutate: unsubscribeFromTaskMutation } = useMutation(UNSUBSCRIBE_FROM_TASK)
const { mutate: updateConfidentialityMutation } = useMutation(UPDATE_TASK_CONFIDENTIALITY)
const { mutate: updateArchivedMutation } = useMutation(UPDATE_TASK_ARCHIVED)
const { mutate: addViewerMutation } = useMutation(ADD_CONFIDENTIAL_TASK_VIEWER)
const { mutate: removeViewerMutation } = useMutation(REMOVE_CONFIDENTIAL_TASK_VIEWER)
const { mutate: createTaskGradeMutation } = useMutation(CREATE_TASK_GRADE)
const { mutate: updateTaskGradeMutation } = useMutation(UPDATE_TASK_GRADE)
const { mutate: destroyTaskGradeMutation } = useMutation(DESTROY_TASK_GRADE)
const { mutate: changeTaskCategoryMutation } = useMutation(CHANGE_TASK_CATEGORY)
const { mutate: subscribeUserToTaskMutation } = useMutation(SUBSCRIBE_USER_TO_TASK)
const { mutate: unsubscribeUserFromTaskMutation } = useMutation(UNSUBSCRIBE_USER_FROM_TASK)

watch(
  qLoading,
  v => {
    if (!loadingMoreComments.value) {
      loading.value = !!v
    }
  },
  { immediate: true }
)

// Remove HTML loading screen when data is loaded
watch(loading, isLoading => {
  if (!isLoading) {
    removeInitialLoading()
  }
})

watch(activeTab, newTab => {
  // Refetch events when switching to history tab
  if (newTab === 'history') {
    refetchEvents()
  }
})

// Helper function to refetch events if user is on history tab
async function refetchEventsIfNeeded() {
  if (activeTab.value === 'history') {
    await refetchEvents()
  }
}

// Handler for parent changed event
async function handleParentChanged() {
  await refetch()
}

// Handler for child created event
async function handleChildCreated() {
  await refetch()
}

// Handler for horizontal relation changed event (added/updated/removed)
async function handleRelationChanged() {
  await refetch()
  // Wait for Vue to process the result update
  await nextTick()
}

// Handler for attachments changed event (uploaded/deleted)
function handleAttachmentsChanged() {
  // No need to refetch the main task query - attachments are loaded separately
  // The TaskAttachments component handles its own refetch
}

// Handler for attachments count changed
function handleAttachmentsCountChanged(count) {
  attachmentsCount.value = count
}
watch(
  qError,
  e => {
    if (e) error.value = e.message || 'Failed to load task'
  },
  { immediate: true }
)
// Track if we've already processed the initial result to prevent unnecessary updates
const initialResultProcessed = ref(false)

watch(
  result,
  data => {
    const n = data?.project?.task
    // Store project data - only update if we have project data (preserve during refetch)
    // This ensures project.value doesn't become undefined during refetch, preventing component unmount
    if (data?.project) {
      project.value = data.project
      projectArchived.value = data.project.archived || false
      projectTaskCategories.value = data.project.taskCategories || []
      requiredFields.value = data.project.requiredFields || []
      projectMembers.value = data.project.members || []
      memberships.value = data.project.memberships || []
      // Update checklistsEnabled only when we have new project data
      // This preserves the value during refetch when data might be temporarily undefined
      if (data.project.checklistsEnabled !== undefined) {
        checklistsEnabled.value = data.project.checklistsEnabled
      }
    }
    // If data is null/undefined (during refetch), preserve existing project.value and checklistsEnabled.value
    // This prevents the Checklist component from being unmounted
    // Update confidentialTasksEnabled first so we can use it in the check below
    confidentialTasksEnabled.value = data?.project?.confidentialTasksEnabled || false

    // Check if project exists but task is null (likely confidential/not accessible)
    if (!n && data?.project) {
      taskNotFound.value = true
      task.value = null
      // Still load project data for the back link
      projectLabels.value = data?.project?.labels || []
      projectMembers.value = data?.project?.members || []
      memberships.value = data?.project?.memberships || []
      isPersonalProject.value =
        data?.project?.isPersonal === true || data?.project?.is_personal === true
      return
    }
    taskNotFound.value = false
    if (!n) return

    // Skip if we're updating from time entries query to prevent refetch loop
    if (updatingFromTimeEntries.value) {
      return
    }

    // Always update time tracking metrics when result changes (for refetches after mutations)
    // This ensures metrics are updated even after initial load
    timeTrackingEnabled.value = data?.project?.timeTrackingEnabled || false
    totalTimeSpent.value = n.totalTimeSpent || 0
    myTimeSpent.value = n.myTimeSpent || 0
    totalTimeSpentInDescendants.value = n.totalTimeSpentInDescendants || 0

    // Always update projectLabels when project data changes (for new labels, etc.)
    if (data?.project?.labels) {
      projectLabels.value = data.project.labels || []
    }

    // Update personal labels enabled flag
    personalLabelsEnabled.value = data?.currentUser?.personalLabelsEnabled || false

    // Only update task-related refs if task ID changed or this is the first load
    const taskIdChanged = task.value?.id !== n.id
    if (!initialResultProcessed.value || taskIdChanged) {
      task.value = n
      projectMembers.value = data?.project?.members || []
      memberships.value = data?.project?.memberships || []
      gradesEnabled.value = data?.project?.gradesEnabled || false
      isPersonalProject.value =
        data?.project?.isPersonal === true || data?.project?.is_personal === true
      canManageLabels.value = data?.project?.canManageLabels || false
      selectedWhitelistedViewerIds.value = (n.whitelistedViewers || []).map(v => v.id)
      canUpdate.value = n.canUpdateTask || false
      canUpdateAssigneeBackend.value = n.canUpdateAssignee || false
      canComment.value = n.canCreateComment || false
      canArchive.value = n.canArchiveTask || false
      canClone.value = n.canCloneTask || false
      canSubscribeOthers.value = n.canSubscribeOthers || false
      canManageRelations.value = n.canManageRelations || false
      subscribed.value = n.subscribed || false
      subscribersCount.value = n.subscribersCount || 0
      subscribers.value = n.subscribers || []
      eventsCount.value = n.eventsCount || 0
      attachmentsCount.value = n.attachmentsCount || 0

      // Extract vertical relations (hierarchy)
      verticalRelationsEnabled.value = data?.project?.verticalRelationsEnabled || false

      initialResultProcessed.value = true

      // Check if user is admin or owner (simplified - check if user can update project)
      isAdminOrOwner.value = data?.project?.canUpdateProject || false
    } else {
      // Keep self subscription state reactive after subscribe/unsubscribe mutations.
      if (n.subscribed !== undefined) {
        subscribed.value = n.subscribed
      }
      // Always update subscribersCount even if task ID hasn't changed (e.g., after adding a comment with mentions)
      if (n.subscribersCount !== undefined) {
        subscribersCount.value = n.subscribersCount
      }
      if (n.subscribers !== undefined) {
        subscribers.value = n.subscribers || []
      }
      if (n.checklist !== undefined && task.value) {
        task.value = {
          ...task.value,
          checklist: mergeChecklistForTask(n.checklist),
          checklistCompleted: n.checklistCompleted ?? task.value.checklistCompleted,
        }
      }
      // Always update attachmentsCount even if task ID hasn't changed
      if (n.attachmentsCount !== undefined) {
        attachmentsCount.value = n.attachmentsCount
      }
    }

    // Always update horizontal relations when result changes (for refetches after mutations)
    // This ensures relations are updated immediately after add/update/remove operations
    horizontalRelationTypes.value = data?.project?.horizontalRelationTypes || []
    outgoingHorizontalRelations.value = n.outgoingHorizontalRelations || []
    incomingHorizontalRelations.value = n.incomingHorizontalRelations || []

    // Always update whitelisted viewers when result changes (for whitelist updates)
    // This ensures whitelisted viewers are updated immediately after add/remove operations
    if (n.whitelistedViewers !== undefined && task.value) {
      const newViewerIds = (n.whitelistedViewers || [])
        .map(v => v.id)
        .sort()
        .join(',')
      const currentViewerIds = (task.value.whitelistedViewers || [])
        .map(v => v.id)
        .sort()
        .join(',')

      // Only update if whitelisted viewers actually changed to avoid unnecessary reactivity triggers
      if (newViewerIds !== currentViewerIds) {
        // Create a new task object to maintain Vue reactivity
        task.value = { ...task.value, whitelistedViewers: n.whitelistedViewers || [] }
        selectedWhitelistedViewerIds.value = (n.whitelistedViewers || []).map(v => v.id)
      }
    }

    // Always update child tasks when result changes (for hierarchy updates like adding children)
    // This ensures child tasks are updated even when other task fields don't change
    if (n.childTasks) {
      childTasks.value = (n.childTasks || []).map(child => ({
        id: child.id,
        title: child.title,
        workflowStatus: child.workflowStatus,
        myTimeSpent: child.myTimeSpent || 0,
        childTasks: (child.childTasks || []).map(grandchild => ({
          id: grandchild.id,
          title: grandchild.title,
          workflowStatus: grandchild.workflowStatus,
          myTimeSpent: grandchild.myTimeSpent || 0,
        })),
      }))
    }

    // Extract comments from connection structure
    // Skip if we're loading more comments or if we just updated comments in loadEarlierComments
    // Also skip if the query result has fewer comments than we currently have
    // This can happen when the watch handler runs with a stale result after loading more comments
    const shouldSkip =
      loadingMoreComments.value ||
      skipNextWatch.value ||
      (n.comments && n.comments.edges && comments.value.length > n.comments.edges.length)

    if (n.comments && n.comments.edges && !shouldSkip) {
      // Backend returns newest-first, but we want oldest-first for display
      // So we reverse them when storing
      const edges = n.comments.edges || []
      const newCommentsData = [...edges].reverse().map(edge => {
        const comment = edge.node
        // Replies are already an array from the backend (newest first)
        // Reverse to oldest-first for display (oldest at top, newest at bottom)
        // Create a new object to avoid mutating the read-only GraphQL response
        const replies =
          comment.replies && Array.isArray(comment.replies)
            ? [...comment.replies].reverse() // Reverse to oldest-first (newest at bottom)
            : []

        // Track that we've loaded 5 replies initially (only if not already tracked)
        if (!loadedRepliesCount.value[comment.id]) {
          loadedRepliesCount.value[comment.id] =
            comment.replies && Array.isArray(comment.replies) ? 5 : 0
        }

        return {
          ...comment,
          replies,
        }
      })

      // Merge with existing comments to preserve loaded replies
      // Create a map of existing comments by ID to preserve their loaded replies
      const existingCommentsMap = new Map()
      comments.value.forEach(comment => {
        existingCommentsMap.set(comment.id, comment)
      })

      // Merge new comments with existing ones, preserving loaded replies
      // Also preserve comments that might not be in the new data (shouldn't happen, but just in case)
      const mergedComments = newCommentsData.map(newComment => {
        const existingComment = existingCommentsMap.get(newComment.id)
        if (existingComment) {
          // Preserve existing replies if we've loaded more than the initial 5
          // Check both reply count and loadedRepliesCount to determine if we've loaded more
          const hasLoadedMoreReplies =
            existingComment.replies &&
            (existingComment.replies.length > newComment.replies.length ||
              (loadedRepliesCount.value[newComment.id] &&
                loadedRepliesCount.value[newComment.id] > 5))

          return {
            ...newComment,
            replies: hasLoadedMoreReplies ? existingComment.replies : newComment.replies,
            hasMoreReplies:
              existingComment.hasMoreReplies !== undefined
                ? existingComment.hasMoreReplies
                : newComment.hasMoreReplies,
          }
        }
        return newComment
      })

      // IMPORTANT: Preserve comments that are NOT in the query result
      // This can happen when we've loaded more comments via loadEarlierComments
      // The query result might only have the original 25, but we have 27 (25 + 2 new)
      const mergedCommentIds = new Set(mergedComments.map(c => c.id))
      const preservedComments = comments.value.filter(c => !mergedCommentIds.has(c.id))

      // Combine preserved comments (older ones) with merged comments (from query result)
      // Preserved comments should be prepended since they're older
      comments.value = [...preservedComments, ...mergedComments]
      // With recent ordering (newest first), hasNextPage indicates if there are older comments
      // and endCursor points to the oldest comment in the current set
      commentsHasPreviousPage.value = n.comments.pageInfo?.hasNextPage || false
      commentsStartCursor.value = n.comments.pageInfo?.endCursor || null
    }

    // Only update these fields if task ID changed or this is the first load
    if (!initialResultProcessed.value || taskIdChanged) {
      currentUserId.value = data?.currentUser?.id || null
      userTimezone.value = data?.currentUser?.preferredTimezone || null
      const preferredLanguage = data?.currentUser?.preferredLanguage || null
      userLocale.value = preferredLanguage

      // Load translations and set HTML lang attribute if user has preferred language
      if (preferredLanguage) {
        document.documentElement.lang = preferredLanguage
        loadTranslations(preferredLanguage)
        localeKey.value++ // Force re-render of computed properties that use translations
      }

      personalLabelsEnabled.value = data?.currentUser?.personalLabelsEnabled || false

      // Initialize grade form (for current user's grade)
      if (n.grade) {
        gradeForm.value = {
          gradeId: n.grade.grade?.id || null,
          confidential: n.grade.confidential || false,
        }
      } else {
        gradeForm.value = {
          gradeId: null,
          confidential: false,
        }
      }
      editingGradeItem.value = null

      // Initialize edit form
      editForm.value = {
        title: n.title || '',
        description: n.description || '',
        priority: n.priority || null,
        dueAt: n.dueAt || null,
        assigneeId: n.assignee?.id || null,
        confidential: n.confidential || false,
      }
    }
  },
  { flush: 'post' }
)

const GET_AVAILABLE_TRANSITIONS = gql`
  query GetAvailableTransitions($projectId: ID!, $taskId: ID!) {
    project(id: $projectId) {
      task(id: $taskId) {
        id
        availableTransitions {
          id
          name
          targetStatus {
            id
            name
            color
            requiresCompletedChecklists
          }
        }
      }
    }
  }
`

const GET_WORKFLOW_STATUSES = gql`
  query GetWorkflowStatuses($projectId: ID!) {
    project(id: $projectId) {
      taskCategories {
        id
        workflow {
          id
          statuses {
            id
            name
            color
            isInitial
            isFinal
          }
        }
      }
    }
  }
`

const {
  load: loadTransitions,
  refetch: refetchTransitions,
  result: transitionsResult,
  loading: transitionsQueryLoading,
} = useLazyQuery(
  GET_AVAILABLE_TRANSITIONS,
  () => ({
    projectId: props.projectId,
    taskId: taskGlobalId.value || props.taskId,
  }),
  {
    fetchPolicy: 'no-cache', // Don't write to cache to prevent triggering main query refetch
    notifyOnNetworkStatusChange: false,
  }
)

const {
  load: loadWorkflowStatuses,
  result: workflowStatusesResult,
  loading: workflowStatusesLoading,
} = useLazyQuery(
  GET_WORKFLOW_STATUSES,
  () => ({
    projectId: props.projectId,
  }),
  {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: false,
  }
)

// Watch for workflow statuses result
watch(
  workflowStatusesResult,
  data => {
    if (data?.project?.taskCategories) {
      // Use pendingCategoryChange first (set before loading), then fall back to selectedTaskCategoryId
      const categoryId = pendingCategoryChange.value?.taskCategoryId || selectedTaskCategoryId.value
      if (categoryId) {
        const category = data.project.taskCategories.find(c => c.id === categoryId)
        if (category?.workflow?.statuses) {
          newWorkflowStatuses.value = category.workflow.statuses
        } else {
          // If no statuses found, clear the array
          newWorkflowStatuses.value = []
        }
      }
    }
  },
  { immediate: true }
)

// Watch loading state
watch(
  workflowStatusesLoading,
  loading => {
    loadingWorkflowStatuses.value = loading
  },
  { immediate: true }
)

// Watch for transitions result
watch(
  transitionsResult,
  data => {
    if (data?.project?.task?.availableTransitions) {
      availableTransitions.value = data.project.task.availableTransitions
    }
  },
  { immediate: true }
)

// Watch loading state
watch(
  transitionsQueryLoading,
  loading => {
    loadingTransitions.value = loading
  },
  { immediate: true }
)

async function openTransitionModal() {
  showTransitionModal.value = true

  // Always refetch transitions when opening modal to get latest state
  // This ensures transitions reflect current checklist completion status
  try {
    if (transitionsResult.value) {
      // If we've loaded transitions before, refetch to get latest state
      await refetchTransitions()
    } else {
      // First time loading
      await loadTransitions()
    }
  } catch (e) {
    console.error('Failed to load transitions:', e)
    transitionError.value = e.message || 'Failed to load transitions'
  }
}

function closeTransitionModal() {
  showTransitionModal.value = false
  transitionError.value = ''
}

async function executeTransition(transition) {
  try {
    executing.value = true
    transitionError.value = ''

    const { data } = await executeTransitionMutation({
      taskId: taskGlobalId.value,
      transitionId: transition.id,
    })

    if (data?.executeTransition?.errors?.length) {
      // Show error banner
      taskAlert.value = {
        type: 'error',
        title: s__('TaskDetail|Failed to execute transition'),
        message: data.executeTransition.errors.join(', '),
      }
      return
    }

    // Update local task data with the new status and available transitions
    // Create a new object to avoid mutating readonly reactive properties
    if (data?.executeTransition?.task) {
      task.value = {
        ...task.value,
        workflowStatus: data.executeTransition.task.workflowStatus,
      }
      // Update available transitions from the mutation response
      if (data.executeTransition.task.availableTransitions) {
        availableTransitions.value = data.executeTransition.task.availableTransitions
      }
    }

    // Refetch full task data to update grade and canGradeTask fields
    // This ensures the Grade section is properly updated when status changes to/from finished
    await refetch()
    await refetchEventsIfNeeded()

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Status updated'),
      message: s__('TaskDetail|The task status has been successfully updated.'),
    }

    // Close modal immediately after successful transition
    closeTransitionModal()
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to execute transition'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  } finally {
    executing.value = false
  }
}

// Handle project labels update from OnTaskUpdateLabelsForm
async function handleProjectLabelsUpdate(updatedLabels) {
  try {
    if (task.value) {
      task.value = {
        ...task.value,
        labels: updatedLabels,
      }
    }
    await refetch()
    await refetchEventsIfNeeded()

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Labels updated'),
      message: s__('TaskDetail|The project labels have been successfully updated.'),
    }
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update labels'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  }
}

// Handle personal labels update from OnTaskUpdateLabelsForm
async function handlePersonalLabelsUpdate(updatedLabels) {
  try {
    if (task.value) {
      task.value = {
        ...task.value,
        personalLabels: updatedLabels,
      }
    }

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Personal labels updated'),
      message: s__('TaskDetail|The personal labels have been successfully updated.'),
    }
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update personal labels'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  }
}

// Refetch function for project labels only (without refetching entire task)
async function refetchProjectLabelsOnly() {
  if (!props.projectId) return
  try {
    const { data } = await apolloClient.query({
      query: gql`
        query GetProjectLabels($id: ID!) {
          project(id: $id) {
            labels {
              id
              title
              color
            }
          }
        }
      `,
      variables: { id: props.projectId },
      fetchPolicy: 'network-only',
    })
    if (data?.project?.labels) {
      projectLabels.value = data.project.labels || []
    }
  } catch (e) {
    console.error('Failed to refetch project labels:', e)
  }
}

// Checklist event handlers
// Don't refetch - the Checklist component manages its own state reactively
// Refetching causes the component to unmount and lose its local state
// The component will sync with props when they update naturally (e.g., on page navigation)
// If other parts of the UI need checklist data, they can access it through the Checklist component's events
function handleChecklistCreated(checklist) {
  // Update task.checklist and checklistCompleted directly to keep parent in sync without refetch
  if (task.value && checklist) {
    const mergedChecklist = mergeChecklistForTask(checklist)
    task.value = {
      ...task.value,
      checklist: mergedChecklist,
      checklistCompleted: mergedChecklist.completed || false,
    }
  }
  checklistCreateRequested.value = false
  // Don't call refetch() - it causes component unmount
  // But transitions will be refetched when modal opens
}

function handleChecklistUpdated(checklist) {
  // Update task.checklist and checklistCompleted directly to keep parent in sync without refetch
  // This makes transitions reactive to checklist changes
  if (task.value) {
    if (checklist) {
      // Checklist object provided - use its completed status
      const mergedChecklist = mergeChecklistForTask(checklist)
      task.value = {
        ...task.value,
        checklist: mergedChecklist,
        checklistCompleted: mergedChecklist.completed || false,
      }
    } else {
      // No checklist object - calculate from existing checklist if available
      // This handles edge cases where emit is called without argument
      if (task.value.checklist) {
        // Recalculate completed status from items if possible
        const items = task.value.checklist.items || []
        if (items.length > 0) {
          const completed = items.filter(i => i.completed).length
          task.value = {
            ...task.value,
            checklistCompleted: completed === items.length,
          }
        }
      }
    }
  }
  // Don't call refetch() - it causes component unmount
  // But transitions will be refetched when modal opens
  // The disable state is reactive to task.value.checklistCompleted changes
}

function handleChecklistDeleted() {
  // Update task.checklist directly to keep parent in sync without refetch
  if (task.value) {
    task.value = {
      ...task.value,
      checklist: null,
    }
  }
  checklistCreateRequested.value = false
  // Don't call refetch() - it causes component unmount
}

// Computed property to check if checklist is blocking transition
const isBlockingTransition = computed(() => {
  if (!project.value?.checklistsEnabled) return false
  if (!task.value?.workflowStatus?.requiresCompletedChecklists) return false
  return !task.value?.checklistCompleted
})

// Check if a specific transition should be disabled due to checklist requirements
function isTransitionDisabled(transition) {
  // Check if target status requires completed checklists
  if (!transition.targetStatus?.requiresCompletedChecklists) return false

  // Check if feature flag is enabled
  if (!project.value?.checklistsEnabled) return false

  // Check if task has a checklist
  if (!task.value?.checklist) return false

  // Check if checklist is incomplete
  if (task.value?.checklistCompleted) return false

  return true
}

// Get tooltip text for disabled transition
function getTransitionTooltip(transition) {
  if (!isTransitionDisabled(transition)) return ''
  return s__('TaskDetail|Checklist must be completed before transitioning to this status')
}

// Refetch function for personal labels that also updates available labels
async function refetchPersonalLabelsAndUpdate() {
  await refetchPersonalLabels()
  // The watch handler will update personalLabels.value automatically
}

// Watch for personal labels enabled to load personal labels
watch(
  personalLabelsEnabled,
  enabled => {
    if (enabled && !personalLabelsResult.value) {
      loadPersonalLabels()
    }
  },
  { immediate: true }
)

// Watch for personal labels query result
watch(
  personalLabelsResult,
  data => {
    if (data?.personalLabels) {
      personalLabels.value = data.personalLabels || []
    }
  },
  { immediate: true }
)

// Edit field functions
function startEditTitle() {
  editForm.value.title = task.value?.title || ''
  editingTitle.value = true
}

function cancelEditTitle() {
  editingTitle.value = false
}

async function saveTitle() {
  try {
    const { data } = await updateTitleMutation({
      taskId: taskGlobalId.value,
      title: editForm.value.title,
    })
    if (data?.updateTaskTitle?.errors?.length) {
      throw new Error(data.updateTaskTitle.errors.join(', '))
    }
    task.value = { ...task.value, ...data.updateTaskTitle.task }
    await refetchEventsIfNeeded()
    editingTitle.value = false

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Title updated'),
      message: s__('TaskDetail|The title has been successfully updated.'),
    }
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update title'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  }
}

async function showDescriptionPreview() {
  if (!editForm.value.description.trim()) return

  // Ensure URLs have schemas before preview
  ensureDescriptionUrlSchemas()

  // Extract task mentions from the edited description
  const taskIds = extractTaskMentions(editForm.value.description)
  let taskMap = { ...taskMentionsMap.value } // Start with existing map

  // Fetch task information for any new mentions not already in the map
  const tasksToFetch = taskIds.filter(id => !taskMap[id])
  if (tasksToFetch.length > 0 && props.projectId) {
    try {
      const fetchedTasks = await fetchMentionedTasks(apolloClient, props.projectId, tasksToFetch)
      taskMap = { ...taskMap, ...fetchedTasks }
    } catch (error) {
      console.error('Failed to fetch mentioned tasks for preview:', error)
    }
  }

  // Use the same renderer as read-only view for WYSIWYG-consistent preview.
  descriptionPreviewContent.value = telegramMarkdownToHtml(editForm.value.description, {
    processTaskMentions: true,
    taskMap: taskMap,
    buildTaskUrl: (taskId, projectId) => buildTaskUrl(taskId, projectId || props.projectId),
  })
  showDescriptionPreviewModal.value = true
}

function ensureDescriptionUrlSchemas() {
  if (!editForm.value.description) return

  let text = editForm.value.description
  // Find all link patterns [text](url) and ensure URLs have schemas
  const updatedText = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
    // Check if URL already has a schema (http://, https://, mailto:, etc.)
    const hasSchema = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url.trim())
    const isRelativeUrl = url.trim().startsWith('/') || url.trim().startsWith('#')
    if (!hasSchema && !isRelativeUrl && url.trim()) {
      return `[${linkText}](https://${url.trim()})`
    }
    return match
  })

  if (updatedText !== text) {
    editForm.value.description = updatedText
  }
}

function extractInlineAttachmentIds(text) {
  const ids = new Set()
  const pattern = /!?\[[^\]]*]\((?:https?:\/\/[^/)]+)?\/attachments\/(\d+)\/download(?:\?[^)]*)?\)/g
  let match
  while ((match = pattern.exec(text || '')) !== null) {
    ids.add(String(match[1]))
  }
  return Array.from(ids)
}

function removedInlineAttachmentIds(originalIds, uploadedIds, currentText) {
  const currentIds = new Set(extractInlineAttachmentIds(currentText))
  return Array.from(new Set([...originalIds, ...uploadedIds])).filter(
    id => !currentIds.has(String(id))
  )
}

function trackDescriptionInlineAttachmentUpload(result) {
  const id = result?.attachment?.id
  if (!id) return
  descriptionUploadedInlineAttachmentIds.value = [
    ...new Set([...descriptionUploadedInlineAttachmentIds.value, String(id)]),
  ]
}

function trackCommentInlineAttachmentUpload(result) {
  const id = result?.attachment?.id
  if (!id) return
  commentUploadedInlineAttachmentIds.value = [
    ...new Set([...commentUploadedInlineAttachmentIds.value, String(id)]),
  ]
}

function focusTemplateRef(refValue) {
  const target = Array.isArray(refValue) ? refValue[0] : refValue

  if (typeof target?.focus === 'function') {
    target.focus()
  }
}

async function startEditDescription() {
  editForm.value.description = task.value?.description || ''
  descriptionOriginalInlineAttachmentIds.value = extractInlineAttachmentIds(
    editForm.value.description
  )
  descriptionUploadedInlineAttachmentIds.value = []
  editingDescription.value = true

  for (let attempt = 0; attempt < 6; attempt += 1) {
    await nextTick()
    await new Promise(resolve => window.requestAnimationFrame(resolve))

    const editorElement =
      document.querySelector('.task-description-editor .wysiwyg-editor .ProseMirror') ||
      document.querySelector('.task-description-editor textarea')
    if (!editorElement) continue

    const rect = editorElement.getBoundingClientRect()
    const targetTop = window.scrollY + rect.top + rect.height / 2 - window.innerHeight / 2
    window.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' })

    if (editorElement.scrollIntoView) {
      editorElement.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }

    try {
      editorElement.focus({ preventScroll: true })
    } catch {
      editorElement.focus()
    }
    if (document.activeElement !== editorElement && editorElement.click) {
      editorElement.click()
    }
    return
  }
}

async function openChecklistCreateForm() {
  checklistCreateRequested.value = true
  await nextTick()
  checklistRef.value?.openCreateForm?.()
}

function cancelEditDescription() {
  editingDescription.value = false
  descriptionOriginalInlineAttachmentIds.value = []
  descriptionUploadedInlineAttachmentIds.value = []
}

async function saveDescription() {
  try {
    // Ensure URLs have schemas before submitting
    ensureDescriptionUrlSchemas()

    const { data } = await updateDescriptionMutation({
      taskId: taskGlobalId.value,
      description: editForm.value.description || null,
      removeAttachmentIds: removedInlineAttachmentIds(
        descriptionOriginalInlineAttachmentIds.value,
        descriptionUploadedInlineAttachmentIds.value,
        editForm.value.description
      ),
    })
    if (data?.updateTaskDescription?.errors?.length) {
      throw new Error(data.updateTaskDescription.errors.join(', '))
    }
    task.value = { ...task.value, ...data.updateTaskDescription.task }
    attachmentsCount.value = data.updateTaskDescription.task?.attachmentsCount || 0
    await refetchEventsIfNeeded()
    editingDescription.value = false
    descriptionOriginalInlineAttachmentIds.value = []
    descriptionUploadedInlineAttachmentIds.value = []

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Description updated'),
      message: s__('TaskDetail|The description has been successfully updated.'),
    }
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update description'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  }
}

function startEditPriority() {
  // Convert lowercase priority from backend to uppercase for GraphQL enum
  const priority = task.value?.priority
  editForm.value.priority = priority ? priority.toUpperCase() : null
  editingPriority.value = true

  // Note: Native select dropdowns cannot be programmatically opened in most browsers
  // The watcher will attempt to open it, but it may not work in all browsers
}

function cancelEditPriority() {
  editingPriority.value = false
}

function handlePriorityClickOutside(event) {
  if (!editingPriority.value) return
  if (!priorityEditRef.value) return

  // Don't cancel if we're in the process of opening the dropdown
  if (isOpeningPrioritySelect.value) return

  // Don't cancel if clicking inside the edit area
  if (priorityEditRef.value.contains(event.target)) return

  // Don't cancel if clicking inside the priority-select dropdown (which is teleported to body)
  const dropdown = document.querySelector('.priority-select-dropdown')
  if (dropdown && dropdown.contains(event.target)) return

  // Don't cancel if clicking on the Edit button (it might be in a different container)
  if (event.target.closest('button[title*="Edit priority"]')) return

  // Cancel edit mode
  cancelEditPriority()
}

// Watch for priority changes and auto-save
watch(
  () => editForm.value.priority,
  async (newValue, oldValue) => {
    // Only save if we're in edit mode, value actually changed, and it's different from original
    if (editingPriority.value && newValue !== oldValue && oldValue !== undefined) {
      await savePriority()
    }
  },
  { immediate: false }
)

async function savePriority() {
  try {
    const { data } = await updatePriorityMutation({
      taskId: taskGlobalId.value,
      priority: editForm.value.priority || null,
    })
    if (data?.updateTaskPriority?.errors?.length) {
      throw new Error(data.updateTaskPriority.errors.join(', '))
    }
    task.value = { ...task.value, ...data.updateTaskPriority.task }
    await refetchEventsIfNeeded()

    // Show success banner
    const priorityText = editForm.value.priority
      ? translatePriority(editForm.value.priority)
      : s__('TaskDetail|Not set')
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Priority updated'),
      message: sprintf(s__('TaskDetail|Priority updated to %{priority}'), {
        priority: priorityText,
      }),
    }

    // Auto-close edit mode after a short delay
    setTimeout(() => {
      editingPriority.value = false
    }, 500)
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update priority'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
    // Keep edit mode open on error so user can retry
  }
}

function startEditDueDate() {
  editForm.value.dueAt = task.value?.dueAt || null
  editingDueDate.value = true
}

function cancelEditDueDate() {
  editingDueDate.value = false
}

async function handleDueDateUpdate(newDate) {
  editForm.value.dueAt = newDate
  await saveDueDate()
}

async function saveDueDate() {
  try {
    if (isDueAtRequired.value && !editForm.value.dueAt) {
      throw new Error(s__('TaskForm|Due is required'))
    }

    const { data } = await updateDueDateMutation({
      taskId: taskGlobalId.value,
      dueAt: editForm.value.dueAt,
    })
    if (data?.updateTaskDueDate?.errors?.length) {
      throw new Error(data.updateTaskDueDate.errors.join(', '))
    }
    task.value = { ...task.value, ...data.updateTaskDueDate.task }
    await refetchEventsIfNeeded()
    editingDueDate.value = false

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Due date updated'),
      message: s__('TaskDetail|The due date has been successfully updated.'),
    }
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update due date'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  }
}

function startEditAssignee() {
  const currentAssigneeId = task.value?.assignee?.id || null
  editForm.value.assigneeId = currentAssigneeId
  originalAssigneeId.value = currentAssigneeId
  editingAssignee.value = true

  // Open the select dropdown after component is rendered
  // Use a watcher to handle this more reliably
}

function cancelEditAssignee() {
  editingAssignee.value = false
  originalAssigneeId.value = null
}

function handleAssigneeClickOutside(event) {
  if (!editingAssignee.value) return
  if (!assigneeEditRef.value) return

  // Don't cancel if we're in the process of opening the dropdown
  if (isOpeningAssigneeDropdown.value) return

  // Don't cancel if clicking inside the edit area
  if (assigneeEditRef.value.contains(event.target)) return

  // Don't cancel if clicking inside the user-select dropdown (which is teleported to body)
  const dropdown = document.querySelector('.user-select-dropdown')
  if (dropdown && dropdown.contains(event.target)) return

  // Don't cancel if clicking on the Edit button (it might be in a different container)
  if (event.target.closest('.assignee-edit-trigger')) return

  // Cancel edit mode
  cancelEditAssignee()
}

// Watch for edit mode to open dropdown
watch(
  () => editingAssignee.value,
  newValue => {
    if (newValue) {
      // Set flag to prevent click-outside handler from interfering
      isOpeningAssigneeDropdown.value = true

      // Open the select dropdown when edit mode is activated
      // Wait for Vue to render the component, then click the trigger
      nextTick(() => {
        nextTick(() => {
          requestAnimationFrame(() => {
            if (assigneeEditRef.value) {
              const trigger = assigneeEditRef.value.querySelector('.user-select-trigger')
              if (trigger) {
                trigger.click()
              }
            }
            // Clear the flag after dropdown should be open
            requestAnimationFrame(() => {
              isOpeningAssigneeDropdown.value = false
            })
          })
        })
      })
    }
  }
)

// Watch for priority edit mode to open dropdown
watch(
  () => editingPriority.value,
  newValue => {
    if (newValue) {
      // Set flag to prevent click-outside handler from interfering
      isOpeningPrioritySelect.value = true

      // Open the select dropdown when edit mode is activated
      // Wait for Vue to render the component, then click the trigger
      nextTick(() => {
        nextTick(() => {
          requestAnimationFrame(() => {
            if (priorityEditRef.value) {
              const trigger = priorityEditRef.value.querySelector('.priority-select-trigger')
              if (trigger) {
                trigger.click()
              }
            }
            // Clear the flag after dropdown should be open
            requestAnimationFrame(() => {
              isOpeningPrioritySelect.value = false
            })
          })
        })
      })
    }
  }
)

// Watch for priority changes and auto-save
watch(
  () => editForm.value.priority,
  async (newValue, oldValue) => {
    // Only save if we're in edit mode, value actually changed, and it's different from original
    if (editingPriority.value && newValue !== oldValue && oldValue !== undefined) {
      await savePriority()
    }
  },
  { immediate: false }
)

// Watch for assignee changes and auto-save
watch(
  () => editForm.value.assigneeId,
  async (newValue, oldValue) => {
    // Only save if we're in edit mode, value actually changed, and it's different from original
    if (
      editingAssignee.value &&
      newValue !== oldValue &&
      oldValue !== undefined &&
      newValue !== originalAssigneeId.value
    ) {
      await saveAssignee()
    }
  },
  { immediate: false }
)

async function saveAssignee() {
  try {
    const { data } = await updateAssigneeMutation({
      taskId: taskGlobalId.value,
      assigneeId: editForm.value.assigneeId || null,
    })
    if (data?.updateTaskAssignee?.errors?.length) {
      throw new Error(data.updateTaskAssignee.errors.join(', '))
    }
    // Update task with new assignee
    const updatedTask = data.updateTaskAssignee.task
    // Explicitly update assignee to ensure reactivity
    task.value = {
      ...task.value,
      assignee: updatedTask.assignee,
      events: updatedTask.events || task.value.events,
    }
    // Update backend permission immediately from mutation response
    if (updatedTask.canUpdateAssignee !== undefined) {
      canUpdateAssigneeBackend.value = updatedTask.canUpdateAssignee
    }
    await refetch()
    await refetchEventsIfNeeded()

    // Show success banner
    const assigneeName = updatedTask.assignee
      ? updatedTask.assignee.name ||
        updatedTask.assignee.username ||
        `User ${updatedTask.assignee.id}`
      : s__('TaskDetail|Not assigned')
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Assignee updated'),
      message: sprintf(s__('TaskDetail|Assignee updated to %{assignee}'), {
        assignee: assigneeName,
      }),
    }

    // Auto-close after a short delay
    setTimeout(() => {
      editingAssignee.value = false
      originalAssigneeId.value = null
    }, 500)
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update assignee'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
    // Keep edit mode open on error so user can retry
  }
}

// Comment functions
function showCommentPreview() {
  if (!commentForm.value.body.trim()) return

  // Ensure URLs have schemas before preview
  ensureCommentUrlSchemas()

  // Use the same renderer as read-only view for WYSIWYG-consistent preview.
  previewContent.value = telegramMarkdownToHtml(commentForm.value.body, {
    processTaskMentions: true,
    taskMap: taskMentionsMap.value || {},
    buildTaskUrl: (taskId, projectId) => buildTaskUrl(taskId, projectId || props.projectId),
  })
  showPreviewModal.value = true
}

function ensureCommentUrlSchemas() {
  if (!commentForm.value.body) return

  let text = commentForm.value.body
  // Find all link patterns [text](url) and ensure URLs have schemas
  const updatedText = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
    // Check if URL already has a schema (http://, https://, mailto:, etc.)
    const hasSchema = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url.trim())
    const isRelativeUrl = url.trim().startsWith('/') || url.trim().startsWith('#')
    if (!hasSchema && !isRelativeUrl && url.trim()) {
      return `[${linkText}](https://${url.trim()})`
    }
    return match
  })

  if (updatedText !== text) {
    commentForm.value.body = updatedText
  }
}

async function submitComment() {
  if (!commentForm.value.body.trim()) return

  try {
    submittingComment.value = true
    commentError.value = ''

    // Ensure URLs have schemas before submitting
    ensureCommentUrlSchemas()

    if (editingComment.value) {
      // Update existing comment
      const { data } = await updateCommentMutation({
        id: editingComment.value.id,
        body: commentForm.value.body,
        removeAttachmentIds: removedInlineAttachmentIds(
          commentOriginalInlineAttachmentIds.value,
          commentUploadedInlineAttachmentIds.value,
          commentForm.value.body
        ),
      })

      if (data?.updateTaskComment?.errors?.length) {
        throw new Error(data.updateTaskComment.errors.join(', '))
      }

      // Update comment in local state (create new array to avoid readonly issues)
      const updatedComment = data.updateTaskComment.comment
      attachmentsCount.value = updatedComment?.task?.attachmentsCount ?? attachmentsCount.value

      // Check if it's a top-level comment
      const topLevelIndex = comments.value.findIndex(c => c.id === editingComment.value.id)
      if (topLevelIndex !== -1) {
        const newComments = [...comments.value]
        newComments[topLevelIndex] = { ...newComments[topLevelIndex], ...updatedComment }
        comments.value = newComments
      } else {
        // It's a reply - find parent and update reply
        const newComments = [...comments.value]
        for (let i = 0; i < newComments.length; i++) {
          const replyIndex = (newComments[i].replies || []).findIndex(
            r => r.id === editingComment.value.id
          )
          if (replyIndex !== -1) {
            const parent = { ...newComments[i] }
            const newReplies = [...(parent.replies || [])]
            newReplies[replyIndex] = updatedComment
            parent.replies = newReplies
            newComments[i] = parent
            comments.value = newComments
            break
          }
        }
      }

      editingComment.value = null
      commentOriginalInlineAttachmentIds.value = []
      commentUploadedInlineAttachmentIds.value = []

      // Show success banner for comment update
      taskAlert.value = {
        type: 'success',
        title: s__('TaskDetail|Comment updated'),
        message: s__('TaskDetail|The comment has been successfully updated.'),
      }
    } else {
      // Create new comment
      const { data } = await createCommentMutation({
        taskId: taskGlobalId.value,
        body: commentForm.value.body,
        repliedCommentId: replyingTo.value?.id || null,
        attachmentDraftId: commentAttachmentDraftId.value || null,
      })

      if (data?.createTaskComment?.errors?.length) {
        throw new Error(data.createTaskComment.errors.join(', '))
      }

      // Store whether this was a reply before clearing replyingTo
      const wasReply = !!replyingTo.value
      const parentCommentId = replyingTo.value?.id

      // Ensure comment has attachments array initialized
      const newComment = {
        ...data.createTaskComment.comment,
        attachments: data.createTaskComment.comment.attachments || [],
      }

      // Add new comment to local state (create new array to avoid readonly issues)
      if (replyingTo.value) {
        // It's a reply - append to parent's replies array since newest replies go at bottom
        const parentIndex = comments.value.findIndex(c => c.id === replyingTo.value.id)
        if (parentIndex !== -1) {
          const newComments = [...comments.value]
          const parent = { ...newComments[parentIndex] }
          // Initialize replies array if it doesn't exist
          if (!parent.replies) {
            parent.replies = []
            parent.hasMoreReplies = false
          }
          parent.replies = [...(parent.replies || []), newComment]
          newComments[parentIndex] = parent
          comments.value = newComments
        }
        replyingTo.value = null
      } else {
        // It's a top-level comment - append since newest comments go at bottom
        comments.value = [...comments.value, newComment]
      }

      // Upload files if any
      if (commentForm.value.files && commentForm.value.files.length > 0) {
        const uploadedAttachments = await uploadCommentFiles(
          data.createTaskComment.comment.id,
          commentForm.value.files
        )
        // Update the comment in local state with uploaded attachments
        // Transform backend response (snake_case) to frontend format (camelCase)
        if (uploadedAttachments.length > 0) {
          const transformedAttachments = uploadedAttachments.map(att => ({
            id: att.id,
            filename: att.filename,
            byteSize: att.byte_size,
            contentType: att.content_type,
            createdAt: att.created_at,
            downloadUrl: att.download_url,
            humanSize: att.human_size,
          }))

          const newComments = [...comments.value]
          if (wasReply && parentCommentId) {
            // It's a reply - find parent and update reply
            const parentIndex = newComments.findIndex(c => c.id === parentCommentId)
            if (parentIndex !== -1) {
              const parent = { ...newComments[parentIndex] }
              const replyIndex = (parent.replies || []).findIndex(r => r.id === newComment.id)
              if (replyIndex !== -1) {
                const newReplies = [...(parent.replies || [])]
                newReplies[replyIndex] = {
                  ...newReplies[replyIndex],
                  attachments: [
                    ...(newReplies[replyIndex].attachments || []),
                    ...transformedAttachments,
                  ],
                }
                parent.replies = newReplies
                newComments[parentIndex] = parent
                comments.value = newComments
              }
            }
          } else {
            // It's a top-level comment
            const commentIndex = newComments.findIndex(c => c.id === newComment.id)
            if (commentIndex !== -1) {
              newComments[commentIndex] = {
                ...newComments[commentIndex],
                attachments: [
                  ...(newComments[commentIndex].attachments || []),
                  ...transformedAttachments,
                ],
              }
              comments.value = newComments
            }
          }
        }
      }

      // Refetch task data to update subscribers count (when users are mentioned, they may be added as subscribers)
      // Use network-only to bypass cache and get fresh data
      await refetch({ fetchPolicy: 'network-only' })

      // Show success banner for comment creation
      taskAlert.value = {
        type: 'success',
        title: s__('TaskDetail|Comment added'),
        message: s__('TaskDetail|The comment has been successfully added.'),
      }
    }

    commentForm.value.body = ''
    commentForm.value.files = []
    commentAttachmentDraftId.value = null
    commentOriginalInlineAttachmentIds.value = []
    commentUploadedInlineAttachmentIds.value = []
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to submit comment'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  } finally {
    submittingComment.value = false
  }
}

const handleCommentFileSelect = event => {
  const files = Array.from(event.target.files || [])
  if (files.length > 0) {
    commentForm.value.files = [...(commentForm.value.files || []), ...files]
  }
  // Reset input so the same file can be selected again
  event.target.value = ''
}

const removeCommentFile = index => {
  commentForm.value.files.splice(index, 1)
}

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const uploadCommentFiles = async (commentId, files) => {
  if (!files || files.length === 0) return []

  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
  const uploadedAttachments = []
  const errors = []
  const jobIds = []

  // Upload all files and collect job IDs
  for (const file of files) {
    try {
      const formData = new FormData()
      formData.append('files[]', file)
      formData.append('comment_id', commentId)

      const response = await fetch('/attachments', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
        },
        body: formData,
        credentials: 'same-origin',
      })

      const data = await response.json()

      if (response.ok) {
        // Check if we got a job_id (background job) or attachments (immediate success)
        if (data.job_id) {
          jobIds.push({ jobId: data.job_id, fileName: file.name })
        } else if (data.attachments) {
          // Immediate success (shouldn't happen with new backend, but handle gracefully)
          uploadedAttachments.push(...data.attachments)
        }
      } else {
        const errorMessage = data.errors?.join(', ') || `Failed to upload ${file.name}`
        errors.push(errorMessage)
        console.error('Failed to upload comment attachment:', data.errors)
      }
    } catch (error) {
      const errorMessage = `Error uploading ${file.name}: ${error.message}`
      errors.push(errorMessage)
      console.error('Error uploading comment attachment:', error)
    }
  }

  // Poll for job completion if we have any job IDs
  if (jobIds.length > 0) {
    // Poll all jobs in parallel
    const pollPromises = jobIds.map(async ({ jobId, fileName }) => {
      const maxAttempts = 120 // Poll for up to 2 minutes
      let attempts = 0
      const pollInterval = 1000 // Poll every 1 second

      while (attempts < maxAttempts) {
        try {
          const response = await fetch(`/attachments/job_status?job_id=${jobId}`, {
            method: 'GET',
            credentials: 'same-origin',
          })

          const data = await response.json()

          if (data.status === 'completed') {
            // Job completed - attachments will be available after refetch
            // We don't have the attachment IDs here, but they'll be loaded when the comment is refetched
            return { success: true }
          } else if (data.status === 'failed') {
            const errorMessage = data.error || `Failed to upload ${fileName}`
            errors.push(errorMessage)
            return { success: false, error: errorMessage }
          } else if (data.status === 'retrying') {
            // Still retrying, continue polling
            attempts++
            await new Promise(resolve => setTimeout(resolve, pollInterval))
          } else {
            // Still processing, continue polling
            attempts++
            await new Promise(resolve => setTimeout(resolve, pollInterval))
          }
        } catch (error) {
          console.error('Error polling job status:', error)
          attempts++
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, pollInterval))
          } else {
            errors.push(`Failed to check upload status for ${fileName}`)
            return { success: false, error: error.message }
          }
        }
      }

      // Timeout
      errors.push(`Upload timed out for ${fileName}`)
      return { success: false, error: 'Timeout' }
    })

    await Promise.all(pollPromises)
  }

  // Show errors if any
  if (errors.length > 0) {
    taskAlert.value = {
      type: 'error',
      title: s__('Attachments|Failed to upload attachments'),
      message: errors.join('; '),
    }
  }

  // Note: With background jobs, attachments won't be immediately available
  // They'll appear after the comment is refetched from the server
  return uploadedAttachments
}

function startReply(comment) {
  replyingTo.value = comment
  editingComment.value = null
  commentForm.value.body = ''
  commentForm.value.files = []
  commentAttachmentDraftId.value = null
  commentOriginalInlineAttachmentIds.value = []
  commentUploadedInlineAttachmentIds.value = []
  nextTick(() => {
    focusTemplateRef(commentTextarea.value)
  })
}

function cancelReply() {
  replyingTo.value = null
  commentForm.value.body = ''
  commentForm.value.files = []
  commentAttachmentDraftId.value = null
  commentOriginalInlineAttachmentIds.value = []
  commentUploadedInlineAttachmentIds.value = []
  commentError.value = ''
}

function cancelEdit() {
  editingComment.value = null
  commentForm.value.body = ''
  commentForm.value.files = []
  commentAttachmentDraftId.value = null
  commentOriginalInlineAttachmentIds.value = []
  commentUploadedInlineAttachmentIds.value = []
  commentError.value = ''
}

function startEditComment(comment) {
  editingComment.value = comment
  replyingTo.value = null
  commentForm.value.body = comment.body
  commentForm.value.files = []
  commentAttachmentDraftId.value = null
  commentOriginalInlineAttachmentIds.value = extractInlineAttachmentIds(commentForm.value.body)
  commentUploadedInlineAttachmentIds.value = []
  commentError.value = ''
  nextTick(() => {
    focusTemplateRef(commentTextarea.value)
  })
}

const handleDeleteCommentAttachment = async attachment => {
  try {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ''
    const response = await fetch(`/attachments/${attachment.id}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken,
      },
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.errors?.join(', ') || 'Failed to delete attachment')
    }

    // Refetch comments to update the UI
    await refetch({ fetchPolicy: 'network-only' })
  } catch (error) {
    taskAlert.value = {
      type: 'error',
      title: s__('Attachments|Failed to delete attachment'),
      message: error.message || s__('Attachments|Unknown error'),
    }
  }
}

async function deleteComment(comment) {
  const confirmed = await showConfirm(
    s__('TaskDetail|Are you sure you want to delete this comment?')
  )
  if (!confirmed) return

  try {
    const { data } = await deleteCommentMutation({ id: comment.id })

    if (data?.deleteTaskComment?.errors?.length) {
      throw new Error(data.deleteTaskComment.errors.join(', '))
    }

    // Mark comment as deleted in local state (create new array to avoid readonly issues)
    // Check if it's a top-level comment
    const topLevelIndex = comments.value.findIndex(c => c.id === comment.id)
    if (topLevelIndex !== -1) {
      const newComments = [...comments.value]
      newComments[topLevelIndex] = { ...newComments[topLevelIndex], deleted: true }
      comments.value = newComments
    } else {
      // It's a reply - find parent and mark reply as deleted
      const newComments = [...comments.value]
      for (let i = 0; i < newComments.length; i++) {
        const replyIndex = (newComments[i].replies || []).findIndex(r => r.id === comment.id)
        if (replyIndex !== -1) {
          const parent = { ...newComments[i] }
          const newReplies = [...(parent.replies || [])]
          newReplies[replyIndex] = { ...newReplies[replyIndex], deleted: true }
          parent.replies = newReplies
          newComments[i] = parent
          comments.value = newComments
          break
        }
      }
    }

    // Success is implicit via UI state change (comment marked as deleted)
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to delete comment'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  }
}

async function loadEarlierComments() {
  if (loadingMoreComments.value || !commentsHasPreviousPage.value || !commentsStartCursor.value)
    return

  loadingMoreComments.value = true
  try {
    const result = await fetchMoreComments({
      variables: {
        projectId: props.projectId,
        taskId: taskGlobalId.value || props.taskId,
        first: 25,
        after: commentsStartCursor.value,
        last: null,
        before: null,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.project?.task?.comments) {
          return previousResult
        }

        // Previous comments are in newest-first order (as returned by backend)
        // We need to reverse them to oldest-first to match how we store comments
        // Use optional chaining to safely access nested properties
        const previousEdges = previousResult?.project?.task?.comments?.edges || []
        const previousComments = [...previousEdges].reverse()
        // New comments come in newest-first order from backend (recent ordering)
        // We reverse them to oldest-first order to match how we store comments
        const newComments = [...(fetchMoreResult.project.task.comments.edges || [])].reverse()

        // Prepend older comments (both are now in oldest-first order)
        // IMPORTANT: Use fetchMoreResult as the base - it contains all fields from the query
        // Only update the comments.edges field with our merged comments
        const updated = {
          ...fetchMoreResult,
          project: {
            ...fetchMoreResult.project,
            task: {
              ...fetchMoreResult.project.task,
              comments: {
                ...fetchMoreResult.project.task.comments,
                edges: [...newComments, ...previousComments],
              },
            },
          },
        }

        // Update local state from the merged result
        // Comments are stored in oldest-first order
        // IMPORTANT: Preserve ALL existing comments with their loaded replies
        // Only update comments that are in the GraphQL response, keep others as-is
        const existingCommentsMap = new Map()
        comments.value.forEach(comment => {
          existingCommentsMap.set(comment.id, comment)
        })

        // Process comments from GraphQL response
        const commentsFromResponse = updated.project.task.comments.edges.map(edge => {
          const comment = edge.node
          const existingComment = existingCommentsMap.get(comment.id)

          // Process replies from GraphQL response (initial 5)
          const repliesFromResponse =
            comment.replies && Array.isArray(comment.replies)
              ? [...comment.replies].reverse() // Reverse to oldest-first
              : []

          // Track initial replies count if not already tracked
          if (!loadedRepliesCount.value[comment.id]) {
            loadedRepliesCount.value[comment.id] = repliesFromResponse.length
          }

          // If we have an existing comment with loaded replies, preserve them
          // Otherwise use the replies from the response
          let finalReplies
          let finalHasMoreReplies

          if (existingComment) {
            // ALWAYS preserve existing replies if they exist
            // Check if we've loaded more replies (more than initial 5) or if replies array exists
            // Also check if loadedRepliesCount indicates we've loaded more
            const hasLoadedMoreReplies =
              existingComment.replies &&
              (existingComment.replies.length > repliesFromResponse.length ||
                (loadedRepliesCount.value[comment.id] && loadedRepliesCount.value[comment.id] > 5))

            if (hasLoadedMoreReplies) {
              // Preserve existing loaded replies
              finalReplies = existingComment.replies
            } else {
              // Use replies from response (initial 5)
              finalReplies = repliesFromResponse
            }

            // Preserve hasMoreReplies if it was set
            finalHasMoreReplies =
              existingComment.hasMoreReplies !== undefined
                ? existingComment.hasMoreReplies
                : comment.hasMoreReplies
          } else {
            // New comment, use replies from response
            finalReplies = repliesFromResponse
            finalHasMoreReplies = comment.hasMoreReplies
          }

          return {
            ...comment,
            replies: finalReplies,
            hasMoreReplies: finalHasMoreReplies,
          }
        })

        // Update comments array with merged data
        // This preserves loaded replies for existing comments

        // IMPORTANT: Preserve comments that are NOT in the GraphQL response
        // This can happen if the watch handler runs with a stale result
        const responseCommentIds = new Set(commentsFromResponse.map(c => c.id))
        const preservedComments = comments.value.filter(c => !responseCommentIds.has(c.id))

        // Combine comments: older ones (from response) first, then newer ones (preserved)
        // Comments are stored in oldest-first order for display
        // commentsFromResponse contains older comments, preservedComments contains newer ones
        comments.value = [...commentsFromResponse, ...preservedComments]
        // With recent ordering (newest first), hasNextPage indicates if there are older comments
        commentsHasPreviousPage.value = updated.project.task.comments.pageInfo?.hasNextPage || false
        commentsStartCursor.value = updated.project.task.comments.pageInfo?.endCursor || null

        // Set flag to skip next watch handler run to prevent overwriting
        skipNextWatch.value = true
        // Reset flag after a longer delay to allow Apollo cache to update
        // The watch handler might run with a stale result if we reset too quickly
        setTimeout(() => {
          skipNextWatch.value = false
        }, 500)

        return updated
      },
    })
  } catch (e) {
    error.value = e.message || 'Failed to load earlier comments'
  } finally {
    loadingMoreComments.value = false
  }
}

async function toggleSubscription() {
  try {
    subscribing.value = true

    if (subscribed.value) {
      // Unsubscribe
      const { data } = await unsubscribeFromTaskMutation({ taskId: taskGlobalId.value })

      if (data?.unsubscribeFromTask?.errors?.length) {
        throw new Error(data.unsubscribeFromTask.errors.join(', '))
      }

      subscribed.value = false
      subscribersCount.value = Math.max(0, subscribersCount.value - 1)
      // Keep subscribers list in sync even when modal is closed.
      if (currentUserId.value) {
        subscribers.value = subscribers.value.filter(s => s.id !== currentUserId.value)
      }
    } else {
      // Subscribe
      const { data } = await subscribeToTaskMutation({ taskId: taskGlobalId.value })

      if (data?.subscribeToTask?.errors?.length) {
        throw new Error(data.subscribeToTask.errors.join(', '))
      }

      subscribed.value = true
      subscribersCount.value += 1
      // Keep subscribers list in sync even when modal is closed.
      if (currentUserId.value) {
        const currentUser = projectMembers.value.find(m => m.id === currentUserId.value)
        if (currentUser && !subscribers.value.find(s => s.id === currentUserId.value)) {
          subscribers.value = [...subscribers.value, currentUser]
        }
      }
    }

    await refetch()

    // Success is implicit via UI state change (subscribed/unsubscribed)
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update subscription'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  } finally {
    subscribing.value = false
  }
}

async function loadSubscribers() {
  if (loadingSubscribers.value) return

  try {
    loadingSubscribers.value = true
    const { data } = await apolloClient.query({
      query: GET_TASK_SUBSCRIBERS,
      variables: {
        projectId: props.projectId,
        taskId: taskGlobalId.value || props.taskId,
      },
      fetchPolicy: 'network-only',
    })

    if (data?.project?.task) {
      subscribers.value = data.project.task.subscribers || []
      subscribersCount.value = data.project.task.subscribersCount || 0
      canSubscribeOthers.value = data.project.task.canSubscribeOthers || false
    }
  } catch (e) {
    console.error('Failed to load subscribers:', e)
    error.value = e.message || 'Failed to load subscribers'
  } finally {
    loadingSubscribers.value = false
  }
}

// Handle tasks cloned successfully
function handleTasksCloned(clonedTasks) {
  if (clonedTasks && clonedTasks.length > 0) {
    // Show success message
    const message = s__('TaskClone|Successfully created %{count} cloned tasks').replace(
      '%{count}',
      clonedTasks.length
    )
    alert(message)
  }
}

// Watch for modal opening to fetch subscribers
watch(showSubscribersModal, isOpen => {
  if (isOpen) {
    loadSubscribers()
    selectedUserToSubscribe.value = null
  }
})

// Computed: users available to subscribe (project members who are not yet subscribed and not author/assignee)
const availableUsersToSubscribe = computed(() => {
  if (!projectMembers.value || !subscribers.value) return []

  const subscriberIds = new Set(subscribers.value.map(s => s.id))
  const authorId = task.value?.author?.id
  const assigneeId = task.value?.assignee?.id

  return projectMembers.value.filter(member => {
    // Exclude already subscribed users
    if (subscriberIds.has(member.id)) return false
    // Exclude author and assignee (they receive notifications automatically)
    if (member.id === authorId || member.id === assigneeId) return false
    return true
  })
})

// Subscribe another user to the task
async function subscribeUserToTask() {
  if (!selectedUserToSubscribe.value || subscribingUser.value) return

  try {
    subscribingUser.value = true

    const { data } = await subscribeUserToTaskMutation({
      taskId: taskGlobalId.value,
      userId: selectedUserToSubscribe.value,
    })

    if (data?.subscribeUserToTask?.errors?.length) {
      throw new Error(data.subscribeUserToTask.errors.join(', '))
    }

    // Reload subscribers list
    await loadSubscribers()
    // Refetch task to update availableCloneOptions
    await refetch()
    selectedUserToSubscribe.value = null
  } catch (e) {
    console.error('Failed to subscribe user:', e)
    alert(
      s__('TaskDetail|Failed to subscribe user') +
        ': ' +
        (e.message || s__('TaskDetail|Unknown error'))
    )
  } finally {
    subscribingUser.value = false
  }
}

// Unsubscribe a user from the task
async function unsubscribeUserFromTask(userId) {
  if (subscribingUser.value) return

  try {
    subscribingUser.value = true

    const { data } = await unsubscribeUserFromTaskMutation({
      taskId: taskGlobalId.value,
      userId: userId,
    })

    if (data?.unsubscribeUserFromTask?.errors?.length) {
      throw new Error(data.unsubscribeUserFromTask.errors.join(', '))
    }

    // Reload subscribers list
    await loadSubscribers()
    await refetch()
  } catch (e) {
    console.error('Failed to unsubscribe user:', e)
    alert(
      s__('TaskDetail|Failed to unsubscribe user') +
        ': ' +
        (e.message || s__('TaskDetail|Unknown error'))
    )
  } finally {
    subscribingUser.value = false
  }
}

// Confidentiality functions
function startEditConfidentiality() {
  editForm.value.confidential = task.value?.confidential || false
  selectedWhitelistedViewerIds.value = (task.value?.whitelistedViewers || []).map(v => v.id)
  showConfidentialityModal.value = true
}

function cancelEditConfidentiality() {
  showConfidentialityModal.value = false
}

async function saveConfidentiality() {
  try {
    const currentViewerIds = (task.value?.whitelistedViewers || []).map(v => v.id)

    const { data } = await updateConfidentialityMutation({
      taskId: taskGlobalId.value,
      confidential: editForm.value.confidential,
    })

    if (data?.updateTaskConfidentiality?.errors?.length) {
      throw new Error(data.updateTaskConfidentiality.errors.join(', '))
    }

    task.value = { ...task.value, ...data.updateTaskConfidentiality.task }

    if (editForm.value.confidential) {
      const toAdd = selectedWhitelistedViewerIds.value.filter(id => !currentViewerIds.includes(id))
      const toRemove = currentViewerIds.filter(
        id => !selectedWhitelistedViewerIds.value.includes(id)
      )

      for (const viewerId of toRemove) {
        const { data: removeData } = await removeViewerMutation({
          taskId: taskGlobalId.value,
          viewerId,
        })

        if (removeData?.removeConfidentialTaskViewer?.errors?.length) {
          throw new Error(removeData.removeConfidentialTaskViewer.errors.join(', '))
        }
      }

      for (const viewerId of toAdd) {
        const { data: addData } = await addViewerMutation({
          taskId: taskGlobalId.value,
          viewerId,
        })

        if (addData?.addConfidentialTaskViewer?.errors?.length) {
          throw new Error(addData.addConfidentialTaskViewer.errors.join(', '))
        }
      }
    }

    await refetch()
    await refetchEventsIfNeeded()
    showConfidentialityModal.value = false

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Confidentiality updated'),
      message: s__('TaskDetail|The confidentiality setting has been successfully updated.'),
    }
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update confidentiality'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  }
}

// Archive/Unarchive functions
async function toggleArchived() {
  try {
    const newArchivedValue = !task.value?.archived
    const { data } = await updateArchivedMutation({
      taskId: taskGlobalId.value,
      archived: newArchivedValue,
    })

    if (data?.updateTaskArchived?.errors?.length) {
      throw new Error(data.updateTaskArchived.errors.join(', '))
    }

    task.value = { ...task.value, ...data.updateTaskArchived.task }
    await refetchEventsIfNeeded()

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: newArchivedValue ? s__('TaskDetail|Task archived') : s__('TaskDetail|Task unarchived'),
      message: newArchivedValue
        ? s__('TaskDetail|The task has been successfully archived.')
        : s__('TaskDetail|The task has been successfully unarchived.'),
    }
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to update archived status'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  }
}

function toggleArchivedInfo() {
  showArchivedInfo.value = !showArchivedInfo.value
}

// Close popup when clicking outside
function handleClickOutside(event) {
  if (showTaskMenu.value && taskMenuRef.value && !taskMenuRef.value.contains(event.target)) {
    showTaskMenu.value = false
  }

  if (
    showArchivedInfo.value &&
    infoIconRef.value &&
    infoPopupRef.value &&
    !infoIconRef.value.contains(event.target) &&
    !infoPopupRef.value.contains(event.target)
  ) {
    showArchivedInfo.value = false
  }
}

// Grade functions
const editingGradeItem = ref(null)

function startEditGrade(gradeItem = null) {
  editingGradeItem.value = gradeItem
  if (gradeItem) {
    // Editing existing grade
    gradeForm.value = {
      gradeId: gradeItem.grade?.id || null,
      confidential: gradeItem.confidential || false,
    }
  } else if (task.value?.grade) {
    // Editing current user's grade
    gradeForm.value = {
      gradeId: task.value.grade.grade?.id || null,
      confidential: task.value.grade.confidential || false,
    }
  } else {
    // Creating new grade
    gradeForm.value = {
      gradeId: null,
      confidential: false,
    }
  }
  editingGrade.value = true
  gradeError.value = ''
}

function cancelEditGrade() {
  editingGrade.value = false
  editingGradeItem.value = null
  gradeError.value = ''
}

async function saveGrade() {
  if (!gradeForm.value.gradeId) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Validation error'),
      message: s__('TaskDetail|Please select a grade'),
    }
    return
  }

  try {
    submittingGrade.value = true

    // Check if we're updating an existing grade or creating a new one
    const existingGrade = editingGradeItem.value || task.value?.grade

    if (existingGrade) {
      // Update existing grade
      const { data } = await updateTaskGradeMutation({
        taskId: taskGlobalId.value,
        gradeId: gradeForm.value.gradeId,
        confidential: gradeForm.value.confidential,
      })

      if (data?.updateTaskGrade?.errors?.length) {
        throw new Error(data.updateTaskGrade.errors.join(', '))
      }
    } else {
      // Create new grade
      const { data } = await createTaskGradeMutation({
        taskId: taskGlobalId.value,
        gradeId: gradeForm.value.gradeId,
        confidential: gradeForm.value.confidential,
      })

      if (data?.createTaskGrade?.errors?.length) {
        throw new Error(data.createTaskGrade.errors.join(', '))
      }
    }

    editingGrade.value = false
    editingGradeItem.value = null
    await refetch()
    await refetchEventsIfNeeded()

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Grade saved'),
      message: s__('TaskDetail|The grade has been successfully saved.'),
    }
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to save grade'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  } finally {
    submittingGrade.value = false
  }
}

async function deleteGrade(gradeItem = null) {
  // If gradeItem is provided, use it; otherwise use the current user's grade
  const gradeToDelete = gradeItem || task.value?.grade
  if (!gradeToDelete) return

  const confirmed = await showConfirm(s__('TaskDetail|Are you sure you want to remove this grade?'))
  if (!confirmed) return

  try {
    submittingGrade.value = true
    gradeError.value = ''

    const { data } = await destroyTaskGradeMutation({
      taskId: taskGlobalId.value,
    })

    if (data?.destroyTaskGrade?.errors?.length) {
      throw new Error(data.destroyTaskGrade.errors.join(', '))
    }

    await refetch()
    await refetchEventsIfNeeded()
  } catch (e) {
    gradeError.value = e.message || 'Failed to delete grade'
    alert('Failed to delete grade: ' + (e.message || 'Unknown error'))
  } finally {
    submittingGrade.value = false
  }
}

// Task Category change functions
function startEditTaskCategory() {
  selectedTaskCategoryId.value = task.value?.taskCategory?.id || null
  editingTaskCategory.value = true
  showTaskCategoryModal.value = true
  categoryChangeError.value = ''
}

function cancelEditTaskCategory() {
  editingTaskCategory.value = false
  showTaskCategoryModal.value = false
  selectedTaskCategoryId.value = null
  categoryChangeError.value = ''
  pendingCategoryChange.value = null
}

async function handleTaskCategoryChange() {
  if (
    !selectedTaskCategoryId.value ||
    selectedTaskCategoryId.value === task.value?.taskCategory?.id
  ) {
    cancelEditTaskCategory()
    return
  }

  const newCategory = projectTaskCategories.value.find(c => c.id === selectedTaskCategoryId.value)
  if (!newCategory) {
    categoryChangeError.value = s__('TaskDetail|Task category not found')
    return
  }

  const oldCategory = task.value?.taskCategory
  const oldWorkflow = oldCategory?.workflow
  const newWorkflow = newCategory?.workflow

  // Check if workflows differ
  const workflowsDiffer = oldWorkflow?.id !== newWorkflow?.id

  // Check if grades will be lost
  const oldGradingScale = oldCategory?.gradingScale
  const newGradingScale = newCategory?.gradingScale
  const hasGrades = task.value?.grades && task.value.grades.length > 0
  const willLoseGrades = hasGrades && oldGradingScale?.id !== newGradingScale?.id

  // Store pending change BEFORE loading to ensure watch handler has the correct category ID
  pendingCategoryChange.value = {
    taskCategoryId: selectedTaskCategoryId.value,
    workflowsDiffer,
    willLoseGrades,
  }

  // If workflows differ, load workflow statuses and show modal
  if (workflowsDiffer) {
    // Clear previous workflow statuses
    newWorkflowStatuses.value = []

    // Load workflow statuses for the new category's workflow
    if (newWorkflow?.id) {
      loadingWorkflowStatuses.value = true
      try {
        await loadWorkflowStatuses()
        // The watch handler should populate newWorkflowStatuses, but also manually set it here
        // to ensure it's set even if watch doesn't trigger (e.g., cached data)
        if (workflowStatusesResult.value?.project?.taskCategories) {
          const category = workflowStatusesResult.value.project.taskCategories.find(
            c => c.id === selectedTaskCategoryId.value
          )
          if (category?.workflow?.statuses) {
            newWorkflowStatuses.value = category.workflow.statuses
          } else {
            newWorkflowStatuses.value = []
          }
        }
      } catch (e) {
        console.error('Failed to load workflow statuses:', e)
        categoryChangeError.value = e.message || 'Failed to load workflow statuses'
        return
      } finally {
        loadingWorkflowStatuses.value = false
      }
    }
    showTaskCategoryModal.value = false
    showWorkflowStatusModal.value = true
  } else if (willLoseGrades) {
    // If grades will be lost, show warning modal
    showTaskCategoryModal.value = false
    showGradeWarningModal.value = true
  } else {
    // No special requirements, proceed directly
    showTaskCategoryModal.value = false
    await executeCategoryChange(null)
  }
}

function selectWorkflowStatus(status) {
  if (!pendingCategoryChange.value) return

  // Store selected workflow status
  pendingCategoryChange.value.workflowStatusId = status.id
  showWorkflowStatusModal.value = false

  // If grades will be lost, show warning modal
  if (pendingCategoryChange.value.willLoseGrades) {
    showGradeWarningModal.value = true
  } else {
    // Proceed with workflow status
    executeCategoryChange(status.id)
  }
}

function confirmCategoryChangeWithGradeLoss() {
  if (!pendingCategoryChange.value) return

  showGradeWarningModal.value = false

  // Use stored workflow status ID if available
  const workflowStatusId = pendingCategoryChange.value.workflowStatusId || null

  executeCategoryChange(workflowStatusId)
}

function closeWorkflowStatusModal() {
  showWorkflowStatusModal.value = false
  newWorkflowStatuses.value = []
  cancelEditTaskCategory()
}

function closeGradeWarningModal() {
  showGradeWarningModal.value = false
  cancelEditTaskCategory()
}

async function executeCategoryChange(workflowStatusId) {
  if (!pendingCategoryChange.value) return

  try {
    changingCategory.value = true
    categoryChangeError.value = ''

    const { data } = await changeTaskCategoryMutation({
      taskId: taskGlobalId.value,
      taskCategoryId: pendingCategoryChange.value.taskCategoryId,
      workflowStatusId: workflowStatusId,
    })

    if (data?.changeTaskCategory?.errors?.length) {
      // Show error banner
      taskAlert.value = {
        type: 'error',
        title: s__('TaskDetail|Failed to change task category'),
        message: data.changeTaskCategory.errors.join(', '),
      }
      return
    }

    if (data?.changeTaskCategory?.requiresWorkflowStatus) {
      // Should not happen if we handled it correctly, but show modal if needed
      // Only show if we're not already showing it and we have a workflow status ID (meaning user already selected)
      if (!showWorkflowStatusModal.value && !workflowStatusId) {
        const newCategory = projectTaskCategories.value.find(
          c => c.id === pendingCategoryChange.value.taskCategoryId
        )
        if (newCategory?.workflow?.statuses) {
          newWorkflowStatuses.value = newCategory.workflow.statuses
          showWorkflowStatusModal.value = true
        }
      }
      return
    }

    // Success - update task data
    if (data?.changeTaskCategory?.task) {
      task.value = {
        ...task.value,
        ...data.changeTaskCategory.task,
      }
    }

    await refetch()
    await refetchEventsIfNeeded()

    // Close edit mode
    editingTaskCategory.value = false
    showTaskCategoryModal.value = false
    selectedTaskCategoryId.value = null
    pendingCategoryChange.value = null

    // Show success banner
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Task category updated'),
      message: s__('TaskDetail|The task category has been successfully updated.'),
    }
  } catch (e) {
    // Show error banner
    taskAlert.value = {
      type: 'error',
      title: s__('TaskDetail|Failed to change task category'),
      message: e.message || s__('TaskDetail|Unknown error'),
    }
  } finally {
    changingCategory.value = false
  }
}

function updateFullWidthCapabilities() {
  if (typeof window === 'undefined') return
  canToggleFullWidth.value = window.innerWidth >= 768
  isFullWidth.value = canToggleFullWidth.value && readFullWidthPreference()
}

function toggleFullWidth() {
  if (!canToggleFullWidth.value) return
  isFullWidth.value = !isFullWidth.value
  writeFullWidthPreference(isFullWidth.value)
}

function toggleTaskMenu() {
  showTaskMenu.value = !showTaskMenu.value
}

function closeTaskMenu() {
  showTaskMenu.value = false
}

function handleTaskMenuToggleFullWidth() {
  closeTaskMenu()
  toggleFullWidth()
}

function handleTaskMenuClone() {
  closeTaskMenu()
  showCloneModal.value = true
}

async function handleTaskMenuShare() {
  closeTaskMenu()
  const result = await copyShareMessage()
  if (result === 'copied') {
    taskAlert.value = {
      type: 'success',
      title: s__('TaskDetail|Copied!'),
      message: s__('TaskDetail|Share link'),
    }
  }
}

// Setup click-outside handler for archived info popup
onMounted(() => {
  updateFullWidthCapabilities()
  window.addEventListener('resize', updateFullWidthCapabilities)
  document.addEventListener('click', handleClickOutside)
  // Setup click-outside handler for assignee edit
  document.addEventListener('click', handleAssigneeClickOutside)
  // Setup click-outside handler for priority edit
  document.addEventListener('click', handlePriorityClickOutside)
})

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateFullWidthCapabilities)
  }
  document.removeEventListener('click', handleClickOutside)
  // Cleanup click-outside handler for assignee edit
  document.removeEventListener('click', handleAssigneeClickOutside)
  // Cleanup click-outside handler for priority edit
  document.removeEventListener('click', handlePriorityClickOutside)
})
</script>

<style src="../../../styles/forms.css"></style>

<style scoped>
.card.full-width {
  max-width: 100% !important;
  width: 100%;
  margin: 0;
  border-radius: 0;
}

.task-show-breadcrumbs-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.task-show-breadcrumbs {
  min-width: 0;
  flex: 1 1 auto;
}

.task-show-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.task-show-top-bar-left {
  min-width: 0;
}

.task-show-top-bar .back-button-container {
  margin-bottom: 0;
}

.task-show-top-bar .back-button {
  margin-bottom: 0;
  padding: 4px 8px;
  border-radius: 8px;
  gap: 6px;
  font-size: 14px;
  font-weight: 400;
}

.task-show-top-bar .back-arrow-icon {
  width: 14px;
  height: 14px;
}

.task-menu {
  position: relative;
  flex: 0 0 auto;
}

.task-menu-trigger {
  min-height: 32px;
  padding: 6px 10px;
  gap: 6px;
  border-radius: 8px;
}

.task-menu-icon-burger {
  display: none !important;
}

.task-menu-trigger .task-menu-icon-chevron {
  display: inline-flex !important;
}

.task-menu-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  min-width: 220px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
  z-index: 30;
  overflow: hidden;
}

.task-menu-item {
  width: 100%;
  display: block;
  text-align: left;
  background: #fff;
  border: 0;
  color: #333;
  padding: 10px 12px;
  font-size: 14px;
  cursor: pointer;
}

.task-menu-item:hover:not(:disabled) {
  background: #f5f5f5;
}

.success {
  color: #2d7a2d;
  background: #e8f5e9;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #81c784;
}

.error {
  color: #c62828;
  background: #ffebee;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ef5350;
}

.feedback-message {
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 13px;
}

.feedback-message.success {
  color: #2d7a2d;
  background: #e8f5e9;
  border: 1px solid #81c784;
}

.feedback-message.error {
  color: #c62828;
  background: #ffebee;
  border: 1px solid #ef5350;
}

.timeline {
  margin-top: 12px;
  position: relative;
}

.timeline-item {
  display: flex;
  gap: 12px;
  padding: 12px;
  padding-bottom: 16px;
  position: relative;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.timeline-item:nth-child(even) {
  background-color: #fafafa;
}

.timeline-item:nth-child(odd) {
  background-color: transparent;
}

.timeline-dot {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #2196f3;
  border: 3px solid #fff;
  box-shadow: 0 0 0 2px #e0e0e0;
  align-self: center;
}

.timeline-content {
  flex: 1;
  min-width: 0;
}

.task-field-label-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
}

.task-field-label-row label {
  margin-bottom: 0;
}

.confidentiality-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.confidentiality-state-icon {
  flex-shrink: 0;
}

.confidentiality-state-icon-lock {
  color: #1565c0;
}

.confidentiality-state-icon-unlock {
  color: #6b7280;
}

.confidentiality-edit-btn {
  margin-left: 4px;
}

.confidential-whitelist-summary {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.confidential-whitelist-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 767px) {
  .confidentiality-edit-btn {
    margin-left: auto;
  }
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 4px;
}

.event-time {
  font-size: 12px;
  color: #757575;
  white-space: nowrap;
}

.event-description {
  color: #424242;
  margin-bottom: 2px;
  font-size: 14px;
}

.event-actor {
  font-size: 12px;
  color: #757575;
}

.label-badge {
  display: inline-block;
  padding: 4px 12px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
}

.label-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  margin-top: 8px;
}

.label-checkbox {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.label-checkbox input[type='checkbox'] {
  cursor: pointer;
}

.grade-item {
  border-radius: 4px;
  transition: background-color 0.2s;
}

.grade-item:nth-child(even) {
  background-color: #f0f0f0;
}

.grade-item:nth-child(odd) {
  background-color: #f8f9fa;
}

.grade-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 4px;
  background: #e3f2fd;
  color: #1976d2;
  border: 1px solid #90caf9;
}

.grade-badge.grade-confidential {
  background: #fff3e0;
  color: #e65100;
  border-color: #ffb74d;
}

.confidential-indicator {
  font-size: 12px;
}

/* Button icon styles inherit from forms.css */

.info-icon {
  font-size: 16px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.2s;
  user-select: none;
}

.info-icon:hover {
  opacity: 1;
}

.info-popup {
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  padding: 10px 12px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  font-size: 13px;
  color: #495057;
  line-height: 1.5;
  max-width: 280px;
  z-index: 1000;
  white-space: normal;
}

.info-popup::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 20px;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid #fff;
}

.info-popup::before {
  content: '';
  position: absolute;
  top: 100%;
  right: 19px;
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid #ddd;
  z-index: -1;
}

.empty-state {
  text-align: center;
  padding: 32px 16px;
  color: #757575;
  font-style: italic;
}

/* Comment Form */
.comment-form {
  padding: 16px;
  background: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 16px;
}

.reply-form {
  margin-top: 12px;
  margin-bottom: 12px;
  background: #fafafa;
  border-left: 3px solid #2196f3;
}

.replying-to {
  padding: 8px 12px;
  background: #e3f2fd;
  margin-bottom: 8px;
  font-size: 13px;
  color: #1976d2;
  font-weight: 500;
}

/* Comments List */
.comments-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.comment-thread {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.replies {
  margin-left: 32px;
  padding-left: 16px;
  border-left: 2px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.task-description-readonly {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.task-description {
  flex: 1 1 auto;
  min-width: 360px;
  max-width: 100%;
}

.description-label-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.description-label-row > label {
  margin-bottom: 0;
}

.task-description-actions,
.task-comment-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-start;
}

@media (max-width: 768px) {
  .task-description {
    min-width: 0;
  }

  .task-description-actions,
  .task-comment-actions {
    width: 100%;
    flex-wrap: wrap;
    justify-content: stretch;
  }

  .task-description-actions > .btn-sm,
  .task-comment-actions > .btn-sm {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
    justify-content: center;
    text-align: center;
  }
}

/* Transition buttons */
.transition-button {
  display: flex;
  align-items: center;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  transition: all 0.15s ease-in-out;
  background: white;
  position: relative;
}

.transition-button:hover:not(:disabled) {
  border-color: #2aabee;
  box-shadow: 0 0 0 3px rgba(42, 171, 238, 0.1);
}

.transition-button:active:not(:disabled) {
  background: #f3f4f6;
}

.transition-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.transition-disabled-checklist {
  border-color: #dc3545 !important;
  background: #fff5f5 !important;
}

.transition-disabled-checklist:hover {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1) !important;
}

.checklist-lock-icon {
  margin-left: 6px;
  font-size: 12px;
  opacity: 0.8;
}

.transition-name {
  background: #e9ecef;
  color: #495057;
  padding: 8px 16px 8px 12px;
  white-space: nowrap;
  position: relative;
}

.transition-name::after {
  content: '';
  position: absolute;
  right: -10px;
  top: 0;
  bottom: 0;
  width: 0;
  height: 0;
  border-top: 16px solid transparent;
  border-bottom: 16px solid transparent;
  border-left: 10px solid #e9ecef;
  z-index: 1;
}

.transition-status {
  background: #f8f9fa;
  color: #212529;
  padding: 8px 12px 8px 22px;
  white-space: nowrap;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.transition-status::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  height: 0;
  border-top: 16px solid transparent;
  border-bottom: 16px solid transparent;
  border-left: 10px solid #dee2e6;
  z-index: 0;
}

.title-container {
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  margin-left: -12px;
  margin-right: -12px;
  width: calc(100% + 24px);
  box-sizing: border-box;
}

.title-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.title-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.title-actions-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-main {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.title-chip {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.3;
  font-weight: 500;
  background: #e5e7eb;
  color: #4b5563;
  white-space: nowrap;
}

button.title-chip {
  min-height: 0;
  min-width: 0;
  gap: 0;
  justify-content: center;
}

.task-id-chip {
  background: #e5e7eb;
  color: #4b5563;
}

.task-category-chip {
  background: #dbeafe;
  color: #1d4ed8;
  border: none;
}

.task-due-chip {
  background: #eef2ff;
  color: #4338ca;
  border: none;
}

.task-priority-chip {
  background: #fff7ed;
  color: #9a3412;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

button.task-category-chip.is-clickable {
  cursor: pointer;
  box-shadow:
    0 1px 2px rgba(29, 78, 216, 0.16),
    0 0 0 1px rgba(29, 78, 216, 0.1);
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

button.task-category-chip.is-clickable:hover:not(:disabled) {
  background: #bfdbfe;
  box-shadow:
    0 2px 6px rgba(29, 78, 216, 0.2),
    0 0 0 1px rgba(29, 78, 216, 0.14);
  transform: translateY(-1px);
}

button.task-category-chip.is-clickable:active:not(:disabled) {
  transform: translateY(0);
}

button.task-due-chip.is-clickable {
  cursor: pointer;
  box-shadow:
    0 1px 2px rgba(67, 56, 202, 0.16),
    0 0 0 1px rgba(67, 56, 202, 0.1);
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

button.task-due-chip.is-clickable:hover:not(:disabled) {
  background: #e0e7ff;
  box-shadow:
    0 2px 6px rgba(67, 56, 202, 0.2),
    0 0 0 1px rgba(67, 56, 202, 0.14);
  transform: translateY(-1px);
}

button.task-due-chip.is-clickable:active:not(:disabled) {
  transform: translateY(0);
}

button.task-priority-chip.is-clickable {
  cursor: pointer;
  gap: 4px;
  box-shadow:
    0 1px 2px rgba(154, 52, 18, 0.16),
    0 0 0 1px rgba(154, 52, 18, 0.1);
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

button.task-priority-chip.is-clickable:hover:not(:disabled) {
  background: #ffedd5;
  box-shadow:
    0 2px 6px rgba(154, 52, 18, 0.2),
    0 0 0 1px rgba(154, 52, 18, 0.14);
  transform: translateY(-1px);
}

button.task-priority-chip.is-clickable:active:not(:disabled) {
  transform: translateY(0);
}

button.title-chip.is-clickable.action-required-chip {
  background: #2aabee;
  color: #fff;
  border: 1px solid #2aabee;
  box-shadow:
    0 1px 2px rgba(42, 171, 238, 0.2),
    0 0 0 1px rgba(42, 171, 238, 0.18);
}

button.title-chip.is-clickable.action-required-chip:hover:not(:disabled),
button.title-chip.is-clickable.action-required-chip:focus:not(:disabled),
button.title-chip.is-clickable.action-required-chip:focus-visible:not(:disabled),
button.title-chip.is-clickable.action-required-chip:active:not(:disabled) {
  background: #229ed9 !important;
  color: #fff !important;
  border-color: #229ed9 !important;
  box-shadow:
    0 2px 6px rgba(42, 171, 238, 0.24),
    0 0 0 1px rgba(42, 171, 238, 0.2);
}

.title-text-with-edit {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 280px;
  min-width: 160px;
}

.task-title-edit-btn {
  flex: 0 0 auto;
}

.title-secondary {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.title-secondary-item {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 28px;
}

.title-secondary-label {
  color: #6b7280;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.title-secondary-value-group {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.title-secondary-editor {
  min-width: 220px;
}

.title-secondary-item-due {
  margin-left: auto;
}

.title-status-button {
  flex-shrink: 0;
  border: 1px solid rgba(17, 24, 39, 0.1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease,
    filter 0.15s ease;
}

.title-status-button:hover:not(:disabled) {
  box-shadow:
    0 2px 8px rgba(42, 171, 238, 0.28),
    0 0 0 1px rgba(42, 171, 238, 0.18);
  transform: translateY(-1px);
  filter: saturate(1.03);
}

.title-status-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.14);
}

@media (max-width: 768px) {
  .subscribe-text {
    display: none;
  }

  .title-container {
    padding: 10px !important;
    margin-left: -12px;
    margin-right: -12px;
    width: calc(100% + 24px);
  }

  .task-menu-trigger > .task-menu-icon-chevron {
    display: none !important;
  }

  .task-menu-trigger > .task-menu-icon-burger {
    display: inline-flex !important;
  }

  .title-header {
    justify-content: flex-end;
  }

  .title-actions-buttons {
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .title-main {
    gap: 8px;
  }

  .title-chip {
    font-size: 11px;
    padding: 2px 8px;
  }

  button.title-chip {
    min-height: 0;
    min-width: 0;
    gap: 0;
    padding: 2px 8px;
  }

  .title-text-with-edit {
    flex-basis: 100%;
    min-width: 0;
  }

  .title-secondary {
    gap: 8px;
    flex-direction: column;
    align-items: stretch;
  }

  .title-secondary-item {
    width: 100%;
  }

  .title-secondary-item-due {
    margin-left: 0;
  }

  .title-secondary-editor {
    min-width: 0;
    width: 100%;
  }

  .title-status-button {
    width: 100%;
    justify-content: center;
  }

  .task-title {
    font-size: 1.05rem !important;
  }

  .transition-button {
    font-size: 12px;
  }

  .transition-name,
  .transition-status {
    padding: 8px 10px;
  }

  .transition-arrow {
    padding: 8px 6px;
  }
}

/* Status clickable - now uses btn-secondary, just need to style status-text */
.status-text {
  flex: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.title-status-content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0 auto;
}

.title-status-button .status-text {
  flex: 0 0 auto;
  display: inline-block;
}

/* Status dot in status button */
.btn-secondary .status-dot {
  flex-shrink: 0;
}

.status-icon {
  font-size: 14px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.status-clickable:hover .status-icon {
  opacity: 1;
}

/* Modal styles */
/* Component-specific: z-index, padding, and max-width overrides */
.modal-overlay {
  z-index: 10000;
  padding: 20px;
}

.modal-content {
  max-width: 500px;
}

.share-link-btn {
  position: relative;
}

.share-link-btn .share-copy-feedback {
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translateX(-50%);
  margin-top: 4px;
  padding: 2px 8px;
  font-size: 0.75rem;
  white-space: nowrap;
  background: #333;
  color: #fff;
  border-radius: 4px;
  pointer-events: none;
  z-index: 1;
}

.modal-body input[type='color'] {
  width: 100%;
  height: 38px;
  padding: 2px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  cursor: pointer;
}

.modal-body .row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;
}

/* Alternating row colors for task information rows */
/* Count all rows, including conditional ones */
.task-info-container > .row:nth-of-type(2n) {
  background-color: #fafafa;
  padding: 12px;
  border-radius: 4px;
  margin-left: -12px;
  margin-right: -12px;
  transition: background-color 0.2s;
}

.task-info-container > .row:nth-of-type(2n + 1) {
  background-color: transparent;
}

/* Reset counter after separator and apply to rows after it */
.task-info-container > div[style*='border-top'] ~ .row:nth-of-type(2n) {
  background-color: #fafafa;
  padding: 12px;
  border-radius: 4px;
  margin-left: -12px;
  margin-right: -12px;
  transition: background-color 0.2s;
}

.task-info-container > div[style*='border-top'] ~ .row:nth-of-type(2n + 1) {
  background-color: transparent;
}

.modal-body .row label {
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 500;
}

.modal-body .row input[type='text'] {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

.task-category-select-modal {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

/* Modal transition buttons - styled as secondary buttons with custom layout */
.transition-button-modal {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 12px 24px;
}

/* Status selection buttons (without transition-name) should fit content */
.transition-button-modal:not(:has(.transition-name)) {
  width: auto;
  min-width: fit-content;
  justify-content: flex-start;
}

.transition-button-modal:not(:has(.transition-name)) .transition-status {
  margin-left: 0;
}

.transition-button-modal .transition-name {
  background: transparent;
  color: inherit;
  padding: 0;
  margin-right: 12px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 0 1 auto;
  min-width: 0;
}

.transition-button-modal .transition-name::after {
  display: none;
}

.transition-button-modal .transition-status {
  background: transparent;
  color: inherit;
  padding: 0;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 1 auto;
  min-width: 0;
}

.transition-button-modal .transition-status-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 0 1 auto;
  min-width: 0;
}

.transition-button-modal .transition-status::before {
  display: none;
}

.transition-button-modal .transition-arrow {
  color: #6b7280;
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.transition-button-modal .transition-arrow svg {
  width: 16px;
  height: 16px;
}

.transition-button-modal.transition-disabled-checklist {
  border-color: #dc3545 !important;
  background: #fff5f5 !important;
  color: #dc3545 !important;
}

.transition-button-modal.transition-disabled-checklist:hover:not(:disabled) {
  background: #ffe0e0 !important;
  border-color: #dc3545 !important;
}

.status-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Archived task styles */
.archived-badge {
  display: inline-block;
  padding: 6px 12px;
  background-color: #6c757d;
  color: white;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.title-container.archived {
  background-color: #e9ecef !important;
  border: 1px solid #dee2e6;
}

.title-container.archived .task-id.archived,
.title-container.archived .task-title.archived {
  color: #6c757d !important;
}

/* User display styles */
.user-display {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-display-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.user-username {
  font-weight: 500;
  color: #212529;
  font-size: 14px;
}

.user-name {
  font-size: 12px;
  color: #9e9e9e;
  font-weight: 400;
}

.user-fallback {
  font-size: 14px;
  color: #757575;
  font-style: italic;
}

.user-project-role {
  font-size: 12px;
  color: #757575;
  font-style: italic;
}

.participants-row > .participants-block {
  flex: 1 1 100%;
  width: 100%;
}

.participants-block {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.participants-top-row {
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(0, 1.2fr) auto;
  gap: 8px;
}

.participants-line {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 10px;
  border-radius: 8px;
  background: #f9fafb;
}

.participants-line-label {
  margin: 0;
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.3;
}

.participants-line-main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.participants-user-summary {
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
}

.participants-user-summary > span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.participants-user-username {
  font-weight: 500;
  color: #212529;
  font-size: 14px;
}

.participants-user-name {
  font-size: 13px;
  color: #4b5563;
}

.participants-user-fallback {
  font-size: 13px;
  color: #757575;
  font-style: italic;
}

.participants-user-placeholder {
  color: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  padding-top: 1px;
}

.assignee-summary-trigger {
  justify-content: flex-start;
  max-width: 100%;
}

.subscribers-summary-trigger.assignee-summary-unassigned {
  border-color: #2aabee !important;
  background: #2aabee !important;
  color: #fff !important;
  font-weight: 600;
}

.subscribers-summary-trigger.assignee-summary-unassigned:hover,
.subscribers-summary-trigger.assignee-summary-unassigned:focus,
.subscribers-summary-trigger.assignee-summary-unassigned:focus-visible,
.subscribers-summary-trigger.assignee-summary-unassigned:active {
  border-color: #229ed9 !important;
  background: #229ed9 !important;
  color: #fff !important;
}

/* Confidential task message */
.confidential-message {
  text-align: center;
  padding: 48px 24px;
  max-width: 600px;
  margin: 0 auto;
}

.confidential-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.confidential-message h3 {
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  margin: 0 0 12px 0;
}

.confidential-message p {
  font-size: 14px;
  color: #757575;
  line-height: 1.6;
  margin: 0;
}

.subscribers-badge {
  display: inline-block;
  padding: 2px 5px;
  font-size: 10px;
  font-weight: 600;
  background: #2196f3;
  color: white;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
  line-height: 1.2;
}

.participants-line-main-subscribers {
  justify-content: flex-start;
  gap: 8px;
}

.participants-line-subscribers {
  width: fit-content;
  justify-self: start;
}

.subscribers-summary-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 30px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  padding: 4px 10px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.subscribers-summary-trigger:hover {
  background: #fff !important;
  color: #374151 !important;
  border-color: #2aabee;
}

.subscribers-summary-trigger:focus,
.subscribers-summary-trigger:focus-visible,
.subscribers-summary-trigger:active {
  background: #fff !important;
  color: #374151 !important;
  border-color: #2aabee;
}

.participants-summary-text {
  white-space: nowrap;
}

.subscribers-badge-inline {
  position: static;
  margin-left: 2px;
}

.subscribers-toggle-trigger {
  justify-content: center;
  min-width: 34px;
  padding: 4px 8px;
}

.subscribers-toggle-trigger :deep(svg) {
  width: 16px;
  height: 16px;
}

@media (max-width: 768px) {
  .participants-top-row {
    grid-template-columns: 1fr;
  }

  .participants-line {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
  }

  .participants-line-label {
    flex: 0 0 auto;
  }

  .participants-line-main-subscribers {
    justify-content: flex-start;
    gap: 10px;
  }
}

/* Subscribers list */
.subscribers-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
}

.subscriber-item {
  padding: 8px 12px;
  border-radius: 4px;
  background-color: #f8f9fa;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.subscriber-item:hover {
  background-color: #e9ecef;
}

/* Subscribe user form */
.subscribe-user-form {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.subscribe-user-form label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #495057;
}

.subscribe-user-form select {
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

.subscribe-user-controls {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.subscribe-user-select {
  flex: 1;
}

.subscribe-user-select :deep(.user-select-trigger) {
  min-height: 38px;
  height: 38px;
  box-sizing: border-box;
}

.subscribe-user-submit-btn {
  min-height: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex-shrink: 0;
}

.subscribe-user-submit-btn-icon {
  display: none !important;
}

@media (max-width: 768px) {
  .subscribe-user-submit-btn {
    min-width: 38px;
    width: 38px;
    padding: 0;
  }

  .subscribe-user-submit-btn-text {
    display: none !important;
  }

  .subscribe-user-submit-btn-icon {
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
  }
}

/* Time tracking block */
.time-tracking-block {
  background: #f9fafb; /* bg-gray-50 */
  border: 2px solid #e5e7eb; /* Gray for all borders */
  border-left: 2px solid rgba(42, 171, 238, 0.3); /* Telegram blue - semi-transparent left border */
  border-radius: 12px; /* rounded-xl */
  padding: 8px; /* p-6 */
  transition: border-left-color 0.2s;
}

.time-tracking-block:hover,
.time-tracking-block:focus-within {
  border-left-color: #2aabee; /* Fully opaque on hover/focus */
}

/* Hierarchy block */
.hierarchy-block {
  background: #f9fafb; /* bg-gray-50 */
  border: 2px solid #e5e7eb; /* Gray for all borders */
  border-left: 2px solid rgba(99, 102, 241, 0.3); /* Indigo - semi-transparent left border */
  border-radius: 12px; /* rounded-xl */
  padding: 8px; /* p-6 */
  transition: border-left-color 0.2s;
}

.hierarchy-block:hover,
.hierarchy-block:focus-within {
  border-left-color: #6366f1; /* Fully opaque on hover/focus */
}

.attachments-block {
  background: #f9fafb; /* bg-gray-50 */
  border: 2px solid #e5e7eb; /* Gray for all borders */
  border-left: 2px solid rgba(16, 185, 129, 0.3); /* Emerald green - semi-transparent left border */
  border-radius: 12px; /* rounded-xl */
  padding: 8px; /* p-6 */
  transition: border-left-color 0.2s;
}

.attachments-block:hover,
.attachments-block:focus-within {
  border-left-color: #10b981; /* Fully opaque on hover/focus */
}

/* Horizontal relations block */
.horizontal-relations-block {
  background: #f9fafb; /* bg-gray-50 */
  border: 2px solid #e5e7eb; /* Gray for all borders */
  border-left: 2px solid rgba(245, 158, 11, 0.3); /* Amber orange - semi-transparent left border */
  border-radius: 12px; /* rounded-xl */
  padding: 8px; /* p-6 */
  transition: border-left-color 0.2s;
}

.horizontal-relations-block:hover,
.horizontal-relations-block:focus-within {
  border-left-color: #f59e0b; /* Fully opaque on hover/focus */
}

/* Collapsible sections */
.collapsible-header {
  cursor: pointer;
  padding: 4px 0;
  user-select: none;
}

.collapsible-header:hover {
  opacity: 0.8;
}

.collapse-icon {
  font-size: 10px;
  color: #6c757d;
  width: 12px;
  display: inline-block;
}

.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 600;
  background: #10b981;
  color: #fff;
  border-radius: 10px;
  margin-left: 4px;
}

.badge-orange {
  background: #f59e0b;
}

.badge-blue {
  background: #2aabee;
}

.badge-faded {
  opacity: 0.5;
}

.tabs {
  border: none;
}

.tabs .tab.active,
.tabs .tab.active:hover,
.tabs .tab.active:focus,
.tabs .tab.active:active {
  background: #fff !important;
  color: #2aabee !important;
  border-radius: 0%;
  border-bottom: 2px solid #2aabee !important;
}

@media (max-width: 768px) {
  .tabs {
    width: 100%;
  }

  .tabs .tab {
    flex: 1 1 50%;
    min-width: 0;
    justify-content: center;
  }

  .tabs .tab :deep(svg) {
    display: none;
  }
}

/* Comment textarea with paperclip button */
.comment-textarea-wrapper {
  position: relative;
}

.comment-attach-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 4px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition:
    background-color 0.2s,
    color 0.2s;
  z-index: 1;
}

.comment-attach-btn:hover {
  background: #6b7280;
  color: #fff;
}

.comment-attach-btn svg {
  width: 16px;
  height: 16px;
}

.time-summary-header {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: auto;
}

.time-summary-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 500;
  background: #fff;
  color: #2e7d32;
  border-radius: 10px;
  margin-left: 4px;
  border: 1px solid #a5d6a7;
}

.time-badge-label {
  font-size: 11px;
  color: #666;
}

.time-badge-value {
  font-weight: 600;
  color: #2e7d32;
}

.collapsible-content {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #c8e6c9;
}

.loading-state {
  padding: 12px;
  text-align: center;
  color: #888;
  font-size: 13px;
}
</style>
